import React, {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaDraftingCompass } from '@react-icons/all-files/fa/FaDraftingCompass';
import { FaEdit } from '@react-icons/all-files/fa/FaEdit';
import { FaSave } from '@react-icons/all-files/fa/FaSave';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../commons/Breadcrumb';
import { playSound } from '../../shared/audio/audio';
import PageHeader from '../../commons/PageHeader';
import { clearSuperContaContext, setSuperContaContext } from '../../commons/superconta-context';
import { compactWorkflowConfig } from '../../commons/superconta-compact';
import WorkflowConfigActivitiesTable, {
  type WorkflowActiveEmpresaGroupHeader,
} from './workflow-config-activities-table';
import PageAlert, { usePageAlert, type AlertVariant, type ToastOptions } from '../../commons/PageAlert';
import Confirm, { useConfirm } from '../../commons/Confirm';
import UserInfo, { UserAvatar } from '../../commons/UserInfo';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import { FaLock } from '@react-icons/all-files/fa/FaLock';
import { FaEnvelope } from '@react-icons/all-files/fa/FaEnvelope';
import {
  isMailRequeridoParaTerminar,
  mailIconOpacity,
  resolveNodoPoliticaMail,
} from '../../shared/politica-mail';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaCopy } from '@react-icons/all-files/fa/FaCopy';
import { FaInfoCircle } from '@react-icons/all-files/fa/FaInfoCircle';
import { FaExclamationTriangle } from '@react-icons/all-files/fa/FaExclamationTriangle';
import { FaBan } from '@react-icons/all-files/fa/FaBan';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaThumbtack } from '@react-icons/all-files/fa/FaThumbtack';
import WorkflowSearchModal from './workflow-search-modal';
import WorkflowActivitiesVisibilityModal from './workflow-activities-visibility-modal';
import WorkflowEmpresasVisibilityModal from './workflow-empresas-visibility-modal';
import WorkflowCalendarioPeriodoModal from './workflow-calendario-periodo-modal';
import {
  parseConfigPeriodos,
  periodosConfigWarningTitle,
  serializePeriodosForConfigSave,
  type PeriodoActividadWorkflowPeriodo,
} from '../../shared/workflow-periodo';
import {
  isWorkflowConfigStayNavigation,
  resolveHashRouterHref,
  shouldOpenWorkflowPeriodoModal,
  workflowConfigPath,
  workflowConfigPeriodoPath,
  WORKFLOW_CONFIG_LEAVE_MESSAGE,
} from './workflow-app-routes';

type ShowPageToast = (
  variant: AlertVariant,
  message: string,
  durationOrOptions?: number | ToastOptions,
) => void;

const APP_LIST = '/workflow';
const EMPRESA_ACTIVIDADES_EDIT_TOAST =
  'Active el modo Editar para modificar la configuración de actividades de empresa.';
const WORKFLOW_ACTIVIDAD_FILTERS = [
  { label: 'Actividad', name: 'profaseActividad.descripcion' },
];
const USUARIO_DRAG_TYPE = 'application/x-profase-usuario';
const USUARIO_DROP_SOUND = '/assets/audio/drop.mp3';
const USUARIO_APPLY_ALL_SOUND = '/assets/audio/magic.mp3';
const USUARIO_DROP_DENIED_SOUND = '/assets/audio/wrong.mp3';
const LOCK_DOOR_SOUND = '/assets/audio/loock-door.mp3';
const UNLOCK_DOOR_SOUND = '/assets/audio/unlock-door.mp3';
const PLAY_CARD_SOUND = '/assets/audio/play-card.mp3';
const SHUFFLE_CARD_SOUND = '/assets/audio/shuffle-card.mp3';
const SELECT_BUTTON_SOUND = '/assets/audio/select-button.mp3';
const FLASH_SOUND = '/assets/audio/flash.mp3';
const COPY_CUT_SOUND = '/assets/audio/copy_cut.mp3';
const EMPRESA_DRAG_TYPE = 'application/x-profase-empresa';
/** Portapapeles: configuración de actividades por empresa (workflow config). */
const PROFASE_ACTIVIDADES_CLIPBOARD_MIME = 'application/x-profase-app-actividades';
/** Portapapeles: listado de empresas (alias, RFC, razón social). */
const PROFASE_EMPRESAS_CLIPBOARD_MIME = 'application/x-profase-app-empresas';
/** Columnas por empresa (últimas 4): fondo distinto; la primera incluye separador. */

type EmpresaPanelSide = 'available' | 'selected';

function handleEmpresaDragStart(
  e: React.DragEvent,
  side: EmpresaPanelSide,
  empresa: EmpresaItem,
) {
  e.dataTransfer.setData(EMPRESA_DRAG_TYPE, JSON.stringify({ side, empresa }));
  e.dataTransfer.effectAllowed = 'move';
}

function handleUsuarioDragStart(e: React.DragEvent, usuario: unknown) {
  e.dataTransfer.setData(USUARIO_DRAG_TYPE, JSON.stringify(usuario));
  e.dataTransfer.effectAllowed = 'copy';
}

function isUsuarioDragEvent(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer.types).includes(USUARIO_DRAG_TYPE);
}

function playUsuarioDropSound() {
  playSound(USUARIO_DROP_SOUND);
}

/** Audio al pulsar aplicar masivo (actividades / todas las empresas / empresas RFC marcadas). */
function runBulkApplyAction(action: () => void) {
  playUsuarioApplyAllSound();
  action();
}

function playUsuarioDropDeniedSound() {
  playSound(USUARIO_DROP_DENIED_SOUND);
}

function parseUsuarioFromDropEvent(e: React.DragEvent): any | null {
  if (!isUsuarioDragEvent(e)) return null;
  try {
    const raw = e.dataTransfer.getData(USUARIO_DRAG_TYPE);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function playUsuarioApplyAllSound() {
  playSound(USUARIO_APPLY_ALL_SOUND);
}

function playLockDoorSound() {
  playSound(LOCK_DOOR_SOUND);
}

function playUnlockDoorSound() {
  playSound(UNLOCK_DOOR_SOUND);
}

function playSelectEmpresaCardSound() {
  playSound(PLAY_CARD_SOUND);
}

function playShuffleEmpresaCardSound() {
  playSound(SHUFFLE_CARD_SOUND);
}

function playSelectButtonSound() {
  playSound(SELECT_BUTTON_SOUND);
}

function playFlashSound() {
  playSound(FLASH_SOUND);
}

function playCopyCutSound() {
  playSound(COPY_CUT_SOUND);
}

type UsuarioDropDragState = 'idle' | 'allowed' | 'denied';

type UsuarioAssignAlsoScope = 'checkedEmpresas' | 'selectedActivities';

interface PendingUsuarioAssign {
  user: any;
  field: 'usuario' | 'autoriza';
  targetRow: any;
  /** Drop en cabecera de columna: ya se aplicó a todas las actividades de la empresa activa. */
  columnDrop?: boolean;
  /** Tras aplicar a actividades seleccionadas: confirmar copia a otras empresas. */
  afterActivitiesStep?: boolean;
  /** Asignaciones por actividad (usuario/autoriza) a replicar en otras empresas. */
  activityConfigsToCopy?: Record<string, EmpresaActividadConfig>;
  /** Cantidad de actividades aplicadas en el paso anterior. */
  appliedActivitiesCount?: number;
}

type EmpresaFieldToggleScope = 'allEmpresas' | 'checkedEmpresas';

type EmpresaCheckboxField =
  | 'habilitadoEmpresa'
  | 'asignarAContadorEmpresa'
  | 'requiereAutorizacionEmpresa'
  | 'requiereArchivoEmpresa';

type EmpresaActividadColumnKey =
  | EmpresaCheckboxField
  | 'usuario'
  | 'autoriza';

const EMPRESA_ACTIVIDAD_COLUMN_LABELS: Record<EmpresaActividadColumnKey, string> = {
  habilitadoEmpresa: 'Habilitada',
  asignarAContadorEmpresa: 'Asig. contador',
  usuario: 'Usuario',
  requiereAutorizacionEmpresa: 'Req. Autoriz.',
  autoriza: 'Autoriza (usuario)',
  requiereArchivoEmpresa: 'Req. Archivo',
};

type HabilitadoEmpresaSelectValue = 'HABILITADA' | 'NO_HABILITADA' | 'NO_MODIFICAR';

type EmpresaFieldToggleValue = boolean | HabilitadoEmpresaSelectValue;

const HABILITADO_EMPRESA_OPTIONS: { value: HabilitadoEmpresaSelectValue; label: string }[] = [
  { value: 'HABILITADA', label: 'Habilitada' },
  { value: 'NO_HABILITADA', label: 'No Habilitada' },
  { value: 'NO_MODIFICAR', label: 'No Modificar' },
];

function isHabilitadoEmpresaSelectValue(value: unknown): value is HabilitadoEmpresaSelectValue {
  return value === 'HABILITADA' || value === 'NO_HABILITADA' || value === 'NO_MODIFICAR';
}

/** Normaliza `habilitado` de actividad-empresa (API: enum string; legacy: boolean). */
function normalizeHabilitadoEmpresaStored(
  value: unknown,
): HabilitadoEmpresaSelectValue | null {
  if (isHabilitadoEmpresaSelectValue(value)) return value;
  if (value === true) return 'HABILITADA';
  if (value === false) return 'NO_HABILITADA';
  return null;
}

function habilitadoEmpresaToSelectValue(
  value: HabilitadoEmpresaSelectValue | boolean | null | undefined,
): HabilitadoEmpresaSelectValue {
  return normalizeHabilitadoEmpresaStored(value) ?? 'NO_MODIFICAR';
}

function habilitadoEmpresaRowsEqual(
  a: HabilitadoEmpresaSelectValue | boolean | null | undefined,
  b: HabilitadoEmpresaSelectValue | null,
): boolean {
  return normalizeHabilitadoEmpresaStored(a) === normalizeHabilitadoEmpresaStored(b);
}

function applyRowHabilitadoEmpresaUpdate(
  row: any,
  value: HabilitadoEmpresaSelectValue,
): any {
  return reconcileRowEmpresaActividadFields({ ...row, habilitadoEmpresa: value });
}

interface PendingEmpresaFieldToggle {
  targetRow?: any;
  field: EmpresaCheckboxField;
  value: EmpresaFieldToggleValue;
  bulkActivityUpdates?: Array<{ actividadKey: string; nodoId: number }>;
}

function empresaFieldToggleActionLabel(
  field: EmpresaCheckboxField,
  value: EmpresaFieldToggleValue,
): string {
  if (field === 'requiereAutorizacionEmpresa') {
    return value
      ? 'marcar requiere autorización en'
      : 'desmarcar requiere autorización en';
  }
  if (field === 'requiereArchivoEmpresa') {
    return value
      ? 'marcar requiere archivo en'
      : 'desmarcar requiere archivo en';
  }
  if (field === 'asignarAContadorEmpresa') {
    return value
      ? 'activar asignar a contador en'
      : 'desactivar asignar a contador en';
  }
  if (value === 'HABILITADA') return 'habilitar';
  if (value === 'NO_HABILITADA') return 'deshabilitar';
  return 'dejar sin modificar';
}

function empresaFieldToggleSuccessLabel(
  field: EmpresaCheckboxField,
  value: EmpresaFieldToggleValue,
): string {
  if (field === 'requiereAutorizacionEmpresa') {
    return value ? 'Requiere autorización activado' : 'Requiere autorización desactivado';
  }
  if (field === 'requiereArchivoEmpresa') {
    return value ? 'Requiere archivo activado' : 'Requiere archivo desactivado';
  }
  if (field === 'asignarAContadorEmpresa') {
    return value ? 'Asignar a contador activado' : 'Asignar a contador desactivado';
  }
  if (value === 'HABILITADA') return 'Habilitada';
  if (value === 'NO_HABILITADA') return 'No habilitada';
  return 'Sin modificar';
}

function applyEmpresaActividadCfgField(
  cfg: EmpresaActividadConfig,
  field: EmpresaCheckboxField,
  value: EmpresaFieldToggleValue,
  nodoId: number,
): EmpresaActividadConfig {
  let next: EmpresaActividadConfig;
  if (field === 'habilitadoEmpresa') {
    next = { ...cfg, nodoId, habilitado: value as HabilitadoEmpresaSelectValue };
  } else if (field === 'asignarAContadorEmpresa') {
    next = value
      ? { ...cfg, nodoId, asignarAContador: true, usuario: null }
      : { ...cfg, nodoId, asignarAContador: false };
  } else if (field === 'requiereAutorizacionEmpresa') {
    next = value
      ? { ...cfg, nodoId, requiereAutorizacion: true }
      : { ...cfg, nodoId, requiereAutorizacion: false, autoriza: null };
  } else {
    next = value
      ? { ...cfg, nodoId, requiereArchivo: true }
      : { ...cfg, nodoId, requiereArchivo: false };
  }
  return markEmpresaActividadModificado(
    reconcileEmpresaActividadRequiereAutorizAutoriza(
      reconcileEmpresaActividadUsuarioAsignar(next),
    ),
  );
}

function empresaActividadCfgFieldMatches(
  cfg: EmpresaActividadConfig,
  field: EmpresaCheckboxField,
  value: EmpresaFieldToggleValue,
): boolean {
  if (field === 'habilitadoEmpresa') {
    return habilitadoEmpresaRowsEqual(
      cfg.habilitado,
      value as HabilitadoEmpresaSelectValue,
    );
  }
  if (field === 'asignarAContadorEmpresa') return cfg.asignarAContador === value;
  if (field === 'requiereAutorizacionEmpresa') return cfg.requiereAutorizacion === value;
  return cfg.requiereArchivo === value;
}

function markEmpresaActividadModificado(cfg: EmpresaActividadConfig): EmpresaActividadConfig {
  return cfg.modificado ? cfg : { ...cfg, modificado: true };
}

function empresaActividadConfigValuesEqual(
  a: EmpresaActividadConfig | undefined,
  b: EmpresaActividadConfig,
): boolean {
  if (!a) return false;
  return (
    a.usuario?.id === b.usuario?.id
    && a.autoriza?.id === b.autoriza?.id
    && a.habilitado === b.habilitado
    && a.requiereAutorizacion === b.requiereAutorizacion
    && a.asignarAContador === b.asignarAContador
    && a.requiereArchivo === b.requiereArchivo
  );
}

/** Si hay usuario asignado, `asignarAContador` debe ser false (al marcar la casilla se limpia el usuario). */
function reconcileEmpresaActividadUsuarioAsignar(
  cfg: EmpresaActividadConfig,
): EmpresaActividadConfig {
  if (cfg.usuario?.id != null && cfg.asignarAContador === true) {
    return { ...cfg, asignarAContador: false };
  }
  return cfg;
}

function reconcileRowUsuarioAsignarAContador(row: any): any {
  if (row?.usuario?.id != null && row?.asignarAContadorEmpresa === true) {
    return { ...row, asignarAContadorEmpresa: false };
  }
  return row;
}

/** Si hay autoriza asignado, `requiereAutorizacion` debe ser true (al desmarcar se limpia autoriza). */
function reconcileEmpresaActividadRequiereAutorizAutoriza(
  cfg: EmpresaActividadConfig,
): EmpresaActividadConfig {
  if (cfg.autoriza?.id != null && cfg.requiereAutorizacion !== true) {
    return { ...cfg, requiereAutorizacion: true };
  }
  if (cfg.requiereAutorizacion !== true && cfg.autoriza?.id != null) {
    return { ...cfg, autoriza: null };
  }
  return cfg;
}

function reconcileRowRequiereAutorizAutoriza(row: any): any {
  if (row?.autoriza?.id != null && row?.requiereAutorizacionEmpresa !== true) {
    return { ...row, requiereAutorizacionEmpresa: true };
  }
  if (row?.requiereAutorizacionEmpresa !== true && row?.autoriza?.id != null) {
    return { ...row, autoriza: null };
  }
  return row;
}

function reconcileRowEmpresaActividadFields(row: any): any {
  return reconcileRowRequiereAutorizAutoriza(
    reconcileRowUsuarioAsignarAContador(row),
  );
}

function applyRowEmpresaCheckboxUpdate(
  row: any,
  field: EmpresaCheckboxField,
  value: boolean,
): any {
  if (field === 'asignarAContadorEmpresa') {
    return value
      ? { ...row, asignarAContadorEmpresa: true, usuario: null }
      : { ...row, asignarAContadorEmpresa: false };
  }
  if (field === 'requiereAutorizacionEmpresa') {
    return value
      ? { ...row, requiereAutorizacionEmpresa: true }
      : { ...row, requiereAutorizacionEmpresa: false, autoriza: null };
  }
  if (field === 'requiereArchivoEmpresa') {
    return { ...row, requiereArchivoEmpresa: value };
  }
  return reconcileRowEmpresaActividadFields({ ...row, [field]: value });
}

function applyRowEmpresaUsuarioFieldUpdate(
  row: any,
  field: 'usuario' | 'autoriza',
  value: { id?: number; nombreCompleto?: string; nombre?: string; apellido?: string } | null,
): any {
  if (field === 'usuario') {
    return reconcileRowUsuarioAsignarAContador({ ...row, usuario: value });
  }
  if (value?.id != null) {
    return { ...row, autoriza: value, requiereAutorizacionEmpresa: true };
  }
  return reconcileRowRequiereAutorizAutoriza({ ...row, autoriza: value });
}

interface PendingEmpresaPasteAlso {
  pasted: Record<string, EmpresaActividadConfig>;
  appliedActivitiesCount: number;
}

interface PendingUsuarioClear {
  field: 'usuario' | 'autoriza';
  targetRow: any;
}

function UsuarioDropCell({
  usuario,
  row,
  field,
  onDropRequest,
  onClear,
  clearDisabled,
  dropDisabled = false,
  dropDisabledTitle,
  size = 22,
}: {
  usuario?: any;
  row: any;
  field: 'usuario' | 'autoriza';
  onDropRequest: (row: any, field: 'usuario' | 'autoriza', user: any) => void;
  onClear?: (row: any, field: 'usuario' | 'autoriza') => void;
  clearDisabled?: boolean;
  dropDisabled?: boolean;
  dropDisabledTitle?: string;
  size?: number;
}) {
  const [dragState, setDragState] = useState<UsuarioDropDragState>('idle');

  const onDragOver = (e: React.DragEvent) => {
    if (!isUsuarioDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (dropDisabled) {
      e.dataTransfer.dropEffect = 'none';
      setDragState('denied');
      return;
    }
    e.dataTransfer.dropEffect = 'copy';
    setDragState('allowed');
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragState('idle');
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragState('idle');
    const user = parseUsuarioFromDropEvent(e);
    if (!user) return;
    if (dropDisabled) {
      playUsuarioDropDeniedSound();
      return;
    }
    onDropRequest(row, field, user);
  };

  return (
    <div
      title={
        dropDisabled
          ? (dropDisabledTitle ?? 'Actividad bloqueada: no se puede asignar usuario')
          : 'Soltar usuario aquí'
      }
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        minHeight: size + 8,
        width: '100%',
        padding: '2px 4px',
        borderRadius: 6,
        boxSizing: 'border-box',
        transition: 'outline 0.12s, background 0.12s',
        ...(dragState === 'allowed' ? usuarioDropS.dropAllowed : {}),
        ...(dragState === 'denied' ? usuarioDropS.dropDenied : {}),
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <UserInfo usuario={usuario} size={size} />
      </span>
      {dragState === 'denied' ? (
        <FaBan size={14} style={usuarioDropS.dropDeniedIcon} aria-hidden />
      ) : null}
      {usuario?.id && onClear ? (
        <button
          type="button"
          style={{
            ...usuarioDropS.clearBtn,
            ...(clearDisabled ? usuarioDropS.clearBtnDisabled : {}),
          }}
          disabled={clearDisabled}
          title={clearDisabled ? 'Empresa bloqueada o no seleccionada' : 'Quitar usuario'}
          aria-label={clearDisabled ? 'Quitar usuario deshabilitado' : 'Quitar usuario'}
          onClick={e => {
            e.stopPropagation();
            onClear(row, field);
          }}
        >
          <FaTimes size={12} />
        </button>
      ) : null}
    </div>
  );
}

const usuarioDropS: Record<string, React.CSSProperties> = {
  dropAllowed: {
    outline: '2px solid #22c55e',
    outlineOffset: 1,
    background: 'var(--crm-surface-hover)',
  },
  dropDenied: {
    outline: '2px solid #ef4444',
    outlineOffset: 1,
    background: 'rgba(239, 68, 68, 0.1)',
    cursor: 'not-allowed',
  },
  dropDeniedIcon: {
    color: '#ef4444',
    flexShrink: 0,
  },
  clearBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 22,
    height: 22,
    padding: 0,
    border: 'none',
    borderRadius: 4,
    background: 'transparent',
    color: 'var(--crm-text-secondary)',
    cursor: 'pointer',
  },
  clearBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
};

function ActividadNameDropZone({
  label,
  row,
  actividadBloqueada,
  dropDisabled = false,
  dropDisabledTitle,
  modificado,
  onDropOnActividad,
}: {
  label: string;
  row: any;
  actividadBloqueada: boolean;
  dropDisabled?: boolean;
  dropDisabledTitle?: string;
  modificado?: boolean;
  onDropOnActividad: (row: any, user: any) => void;
}) {
  const [dragState, setDragState] = useState<UsuarioDropDragState>('idle');
  const dropBlocked = actividadBloqueada || dropDisabled;

  const onDragOver = (e: React.DragEvent) => {
    if (!isUsuarioDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (dropBlocked) {
      e.dataTransfer.dropEffect = 'none';
      setDragState('denied');
      return;
    }
    e.dataTransfer.dropEffect = 'copy';
    setDragState('allowed');
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragState('idle');
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragState('idle');
    const user = parseUsuarioFromDropEvent(e);
    if (!user) return;
    if (dropBlocked) {
      playUsuarioDropDeniedSound();
      return;
    }
    onDropOnActividad(row, user);
  };

  return (
    <span
      title={
        dropBlocked
          ? (dropDisabledTitle
            ?? (actividadBloqueada
              ? 'Actividad bloqueada: no se puede asignar usuario'
              : EMPRESA_ACTIVIDADES_EDIT_TOAST))
          : 'Soltar usuario: aplicar en esta actividad (empresa seleccionada; opcionalmente otras empresas)'
      }
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flex: 1,
        minWidth: 0,
        padding: '2px 4px',
        borderRadius: 6,
        boxSizing: 'border-box',
        transition: 'outline 0.12s, background 0.12s',
        ...(dragState === 'allowed' ? usuarioDropS.dropAllowed : {}),
        ...(dragState === 'denied' ? usuarioDropS.dropDenied : {}),
      }}
    >
      <span
        className="profase-datagrid-cell-wrap"
        style={{
          color: 'var(--crm-grid-cell-text)',
          flex: 1,
          minWidth: 0,
          ...(modificado ? { fontStyle: 'italic' } : {}),
        }}
      >
        {label}
      </span>
      {dragState === 'denied' ? (
        <FaBan size={13} style={usuarioDropS.dropDeniedIcon} aria-hidden />
      ) : null}
    </span>
  );
}

function UsuarioDropHeader({
  label,
  field,
  onColumnDrop,
  disabled,
}: {
  label: string;
  field: 'usuario' | 'autoriza';
  onColumnDrop: (field: 'usuario' | 'autoriza', user: any) => void;
  disabled?: boolean;
}) {
  const [isDragOver, setIsDragOver] = useState(false);

  const onDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const user = parseUsuarioFromDropEvent(e);
    if (!user) return;
    if (disabled) {
      playUsuarioDropDeniedSound();
      return;
    }
    onColumnDrop(field, user);
  };

  return (
    <div
      title={disabled ? 'Seleccione una empresa en la lista de empresas seleccionadas' : 'Soltar usuario para aplicar en toda la columna'}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={e => e.stopPropagation()}
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: '4px 0',
        borderRadius: 6,
        boxSizing: 'border-box',
        transition: 'outline 0.12s, background 0.12s',
        opacity: disabled ? 0.55 : 1,
        ...(isDragOver
          ? {
            outline: '2px solid #22c55e',
            outlineOffset: 1,
            background: 'var(--crm-surface-hover)',
          }
          : {}),
      }}
    >
      <span className="profase-datagrid-header-label">{label}</span>
    </div>
  );
}

function UsuarioAssignAlsoScopeConfirm({
  pending,
  selectedActivitiesUnlockedCount,
  checkedRfcCount,
  checkedRfcAlsoApplicableCount,
  columnDrop,
  onApply,
  onDismiss,
}: {
  pending: PendingUsuarioAssign;
  selectedActivitiesUnlockedCount: number;
  checkedRfcCount: number;
  checkedRfcAlsoApplicableCount: number;
  columnDrop?: boolean;
  onApply: (scope: UsuarioAssignAlsoScope) => void;
  onDismiss: () => void;
}) {
  const fieldLabel = pending.field === 'usuario' ? 'Usuario' : 'Autoriza (usuario)';
  const userName = usuarioDisplayName(pending.user as UsuarioSearchItem);
  const activitiesDisabled = selectedActivitiesUnlockedCount === 0;
  const empresasDisabled = checkedRfcAlsoApplicableCount === 0;

  return (
    <div style={assignConfirmS.root} role="alertdialog" aria-live="polite">
      <div style={assignConfirmS.icon}>ℹ</div>
      <div style={assignConfirmS.body}>
        <p style={assignConfirmS.title}>¿Aplicar también?</p>
        <p style={assignConfirmS.message}>
          {columnDrop ? (
            <>
              <strong>{userName}</strong> ya se asignó en <strong>{fieldLabel}</strong> para todas
              las actividades de la empresa activa. Puede marcar otras empresas (RFC) o actividades
              en la grilla y elegir dónde aplicarlo también.
            </>
          ) : (
            <>
              <strong>{userName}</strong> ya se asignó en <strong>{fieldLabel}</strong> para la
              empresa y actividad del drop. Puede marcar otras empresas (RFC) o actividades en la
              grilla y elegir dónde aplicarlo también.
            </>
          )}
        </p>
        <div style={assignConfirmS.actions}>
          <button
            type="button"
            style={{
              ...assignConfirmS.btnScope,
              ...(activitiesDisabled ? assignConfirmS.btnDisabled : {}),
            }}
            onClick={() => runBulkApplyAction(() => onApply('selectedActivities'))}
            disabled={activitiesDisabled}
          >
            Aplicar a todas las actividades seleccionadas ({selectedActivitiesUnlockedCount}, no bloqueadas)
          </button>
          <button
            type="button"
            style={{
              ...assignConfirmS.btnScope,
              ...(empresasDisabled ? assignConfirmS.btnDisabled : {}),
            }}
            onClick={() => runBulkApplyAction(() => onApply('checkedEmpresas'))}
            disabled={empresasDisabled}
          >
            Aplicar a todas las Empresas Seleccionadas ({checkedRfcCount})
          </button>
          <button type="button" style={assignConfirmS.btnCancel} onClick={onDismiss}>
            {columnDrop ? 'No, solo esta empresa' : 'No, solo esta actividad'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UsuarioAssignAfterActivitiesEmpresasConfirm({
  pending,
  checkedRfcCount,
  checkedRfcAlsoApplicableCount,
  onApply,
  onDismiss,
}: {
  pending: PendingUsuarioAssign;
  checkedRfcCount: number;
  checkedRfcAlsoApplicableCount: number;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const empresasDisabled = checkedRfcAlsoApplicableCount === 0;
  const activitiesCount = pending.appliedActivitiesCount ?? 0;

  return (
    <div style={assignConfirmS.root} role="alertdialog" aria-live="polite">
      <div style={assignConfirmS.icon}>ℹ</div>
      <div style={assignConfirmS.body}>
        <p style={assignConfirmS.title}>¿Aplicar también a empresas?</p>
        <p style={assignConfirmS.message}>
          Las asignaciones de usuario se aplicaron en{' '}
          <strong>{activitiesCount} actividad{activitiesCount === 1 ? '' : 'es'}</strong> de la
          empresa activa. ¿Desea aplicar a todas las empresas seleccionadas? Se copiará la
          selección de usuarios (Usuario y Autoriza) de esas actividades. Puede marcar empresas
          (RFC) antes de confirmar.
        </p>
        <div style={assignConfirmS.actions}>
          <button
            type="button"
            style={{
              ...assignConfirmS.btnScope,
              ...(empresasDisabled ? assignConfirmS.btnDisabled : {}),
            }}
            onClick={() => runBulkApplyAction(onApply)}
            disabled={empresasDisabled}
          >
            Aplicar a todas las Empresas Seleccionadas ({checkedRfcCount})
          </button>
          <button type="button" style={assignConfirmS.btnCancel} onClick={onDismiss}>
            No, solo en esta empresa
          </button>
        </div>
      </div>
    </div>
  );
}

function UsuarioClearAlsoConfirm({
  pending,
  checkedRfcCount,
  clearAlsoEmpresasApplicableCount,
  onApply,
  onDismiss,
}: {
  pending: PendingUsuarioClear;
  checkedRfcCount: number;
  clearAlsoEmpresasApplicableCount: number;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const empresasDisabled = clearAlsoEmpresasApplicableCount === 0;
  const fieldLabel = pending.field === 'usuario' ? 'Usuario' : 'Autoriza (usuario)';
  const actividadLabel =
    pending.targetRow?.profaseActividad?.descripcion
    ?? pending.targetRow?.data?.label
    ?? 'esta actividad';

  return (
    <div style={assignConfirmS.root} role="alertdialog" aria-live="polite">
      <div style={assignConfirmS.icon}>ℹ</div>
      <div style={assignConfirmS.body}>
        <p style={assignConfirmS.title}>¿Aplicar también a empresas?</p>
        <p style={assignConfirmS.message}>
          Se quitó <strong>{fieldLabel}</strong> en <strong>{actividadLabel}</strong> de la empresa
          seleccionada. ¿Desea quitar el mismo usuario en otras empresas? Puede marcar empresas (RFC)
          antes de confirmar.
        </p>
        <div style={assignConfirmS.actions}>
          <button
            type="button"
            style={{
              ...assignConfirmS.btnScope,
              ...(empresasDisabled ? assignConfirmS.btnDisabled : {}),
            }}
            onClick={() => runBulkApplyAction(onApply)}
            disabled={empresasDisabled}
          >
            Aplicar a todas las Empresas Seleccionadas ({checkedRfcCount})
          </button>
          <button type="button" style={assignConfirmS.btnCancel} onClick={onDismiss}>
            No, solo en esta empresa
          </button>
        </div>
      </div>
    </div>
  );
}

function EmpresaPasteAlsoConfirm({
  pending,
  checkedRfcCount,
  pasteAlsoEmpresasApplicableCount,
  onApply,
  onDismiss,
}: {
  pending: PendingEmpresaPasteAlso;
  checkedRfcCount: number;
  pasteAlsoEmpresasApplicableCount: number;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const empresasDisabled = pasteAlsoEmpresasApplicableCount === 0;
  const activitiesCount = pending.appliedActivitiesCount;

  return (
    <div style={assignConfirmS.root} role="alertdialog" aria-live="polite">
      <div style={assignConfirmS.icon}>ℹ</div>
      <div style={assignConfirmS.body}>
        <p style={assignConfirmS.title}>¿Aplicar también a empresas?</p>
        <p style={assignConfirmS.message}>
          Las actividades se pegaron en la empresa activa (
          <strong>{activitiesCount} actividad{activitiesCount === 1 ? '' : 'es'}</strong>
          ). ¿Desea aplicar a todas las empresas seleccionadas? Puede marcar empresas (RFC) antes
          de confirmar.
        </p>
        <div style={assignConfirmS.actions}>
          <button
            type="button"
            style={{
              ...assignConfirmS.btnScope,
              ...(empresasDisabled ? assignConfirmS.btnDisabled : {}),
            }}
            onClick={() => runBulkApplyAction(onApply)}
            disabled={empresasDisabled}
          >
            Aplicar a todas las Empresas Seleccionadas ({checkedRfcCount})
          </button>
          <button type="button" style={assignConfirmS.btnCancel} onClick={onDismiss}>
            No, solo en esta empresa
          </button>
        </div>
      </div>
    </div>
  );
}

function EmpresaFieldToggleScopeConfirm({
  pending,
  rfcEmpresaCount,
  rfcEmpresaAlsoApplicableCount,
  checkedRfcCount,
  checkedRfcAlsoApplicableCount,
  onApply,
  onDismiss,
}: {
  pending: PendingEmpresaFieldToggle;
  rfcEmpresaCount: number;
  rfcEmpresaAlsoApplicableCount: number;
  checkedRfcCount: number;
  checkedRfcAlsoApplicableCount: number;
  onApply: (scope: EmpresaFieldToggleScope) => void;
  onDismiss: () => void;
}) {
  const bulkCount = pending.bulkActivityUpdates?.length ?? 0;
  const isBulk = bulkCount > 0;
  const actividadLabel = isBulk
    ? `${bulkCount} actividad${bulkCount === 1 ? '' : 'es'} visible${bulkCount === 1 ? '' : 's'}`
    : (
      pending.targetRow?.profaseActividad?.descripcion
      ?? pending.targetRow?.data?.label
      ?? 'esta actividad'
    );
  const actionLabel = empresaFieldToggleActionLabel(pending.field, pending.value);

  return (
    <div style={assignConfirmS.root} role="alertdialog" aria-live="assertive">
      <div style={assignConfirmS.icon}>ℹ</div>
      <div style={assignConfirmS.body}>
        <p style={assignConfirmS.title}>¿Aplicar también?</p>
        <p style={assignConfirmS.message}>
          El cambio ya se aplicó a la empresa seleccionada. ¿Desea también {actionLabel}{' '}
          <strong>{actividadLabel}</strong> en otras empresas?
        </p>
        <div style={assignConfirmS.actions}>
          <button
            type="button"
            style={{
              ...assignConfirmS.btnScope,
              ...(rfcEmpresaAlsoApplicableCount === 0 ? assignConfirmS.btnDisabled : {}),
            }}
            onClick={() => runBulkApplyAction(() => onApply('allEmpresas'))}
            disabled={rfcEmpresaAlsoApplicableCount === 0}
          >
            Todas las empresas ({rfcEmpresaCount})
          </button>
          <button
            type="button"
            style={{
              ...assignConfirmS.btnScope,
              ...(checkedRfcAlsoApplicableCount === 0 ? assignConfirmS.btnDisabled : {}),
            }}
            onClick={() => runBulkApplyAction(() => onApply('checkedEmpresas'))}
            disabled={checkedRfcAlsoApplicableCount === 0}
          >
            Solo las empresas seleccionadas ({checkedRfcCount})
          </button>
          <button type="button" style={assignConfirmS.btnCancel} onClick={onDismiss}>
            No, solo esta empresa
          </button>
        </div>
      </div>
    </div>
  );
}

function HabilitadoEmpresaSelect({
  value,
  disabled,
  onChange,
  bulkPlaceholder,
}: {
  value: HabilitadoEmpresaSelectValue | '';
  disabled?: boolean;
  onChange: (value: HabilitadoEmpresaSelectValue) => void;
  bulkPlaceholder?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      className="profase-datagrid-filter-control"
      style={{
        width: '100%',
        minWidth: 0,
        fontSize: 11,
        padding: '4px 6px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={e => e.stopPropagation()}
      onChange={e => {
        const next = e.target.value as HabilitadoEmpresaSelectValue;
        if (!next) return;
        onChange(next);
      }}
    >
      {bulkPlaceholder ? <option value="">—</option> : null}
      {HABILITADO_EMPRESA_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
  ariaLabel,
  style,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate, checked]);

  return (
    <span
      role="presentation"
      onClick={e => {
        e.stopPropagation();
        if (disabled) playUsuarioDropDeniedSound();
      }}
      style={{
        display: 'inline-flex',
        verticalAlign: 'middle',
        cursor: disabled ? 'not-allowed' : undefined,
      }}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        style={{
          ...style,
          ...(disabled ? { pointerEvents: 'none' } : undefined),
        }}
        onClick={e => e.stopPropagation()}
        onChange={e => {
          if (disabled) return;
          onChange(e.target.checked);
        }}
        aria-label={ariaLabel}
      />
    </span>
  );
}

interface WorkflowConfigNodoItem {
  nodo?: {
    id?: number;
    tipoNodo?: string;
    habilitado?: boolean;
    requiereAutorizacion?: boolean;
    profaseActividad?: { descripcion?: string };
    actividad?: {
      descripcion?: string;
      politicaMail?: { requeridoParaTerminar?: boolean } | null;
    };
    profaseWorkflow?: { nombre?: string };
    data?: { label?: string };
    [key: string]: unknown;
  };
  habilitado?: boolean | null;
  requiereAutorizacion?: boolean | null;
  usuario?: unknown;
  autoriza?: unknown;
  bloqueado?: boolean;
  modificado?: boolean;
  tipoNodo?: string;
  id?: number;
  [key: string]: unknown;
}

function markNodoModificado<T extends { modificado?: boolean }>(nodo: T): T {
  return nodo.modificado ? nodo : { ...nodo, modificado: true };
}

interface EmpresaActividadConfig {
  id?: number;
  nodoId: number;
  habilitado?: HabilitadoEmpresaSelectValue | null;
  requiereAutorizacion?: boolean | null;
  asignarAContador?: boolean | null;
  requiereArchivo?: boolean | null;
  modificado?: boolean;
  usuario?: { id?: number; nombreCompleto?: string; nombre?: string; apellido?: string } | null;
  autoriza?: { id?: number; nombreCompleto?: string; nombre?: string; apellido?: string } | null;
}

interface EmpresaSeleccionadaItem {
  id: number;
  bloqueado?: boolean;
  warning?: boolean;
  modificado?: boolean;
  workflowEmpresaId?: number | null;
  disenoPersonalizado?: boolean;
  empresaDisenoId?: number | null;
  instanciaId?: number | null;
  instanciaEstado?: string | null;
  actividades?: Record<string, EmpresaActividadConfig>;
}

type EmpresaInstanciaIcon = 'firefighter';

function resolveEmpresaInstanciaIcon(
  instanciaId: number | null | undefined,
  instanciaEstado: string | null | undefined,
): EmpresaInstanciaIcon | null {
  if (instanciaId == null) return null;
  const estado = String(instanciaEstado ?? '').toUpperCase();
  if (estado === 'PENDIENTE_DE_CORREGIR') return 'firefighter';
  return null;
}

/** Resuelve `?workflowEmpresaId=` al `id` de empresa en empresas seleccionadas. */
function resolveEmpresaIdFromWorkflowEmpresaQuery(
  empresas: EmpresaSeleccionadaItem[],
  queryValue: string | null,
): number | null {
  if (!queryValue?.trim()) return null;
  const paramId = Number(queryValue.trim());
  if (Number.isNaN(paramId)) return null;

  const byWorkflowEmpresaId = empresas.find(e => Number(e.workflowEmpresaId) === paramId);
  if (byWorkflowEmpresaId) return byWorkflowEmpresaId.id;

  const byEmpresaId = empresas.find(e => e.id === paramId);
  if (byEmpresaId) return byEmpresaId.id;

  return null;
}

function markEmpresaModificado(empresa: EmpresaSeleccionadaItem): EmpresaSeleccionadaItem {
  return empresa.modificado ? empresa : { ...empresa, modificado: true };
}

function isEmpresaDisenoDirty(
  disenoPersonalizado: boolean,
  empresaDisenoId: number | null,
  initial: EmpresaSeleccionadaItem | undefined,
): boolean {
  if (!initial) return false;
  return disenoPersonalizado !== !!initial.disenoPersonalizado
    || (empresaDisenoId ?? null) !== (initial.empresaDisenoId ?? null);
}

interface ConfigUsuarioRef {
  id: number;
  nombreCompleto?: string;
}

interface WorkflowConfigResponse {
  nodos?: WorkflowConfigNodoItem[];
  workflow?: { id?: number; nombre?: string; periodicidad?: string | null };
  empresasSeleccionadas?: Array<number | EmpresaSeleccionadaItem>;
  usuarios?: ConfigUsuarioRef[];
  periodos?: PeriodoActividadWorkflowPeriodo[];
}

function parseEmpresaActividades(raw: unknown): Record<string, EmpresaActividadConfig> {
  if (!raw || typeof raw !== 'object') return {};

  const result: Record<string, EmpresaActividadConfig> = {};
  for (const [key, val] of Object.entries(raw)) {
    if (!val || typeof val !== 'object') continue;
    const entry = val as EmpresaActividadConfig;
    const nodoId = Number(entry.nodoId ?? key);
    if (Number.isNaN(nodoId)) continue;
    result[String(nodoId)] = reconcileEmpresaActividadRequiereAutorizAutoriza(
      reconcileEmpresaActividadUsuarioAsignar({
        id: entry.id,
        nodoId,
        habilitado: normalizeHabilitadoEmpresaStored(entry.habilitado),
        requiereAutorizacion: entry.requiereAutorizacion ?? null,
        asignarAContador: entry.asignarAContador ?? null,
        requiereArchivo: entry.requiereArchivo ?? null,
        modificado: !!entry.modificado,
        usuario: entry.usuario ?? null,
        autoriza: entry.autoriza ?? null,
      }),
    );
  }
  return result;
}

/** Soporta `[163]` o `[{ id, bloqueado, actividades }]`. */
function parseEmpresasSeleccionadas(raw: unknown): EmpresaSeleccionadaItem[] {
  if (!Array.isArray(raw)) return [];

  const result: EmpresaSeleccionadaItem[] = [];

  for (const item of raw) {
    if (item != null && typeof item === 'object' && 'id' in item) {
      const src = item as EmpresaSeleccionadaItem & { actividades?: unknown };
      const id = Number(src.id);
      if (!Number.isNaN(id)) {
        result.push({
          id,
          bloqueado: !!src.bloqueado,
          warning: !!src.warning,
          modificado: !!src.modificado,
          workflowEmpresaId: src.workflowEmpresaId != null ? Number(src.workflowEmpresaId) : null,
          disenoPersonalizado: !!src.disenoPersonalizado,
          empresaDisenoId: src.empresaDisenoId != null ? Number(src.empresaDisenoId) : null,
          instanciaId: src.instanciaId ?? null,
          instanciaEstado: src.instanciaEstado ?? null,
          actividades: parseEmpresaActividades(src.actividades),
        });
      }
      continue;
    }

    const id = Number(item);
    if (!Number.isNaN(id)) {
      result.push({
        id,
        bloqueado: false,
        warning: false,
        modificado: false,
        workflowEmpresaId: null,
        disenoPersonalizado: false,
        empresaDisenoId: null,
        instanciaId: null,
        instanciaEstado: null,
        actividades: {},
      });
    }
  }

  return result;
}

/** Regla Usuario: incumple si no hay usuario; no aplica si asignar a contador está activo. */
function empresaActividadUsuarioRuleViolated(cfg: EmpresaActividadConfig | undefined): boolean {
  if (cfg?.asignarAContador === true) return false;
  return cfg?.usuario?.id == null;
}

/** Regla Autoriza: aplica solo con req. autoriz.; incumple si no hay autoriza (usuario). */
function empresaActividadAutorizaRuleViolated(cfg: EmpresaActividadConfig | undefined): boolean {
  if (cfg?.requiereAutorizacion !== true) return false;
  return cfg?.autoriza?.id == null;
}

/** Basta con que una regla aplicable no cumpla para warning en la actividad. */
function empresaActividadConfigHasWarning(cfg: EmpresaActividadConfig | undefined): boolean {
  return empresaActividadUsuarioRuleViolated(cfg)
    || empresaActividadAutorizaRuleViolated(cfg);
}

/** `true` si alguna actividad del workflow incumple al menos una regla. */
function computeEmpresaConfigWarning(
  baseNodos: Record<string, unknown>[],
  actividades: Record<string, EmpresaActividadConfig>,
): boolean {
  for (const nodo of baseNodos) {
    if (nodo.tipoNodo !== 'actividad') continue;
    const cfg = findEmpresaActividadConfig(actividades, nodo);
    if (empresaActividadConfigHasWarning(cfg)) return true;
  }
  return false;
}

function withEmpresaConfigWarning(
  empresa: EmpresaSeleccionadaItem,
  baseNodos: Record<string, unknown>[],
): EmpresaSeleccionadaItem {
  return {
    ...empresa,
    warning: computeEmpresaConfigWarning(baseNodos, empresa.actividades ?? {}),
  };
}

function mapEmpresasConfigWithWarning(
  empresas: EmpresaSeleccionadaItem[],
  baseNodos: Record<string, unknown>[],
): EmpresaSeleccionadaItem[] {
  return empresas.map(e => withEmpresaConfigWarning(e, baseNodos));
}

function empresasConfigNotifyEqual(
  prev: EmpresaSeleccionadaItem[],
  next: EmpresaSeleccionadaItem[],
): boolean {
  if (prev.length !== next.length) return false;
  const nextById = new Map(next.map(e => [e.id, e]));
  for (const e of prev) {
    const other = nextById.get(e.id);
    if (!other) return false;
    if (
      !!e.bloqueado !== !!other.bloqueado
      || !!e.disenoPersonalizado !== !!other.disenoPersonalizado
      || (e.empresaDisenoId ?? null) !== (other.empresaDisenoId ?? null)
    ) return false;
  }
  return true;
}

function buildEmpresaNotifyKey(
  empresas: Array<{ id: number }>,
  bloqueadoById: Record<number, boolean>,
  modificadoById: Record<number, boolean>,
  disenoPersonalizadoById: Record<number, boolean>,
  empresaDisenoIdById: Record<number, number | null | undefined>,
): string {
  return empresas
    .map(e => {
      const disenoId = empresaDisenoIdById[e.id];
      return `${e.id}:${bloqueadoById[e.id] ? 1 : 0}:${modificadoById[e.id] ? 1 : 0}:${disenoPersonalizadoById[e.id] ? 1 : 0}:${disenoId ?? ''}`;
    })
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .join(',');
}

function mergeNodoWithEmpresaActividad(
  baseNodo: Record<string, unknown>,
  actividadCfg?: EmpresaActividadConfig,
): Record<string, unknown> {
  if (!actividadCfg) {
    return {
      ...baseNodo,
      habilitadoEmpresa: null,
      asignarAContadorEmpresa: null,
      requiereAutorizacionEmpresa: null,
      requiereArchivoEmpresa: null,
    };
  }

  return reconcileRowEmpresaActividadFields({
    ...baseNodo,
    _empresaActividadConfigId: actividadCfg.id,
    habilitadoEmpresa: actividadCfg.habilitado ?? null,
    asignarAContadorEmpresa: actividadCfg.asignarAContador ?? null,
    requiereAutorizacionEmpresa: actividadCfg.requiereAutorizacion ?? null,
    requiereArchivoEmpresa: actividadCfg.requiereArchivo ?? null,
    usuario: actividadCfg.usuario !== undefined ? actividadCfg.usuario : baseNodo.usuario,
    autoriza: actividadCfg.autoriza !== undefined ? actividadCfg.autoriza : baseNodo.autoriza,
  });
}

function buildNodosForEmpresa(
  baseNodos: Record<string, unknown>[],
  empresa?: EmpresaSeleccionadaItem,
): Record<string, unknown>[] {
  const actividades = empresa?.actividades ?? {};
  return baseNodos.map(nodo =>
    mergeNodoWithEmpresaActividad(nodo, findEmpresaActividadConfig(actividades, nodo)),
  );
}

function getProfaseActividadId(nodo: Record<string, unknown>): number | null {
  const id = (nodo.profaseActividad as { id?: number } | undefined)?.id;
  if (id == null || Number.isNaN(Number(id))) return null;
  return Number(id);
}

/** Clave al guardar en `actividades` (el API usa `nodoId` = id del nodo del workflow). */
function getEmpresaActividadMapKey(nodo: Record<string, unknown>): string | null {
  if (nodo.id != null && !Number.isNaN(Number(nodo.id))) return String(nodo.id);
  const actividadId = getProfaseActividadId(nodo);
  if (actividadId != null) return String(actividadId);
  return null;
}

function findEmpresaActividadConfig(
  actividades: Record<string, EmpresaActividadConfig>,
  nodo: Record<string, unknown>,
): EmpresaActividadConfig | undefined {
  if (nodo.id != null) {
    const byNodoId = actividades[String(nodo.id)];
    if (byNodoId) return byNodoId;
  }
  const actividadId = getProfaseActividadId(nodo);
  if (actividadId != null) {
    return actividades[String(actividadId)];
  }
  return undefined;
}

function empresaActividadNodoIdForApi(nodo: Record<string, unknown>): number {
  if (nodo.id != null && !Number.isNaN(Number(nodo.id))) return Number(nodo.id);
  const actividadId = getProfaseActividadId(nodo);
  if (actividadId != null) return actividadId;
  return Number(nodo.id);
}

/** Actividades por defecto al agregar una empresa nueva a seleccionadas (sin config previa). */
function buildDefaultEmpresaActividadesNoModificar(
  baseNodos: Record<string, unknown>[],
): Record<string, EmpresaActividadConfig> {
  const actividades: Record<string, EmpresaActividadConfig> = {};
  for (const nodo of baseNodos) {
    if (nodo.tipoNodo !== 'actividad') continue;
    const key = getEmpresaActividadMapKey(nodo);
    if (key == null) continue;
    actividades[key] = {
      nodoId: empresaActividadNodoIdForApi(nodo),
      habilitado: 'NO_MODIFICAR',
      requiereAutorizacion: null,
      asignarAContador: null,
      requiereArchivo: false,
      usuario: null,
      autoriza: null,
    };
  }
  return actividades;
}

function resolveEmpresaActividadesOnSelect(
  existing: EmpresaSeleccionadaItem | undefined,
  incoming: EmpresaSeleccionadaItem,
  baseNodos: Record<string, unknown>[],
): Record<string, EmpresaActividadConfig> {
  const actividades = existing?.actividades ?? incoming.actividades ?? {};
  if (existing != null || Object.keys(actividades).length > 0) return actividades;
  return buildDefaultEmpresaActividadesNoModificar(baseNodos);
}

function nodoToEmpresaActividadConfig(nodo: Record<string, unknown>): EmpresaActividadConfig {
  const nodoId = empresaActividadNodoIdForApi(nodo);
  return reconcileEmpresaActividadRequiereAutorizAutoriza(
    reconcileEmpresaActividadUsuarioAsignar({
      id: nodo._empresaActividadConfigId as number | undefined,
      nodoId,
      habilitado: normalizeHabilitadoEmpresaStored(nodo.habilitadoEmpresa),
      requiereAutorizacion: (nodo.requiereAutorizacionEmpresa as boolean | null | undefined) ?? null,
      asignarAContador: (nodo.asignarAContadorEmpresa as boolean | null | undefined) ?? null,
      requiereArchivo: (nodo.requiereArchivoEmpresa as boolean | null | undefined) ?? null,
      usuario: (nodo.usuario as EmpresaActividadConfig['usuario']) ?? null,
      autoriza: (nodo.autoriza as EmpresaActividadConfig['autoriza']) ?? null,
    }),
  );
}

/** El combo muestra "No Modificar" si `habilitado` aún no está definido; el API no acepta null. */
function habilitadoEmpresaForApi(
  value: unknown,
  previous?: unknown,
): HabilitadoEmpresaSelectValue {
  return normalizeHabilitadoEmpresaStored(value)
    ?? normalizeHabilitadoEmpresaStored(previous)
    ?? 'NO_MODIFICAR';
}

function mergeEmpresaActividadFromRow(
  prevCfg: EmpresaActividadConfig | undefined,
  fromRow: EmpresaActividadConfig,
): EmpresaActividadConfig {
  return {
    ...fromRow,
    id: fromRow.id ?? prevCfg?.id,
    habilitado: habilitadoEmpresaForApi(fromRow.habilitado, prevCfg?.habilitado),
    requiereAutorizacion: fromRow.requiereAutorizacion ?? prevCfg?.requiereAutorizacion ?? null,
    asignarAContador: fromRow.asignarAContador ?? prevCfg?.asignarAContador ?? null,
    requiereArchivo: fromRow.requiereArchivo ?? prevCfg?.requiereArchivo ?? null,
  };
}

function buildEmpresaActividadesClipboardMap(
  nodos: Record<string, unknown>[],
): Record<string, EmpresaActividadConfig> {
  const actividades: Record<string, EmpresaActividadConfig> = {};
  for (const nodo of nodos) {
    const key = getEmpresaActividadMapKey(nodo);
    if (key == null) continue;
    actividades[key] = nodoToEmpresaActividadConfig(nodo);
  }
  return actividades;
}

function parseEmpresaActividadesClipboardText(
  text: string,
): Record<string, EmpresaActividadConfig> | null {
  try {
    const json = JSON.parse(text.trim());
    const raw = json?.data?.actividades;
    if (!raw || typeof raw !== 'object') return null;
    return parseEmpresaActividades(raw);
  } catch {
    return null;
  }
}

function mergePastedEmpresaActividades(
  existing: Record<string, EmpresaActividadConfig>,
  pasted: Record<string, EmpresaActividadConfig>,
  baseNodos: Record<string, unknown>[],
): { actividades: Record<string, EmpresaActividadConfig>; applied: number } {
  const validKeys = new Set<string>();
  for (const nodo of baseNodos) {
    const key = getEmpresaActividadMapKey(nodo);
    if (key) validKeys.add(key);
  }

  const actividades = { ...existing };
  let applied = 0;
  for (const [key, cfg] of Object.entries(pasted)) {
    if (!validKeys.has(key)) continue;
    const nodoId = Number(cfg.nodoId ?? key);
    if (Number.isNaN(nodoId)) continue;
    const prevCfg = actividades[key];
    let nextCfg = reconcileEmpresaActividadRequiereAutorizAutoriza(
      reconcileEmpresaActividadUsuarioAsignar({
        id: cfg.id ?? prevCfg?.id,
        nodoId,
        habilitado: habilitadoEmpresaForApi(cfg.habilitado, prevCfg?.habilitado),
        requiereAutorizacion: cfg.requiereAutorizacion ?? prevCfg?.requiereAutorizacion ?? null,
        asignarAContador: cfg.asignarAContador ?? prevCfg?.asignarAContador ?? null,
        modificado: prevCfg?.modificado,
        usuario: cfg.usuario ?? null,
        autoriza: cfg.autoriza ?? null,
        requiereArchivo: cfg.requiereArchivo ?? prevCfg?.requiereArchivo ?? null,
      }),
    );
    if (!empresaActividadConfigValuesEqual(prevCfg, nextCfg)) {
      nextCfg = markEmpresaActividadModificado(nextCfg);
    }
    actividades[key] = nextCfg;
    applied += 1;
  }
  return { actividades, applied };
}

function applyPastedActividadesToCheckedEmpresas(
  pasted: Record<string, EmpresaActividadConfig>,
  empresas: EmpresaSeleccionadaItem[],
  checkedRfcEmpresaIds: Set<number>,
  baseNodos: Record<string, unknown>[],
  excludeEmpresaId?: number | null,
): { updatesById: Map<number, EmpresaSeleccionadaItem>; activitiesApplied: number } {
  const updatesById = new Map<number, EmpresaSeleccionadaItem>();
  let activitiesApplied = 0;

  for (const empresa of empresas) {
    if (empresa.bloqueado) continue;
    if (!checkedRfcEmpresaIds.has(empresa.id)) continue;
    if (excludeEmpresaId != null && empresa.id === excludeEmpresaId) continue;

    const { actividades, applied } = mergePastedEmpresaActividades(
      empresa.actividades ?? {},
      pasted,
      baseNodos,
    );
    if (!applied) continue;
    activitiesApplied = applied;
    updatesById.set(empresa.id, markEmpresaModificado({ ...empresa, actividades }));
  }

  return { updatesById, activitiesApplied };
}

function copyActivityConfigsToCheckedEmpresas(
  activityConfigs: Record<string, EmpresaActividadConfig>,
  empresas: EmpresaSeleccionadaItem[],
  checkedRfcEmpresaIds: Set<number>,
): Map<number, EmpresaSeleccionadaItem> {
  const updatesById = new Map<number, EmpresaSeleccionadaItem>();

  for (const empresa of empresas) {
    if (empresa.bloqueado) continue;
    if (!checkedRfcEmpresaIds.has(empresa.id)) continue;

    const actividades = { ...(empresa.actividades ?? {}) };
    let changed = false;

    for (const [key, cfg] of Object.entries(activityConfigs)) {
      const prevCfg = actividades[key];
      const nextCfg = reconcileEmpresaActividadUsuarioAsignar({
        id: prevCfg?.id ?? cfg.id,
        nodoId: cfg.nodoId,
        habilitado: habilitadoEmpresaForApi(cfg.habilitado, prevCfg?.habilitado),
        requiereAutorizacion: cfg.requiereAutorizacion ?? prevCfg?.requiereAutorizacion ?? null,
        asignarAContador: cfg.asignarAContador ?? prevCfg?.asignarAContador ?? null,
        modificado: cfg.modificado ?? prevCfg?.modificado,
        usuario: cfg.usuario ?? null,
        autoriza: cfg.autoriza ?? null,
        requiereArchivo: cfg.requiereArchivo ?? prevCfg?.requiereArchivo ?? null,
      });
      if (
        prevCfg?.usuario?.id === nextCfg.usuario?.id
        && prevCfg?.autoriza?.id === nextCfg.autoriza?.id
        && prevCfg?.habilitado === nextCfg.habilitado
        && prevCfg?.requiereAutorizacion === nextCfg.requiereAutorizacion
        && prevCfg?.asignarAContador === nextCfg.asignarAContador
        && prevCfg?.requiereArchivo === nextCfg.requiereArchivo
      ) {
        continue;
      }
      actividades[key] = markEmpresaActividadModificado(nextCfg);
      changed = true;
    }

    if (changed) updatesById.set(empresa.id, markEmpresaModificado({ ...empresa, actividades }));
  }

  return updatesById;
}

function clearUsuarioFieldOnCheckedEmpresas(
  field: 'usuario' | 'autoriza',
  actividadKey: string,
  apiNodoId: number | undefined,
  empresas: EmpresaSeleccionadaItem[],
  checkedRfcEmpresaIds: Set<number>,
  excludeEmpresaId?: number | null,
): Map<number, EmpresaSeleccionadaItem> {
  const updatesById = new Map<number, EmpresaSeleccionadaItem>();

  for (const empresa of empresas) {
    if (empresa.bloqueado) continue;
    if (!checkedRfcEmpresaIds.has(empresa.id)) continue;
    if (excludeEmpresaId != null && empresa.id === excludeEmpresaId) continue;

    const actividades = { ...(empresa.actividades ?? {}) };
    const prevCfg = actividades[actividadKey];
    if (!prevCfg?.[field]?.id) continue;

    const clearedCfg = markEmpresaActividadModificado({
      ...(prevCfg ?? { nodoId: apiNodoId }),
      nodoId: prevCfg?.nodoId ?? apiNodoId,
      [field]: null,
    });
    actividades[actividadKey] = field === 'usuario'
      ? reconcileEmpresaActividadUsuarioAsignar(clearedCfg)
      : reconcileEmpresaActividadRequiereAutorizAutoriza(clearedCfg);
    updatesById.set(empresa.id, markEmpresaModificado({ ...empresa, actividades }));
  }

  return updatesById;
}

function buildUsuarioAssignActivityConfigs(
  pending: PendingUsuarioAssign,
  allNodos: Record<string, unknown>[],
  selectedRowIds: Set<number>,
): Record<string, EmpresaActividadConfig> {
  const rowIds = new Set<number>();
  const targetId = pending.targetRow?.id as number | undefined;

  if (pending.columnDrop) {
    for (const n of allNodos) {
      if (n.id != null && n.tipoNodo === 'actividad' && !n.bloqueado) {
        rowIds.add(n.id as number);
      }
    }
  } else {
    if (targetId != null) {
      const target = allNodos.find(n => n.id === targetId);
      if (target && !target.bloqueado) rowIds.add(targetId);
    }
    for (const n of allNodos) {
      if (n.id != null && selectedRowIds.has(n.id as number) && !n.bloqueado) {
        rowIds.add(n.id as number);
      }
    }
  }

  const configs: Record<string, EmpresaActividadConfig> = {};
  for (const n of allNodos) {
    if (n.id == null || !rowIds.has(n.id as number)) continue;
    const key = getEmpresaActividadMapKey(n);
    if (key) configs[key] = nodoToEmpresaActividadConfig(n);
  }
  return configs;
}

async function readPlainTextFromClipboardRobust(): Promise<string | null> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.readText) {
    try {
      return await navigator.clipboard.readText();
    } catch {
      return null;
    }
  }
  return null;
}

async function copyPlainTextToClipboardRobust(text: string): Promise<boolean> {
  const t = text.trim();
  if (!t) return false;
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(t);
      return true;
    } catch {
      /* fallback */
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = t;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.left = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

type EmpresaActividadesClipboardPayload = {
  data: { actividades: Record<string, EmpresaActividadConfig> };
};

function serializeEmpresaActividadesClipboardPayload(
  payload: EmpresaActividadesClipboardPayload,
): string {
  return JSON.stringify(payload);
}

async function copyEmpresaActividadesToClipboardRobust(
  payload: EmpresaActividadesClipboardPayload,
): Promise<boolean> {
  const text = serializeEmpresaActividadesClipboardPayload(payload).trim();
  if (!text) return false;

  if (typeof navigator !== 'undefined' && navigator.clipboard?.write) {
    try {
      const profaseBlob = new Blob([text], { type: PROFASE_ACTIVIDADES_CLIPBOARD_MIME });
      const plainBlob = new Blob([text], { type: 'text/plain' });
      const item = new ClipboardItem({
        [PROFASE_ACTIVIDADES_CLIPBOARD_MIME]: profaseBlob,
        'text/plain': plainBlob,
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      /* fallback a solo texto */
    }
  }

  return copyPlainTextToClipboardRobust(text);
}

async function readEmpresaActividadesClipboardTextRobust(): Promise<string | null> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.read) {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        if (item.types.includes(PROFASE_ACTIVIDADES_CLIPBOARD_MIME)) {
          const blob = await item.getType(PROFASE_ACTIVIDADES_CLIPBOARD_MIME);
          const text = await blob.text();
          if (text.trim()) return text;
        }
      }
      for (const item of items) {
        if (item.types.includes('text/plain')) {
          const blob = await item.getType('text/plain');
          const text = await blob.text();
          if (text.trim()) return text;
        }
      }
    } catch {
      /* permisos o API no disponible */
    }
  }
  return readPlainTextFromClipboardRobust();
}

/** Aplana nodos del config (envoltorio `{ nodo, habilitado, ... }` o formato legacy). */
function normalizeConfigNodo(item: WorkflowConfigNodoItem): Record<string, unknown> | null {
  const wrapped = item?.nodo != null;
  const nodo = (wrapped ? item.nodo : item) as WorkflowConfigNodoItem['nodo'];
  if (!nodo || nodo.tipoNodo !== 'actividad') return null;

  return {
    ...nodo,
    habilitado: wrapped ? (item.habilitado ?? null) : (nodo.habilitado ?? null),
    requiereAutorizacion: wrapped
      ? (item.requiereAutorizacion ?? null)
      : (nodo.requiereAutorizacion ?? null),
    bloqueado: wrapped ? !!item.bloqueado : false,
    modificado: wrapped ? !!item.modificado : false,
    usuario: wrapped ? (item.usuario ?? null) : null,
    autoriza: wrapped ? (item.autoriza ?? null) : null,
  };
}

function normalizeConfigNodos(nodos: WorkflowConfigNodoItem[]): Record<string, unknown>[] {
  return nodos
    .map(normalizeConfigNodo)
    .filter((n): n is Record<string, unknown> => n != null);
}

interface EmpresaItem {
  id: number;
  rfc?: string;
  razonSocial?: string;
  grupoEmpresarial?: {
    alias?: string;
    grpAliasEquilibrio?: string;
    grpAliasDecomsa?: string;
  };
}

interface UsuarioSearchItem {
  id: number;
  nombre?: string;
  apellido?: string;
  nombreCompleto?: string;
  esContador?: boolean;
  sysPerfil?: { nombre?: string };
}

function parseUsuariosResponse(data: unknown): UsuarioSearchItem[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.content)) return o.content as UsuarioSearchItem[];
    if (Array.isArray(o.data)) return o.data as UsuarioSearchItem[];
  }
  return [];
}

function usuarioDisplayName(u: UsuarioSearchItem): string {
  const full = u.nombreCompleto?.trim();
  if (full) return full;
  const parts = `${u.nombre ?? ''} ${u.apellido ?? ''}`.trim();
  return parts || `#${u.id}`;
}

function parseConfigUsuarios(raw: unknown): ConfigUsuarioRef[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((u): u is ConfigUsuarioRef => !!u && typeof u === 'object' && typeof (u as ConfigUsuarioRef).id === 'number')
    .map(u => ({ id: u.id, nombreCompleto: u.nombreCompleto }));
}

function configUsuariosToPinnedMap(usuarios: ConfigUsuarioRef[]): Record<number, UsuarioSearchItem> {
  const map: Record<number, UsuarioSearchItem> = {};
  for (const u of usuarios) {
    map[u.id] = { id: u.id, nombreCompleto: u.nombreCompleto };
  }
  return map;
}

function usuarioLabel(usuario: { id?: number; nombre?: string; apellido?: string } | null | undefined): string {
  if (!usuario?.id) return '';
  const name = `${usuario.nombre ?? ''} ${usuario.apellido ?? ''}`.trim();
  return name || `#${usuario.id}`;
}

function empresaLabel(e: EmpresaItem) {
  if (e.rfc && e.razonSocial) return `${e.rfc} — ${e.razonSocial}`;
  return e.razonSocial ?? e.rfc ?? `Empresa #${e.id}`;
}

function grupoEmpresarialAlias(e: EmpresaItem): string {
  const g = e.grupoEmpresarial;
  if (!g) return '';
  return g.alias ?? g.grpAliasDecomsa ?? g.grpAliasEquilibrio ?? '';
}

function formatEmpresaClipboardRow(e: EmpresaItem): string {
  const alias = grupoEmpresarialAlias(e);
  const rfc = e.rfc?.trim() ?? '';
  const razonSocial = e.razonSocial?.trim() ?? '';
  return [alias, rfc, razonSocial].join('\t');
}

function buildEmpresasClipboardText(empresas: EmpresaItem[]): string {
  return empresas.map(formatEmpresaClipboardRow).join('\n');
}

type EmpresasClipboardPayload = {
  data: {
    empresas: Array<{ alias?: string; rfc?: string; razonSocial?: string }>;
  };
};

function buildEmpresasClipboardPayload(empresas: EmpresaItem[]): EmpresasClipboardPayload {
  return {
    data: {
      empresas: empresas.map(e => ({
        alias: grupoEmpresarialAlias(e) || undefined,
        rfc: e.rfc?.trim() || undefined,
        razonSocial: e.razonSocial?.trim() || undefined,
      })),
    },
  };
}

function normalizeEmpresaRfc(rfc: string): string {
  return rfc.trim().toUpperCase();
}

function parseRfcsFromProfaseEmpresasJson(text: string): string[] | null {
  try {
    const json = JSON.parse(text.trim()) as EmpresasClipboardPayload;
    const list = json?.data?.empresas;
    if (!Array.isArray(list)) return null;
    const rfcs = list
      .map(row => row?.rfc?.trim())
      .filter((rfc): rfc is string => !!rfc)
      .map(normalizeEmpresaRfc);
    return rfcs.length ? rfcs : null;
  } catch {
    return null;
  }
}

const EXCEL_EMPRESAS_MIN_COLUMNS = 1;
const EXCEL_EMPRESAS_MAX_COLUMNS = 3;

/** Índice del RFC: 1.ª columna con 1–2 columnas; 2.ª con 3 (alias, RFC, razón social). */
function rfcColumnIndexForExcelEmpresasRow(columnCount: number): number {
  return columnCount === 3 ? 1 : 0;
}

/** Pegado desde Excel: 1–3 columnas por fila y al menos 1 fila con datos. */
function isExcelEmpresasGridPaste(text: string): boolean {
  const lines = text.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  if (lines.length < 1) return false;

  for (const line of lines) {
    const colCount = line.split('\t').length;
    if (colCount < EXCEL_EMPRESAS_MIN_COLUMNS || colCount > EXCEL_EMPRESAS_MAX_COLUMNS) {
      return false;
    }
  }

  return true;
}

function parseRfcsFromExcelEmpresasGrid(text: string): string[] | null {
  if (!isExcelEmpresasGridPaste(text)) return null;

  const rfcs: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const cols = trimmed.split('\t').map(col => col.trim());
    if (cols.length < EXCEL_EMPRESAS_MIN_COLUMNS || cols.length > EXCEL_EMPRESAS_MAX_COLUMNS) {
      continue;
    }
    const rfc = cols[rfcColumnIndexForExcelEmpresasRow(cols.length)];
    if (rfc) rfcs.push(normalizeEmpresaRfc(rfc));
  }

  return rfcs.length ? rfcs : null;
}

function parseRfcsFromEmpresasClipboardText(text: string): string[] | null {
  const fromJson = parseRfcsFromProfaseEmpresasJson(text);
  if (fromJson?.length) return fromJson;
  return parseRfcsFromExcelEmpresasGrid(text);
}

type ClipboardPasteSnapshot = {
  profaseEmpresasText: string | null;
  profaseActividadesText: string | null;
  plainText: string | null;
};

async function readClipboardPasteSnapshot(): Promise<ClipboardPasteSnapshot> {
  const snapshot: ClipboardPasteSnapshot = {
    profaseEmpresasText: null,
    profaseActividadesText: null,
    plainText: null,
  };

  if (typeof navigator !== 'undefined' && navigator.clipboard?.read) {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        if (item.types.includes(PROFASE_EMPRESAS_CLIPBOARD_MIME)) {
          const blob = await item.getType(PROFASE_EMPRESAS_CLIPBOARD_MIME);
          snapshot.profaseEmpresasText = await blob.text();
        }
        if (item.types.includes(PROFASE_ACTIVIDADES_CLIPBOARD_MIME)) {
          const blob = await item.getType(PROFASE_ACTIVIDADES_CLIPBOARD_MIME);
          snapshot.profaseActividadesText = await blob.text();
        }
      }
      for (const item of items) {
        if (snapshot.plainText) break;
        if (item.types.includes('text/plain')) {
          const blob = await item.getType('text/plain');
          snapshot.plainText = await blob.text();
        }
      }
    } catch {
      /* permisos o API no disponible */
    }
  }

  if (!snapshot.plainText) {
    snapshot.plainText = await readPlainTextFromClipboardRobust();
  }

  return snapshot;
}

type ResolvedClipboardPaste =
  | { kind: 'empresas-rfc'; rfcs: string[] }
  | { kind: 'actividades'; text: string };

function resolveEmpresasRfcsFromClipboard(snapshot: ClipboardPasteSnapshot): string[] | null {
  if (snapshot.profaseEmpresasText?.trim()) {
    const rfcs = parseRfcsFromEmpresasClipboardText(snapshot.profaseEmpresasText);
    if (rfcs?.length) return rfcs;
  }

  const plain = snapshot.plainText?.trim();
  if (!plain || parseEmpresaActividadesClipboardText(plain)) return null;

  return parseRfcsFromExcelEmpresasGrid(plain);
}

function resolveClipboardPaste(snapshot: ClipboardPasteSnapshot): ResolvedClipboardPaste | null {
  const rfcs = resolveEmpresasRfcsFromClipboard(snapshot);
  if (rfcs?.length) return { kind: 'empresas-rfc', rfcs };

  if (snapshot.profaseActividadesText?.trim()) {
    const text = snapshot.profaseActividadesText.trim();
    if (parseEmpresaActividadesClipboardText(text)) {
      return { kind: 'actividades', text };
    }
  }

  const plain = snapshot.plainText?.trim();
  if (!plain) return null;

  if (parseEmpresaActividadesClipboardText(plain)) {
    return { kind: 'actividades', text: plain };
  }

  return null;
}

async function copyEmpresasListToClipboardRobust(empresas: EmpresaItem[]): Promise<boolean> {
  const plainText = buildEmpresasClipboardText(empresas).trim();
  const jsonText = JSON.stringify(buildEmpresasClipboardPayload(empresas));
  if (!plainText && !jsonText.trim()) return false;

  if (typeof navigator !== 'undefined' && navigator.clipboard?.write) {
    try {
      const profaseBlob = new Blob([jsonText], { type: PROFASE_EMPRESAS_CLIPBOARD_MIME });
      const plainBlob = new Blob([plainText], { type: 'text/plain' });
      const item = new ClipboardItem({
        [PROFASE_EMPRESAS_CLIPBOARD_MIME]: profaseBlob,
        'text/plain': plainBlob,
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      /* fallback a solo texto */
    }
  }

  return copyPlainTextToClipboardRobust(plainText);
}

function empresaPrimaryLine(e: EmpresaItem): string {
  if (e.rfc && e.razonSocial) return `${e.rfc} — ${e.razonSocial}`;
  return e.razonSocial ?? e.rfc ?? `Empresa #${e.id}`;
}

function buildActiveEmpresaGroupHeader(
  e: EmpresaItem | null | undefined,
): WorkflowActiveEmpresaGroupHeader {
  if (!e) {
    return { mainLine: 'Seleccione una empresa', fullTitle: 'Seleccione una empresa' };
  }
  const grupoAlias = grupoEmpresarialAlias(e) || undefined;
  const mainParts = [e.rfc?.trim(), e.razonSocial?.trim()].filter((part): part is string => !!part);
  const mainLine = mainParts.length > 0 ? mainParts.join(' - ') : `Empresa #${e.id}`;
  const fullParts = [grupoAlias, ...mainParts].filter((part): part is string => !!part);
  const fullTitle = fullParts.length > 0 ? fullParts.join(' - ') : mainLine;
  return { grupoAlias, mainLine, fullTitle };
}

const EmpresaListLabel = memo(function EmpresaListLabel({
  empresa,
  active,
  modificado,
}: {
  empresa: EmpresaItem;
  active?: boolean;
  modificado?: boolean;
}) {
  const alias = grupoEmpresarialAlias(empresa);
  return (
    <span style={empresaS.listItemText}>
      <span style={{
        ...empresaS.listItemPrimary,
        ...(modificado ? empresaS.listItemPrimaryModificado : {}),
      }}
      >
        {empresaPrimaryLine(empresa)}
      </span>
      {alias ? (
        <span style={{
          ...empresaS.listItemAlias,
          ...(active ? empresaS.listItemAliasActive : {}),
        }}
        >
          {alias}
        </span>
      ) : null}
    </span>
  );
});

const AvailableEmpresaRow = memo(function AvailableEmpresaRow({
  item,
  active,
  loadingEmpresas,
  onHighlight,
  onDragEnd,
}: {
  item: EmpresaItemIndexed;
  active: boolean;
  loadingEmpresas: boolean;
  onHighlight: (id: number) => void;
  onDragEnd: () => void;
}) {
  return (
    <button
      type="button"
      draggable={!loadingEmpresas}
      title="Arrastrar a la otra lista"
      onDragStart={e => handleEmpresaDragStart(e, 'available', item)}
      onDragEnd={onDragEnd}
      style={{
        ...empresaS.listItem,
        ...empresaS.listItemDraggable,
        ...(active ? empresaS.listItemActive : {}),
        minHeight: EMPRESA_LIST_ROW_HEIGHT,
        boxSizing: 'border-box',
      }}
      onClick={() => onHighlight(item.id)}
    >
      <EmpresaListLabel empresa={item} active={active} />
    </button>
  );
});

type EmpresaItemIndexed = EmpresaItem & { searchIndex: string };

function buildEmpresaSearchIndex(e: EmpresaItem): string {
  const g = e.grupoEmpresarial;
  return [
    e.rfc,
    e.razonSocial,
    g?.alias,
    g?.grpAliasEquilibrio,
    g?.grpAliasDecomsa,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function indexEmpresas(list: EmpresaItem[]): EmpresaItemIndexed[] {
  return list.map(e => ({ ...e, searchIndex: buildEmpresaSearchIndex(e) }));
}

function matchesEmpresaFilter(e: EmpresaItemIndexed, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return e.searchIndex.includes(q);
}

const EMPRESA_LIST_ROW_HEIGHT = 44;
const EMPRESA_LIST_VIEW_HEIGHT = 280;
/** Espacio de la barra de búsqueda dentro del panel (toolbar + padding). */
const EMPRESA_LIST_TOOLBAR_OFFSET = 44;
const EMPRESA_LIST_OVERSCAN = 6;

const virtualListInnerStyle: React.CSSProperties = {
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
};

function VirtualEmpresaList({
  items,
  empty,
  emptyStyle,
  renderItem,
  viewportHeight = EMPRESA_LIST_VIEW_HEIGHT,
}: {
  items: EmpresaItemIndexed[];
  empty: React.ReactNode;
  emptyStyle?: React.CSSProperties;
  renderItem: (item: EmpresaItemIndexed, index: number) => React.ReactNode;
  viewportHeight?: number;
}) {
  const [scrollTop, setScrollTop] = useState(0);

  if (!items.length) {
    return <div style={emptyStyle}>{empty}</div>;
  }

  const start = Math.max(0, Math.floor(scrollTop / EMPRESA_LIST_ROW_HEIGHT) - EMPRESA_LIST_OVERSCAN);
  const visibleCount = Math.ceil(viewportHeight / EMPRESA_LIST_ROW_HEIGHT) + EMPRESA_LIST_OVERSCAN * 2;
  const end = Math.min(items.length, start + visibleCount);
  const offsetY = start * EMPRESA_LIST_ROW_HEIGHT;
  const totalHeight = items.length * EMPRESA_LIST_ROW_HEIGHT;

  return (
    <div
      style={{
        ...virtualListInnerStyle,
        height: viewportHeight,
      }}
      onScroll={e => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div style={{ transform: `translateY(${offsetY}px)` }}>
          {items.slice(start, end).map((item, i) => renderItem(item, start + i))}
        </div>
      </div>
    </div>
  );
}

const EMPRESA_LIST_CONTENT_HEIGHT = EMPRESA_LIST_VIEW_HEIGHT - EMPRESA_LIST_TOOLBAR_OFFSET;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function parseEmpresasResponse(data: unknown): EmpresaItem[] {
  if (Array.isArray(data)) return data as EmpresaItem[];
  if (data && typeof data === 'object' && Array.isArray((data as { content?: unknown }).content)) {
    return (data as { content: EmpresaItem[] }).content;
  }
  return [];
}

function sortEmpresas<T extends EmpresaItem>(list: T[]): T[] {
  return [...list].sort((a, b) =>
    empresaLabel(a).localeCompare(empresaLabel(b), undefined, { numeric: true }),
  );
}

function toEmpresaItemIndexed(
  empresa: EmpresaItem,
  byId?: Map<number, EmpresaItemIndexed>,
): EmpresaItemIndexed {
  const found = byId?.get(empresa.id);
  if (found) return found;
  return { ...empresa, searchIndex: buildEmpresaSearchIndex(empresa) };
}

interface WorkflowConfigEmpresasSectionProps {
  workflowId?: string;
  workflowPeriodicidad?: string | null;
  initialConfigUsuarios?: ConfigUsuarioRef[];
  initialSelectedEmpresas?: EmpresaSeleccionadaItem[];
  activeEmpresaId?: number | null;
  onSelectedEmpresasChange?: (empresas: EmpresaSeleccionadaItem[]) => void;
  onActiveEmpresaChange?: (empresaId: number | null) => void;
  onActiveEmpresaDetailChange?: (empresa: EmpresaItem | null) => void;
  onCopyEmpresaActividades?: (empresaId: number) => void;
  onPasteEmpresaActividades?: (clipboardText?: string) => void;
  pasteClipboardInvalidLocked?: boolean;
  empresaActividadesEditing?: boolean;
  onCheckedRfcIdsChange?: (ids: number[]) => void;
  onHiddenEmpresasChange?: (hiddenIds: Set<number>) => void;
  onHydrationSettled?: () => void;
  showToast: ShowPageToast;
}

function WorkflowConfigEmpresasSection({
  workflowId,
  workflowPeriodicidad = null,
  initialConfigUsuarios = [],
  initialSelectedEmpresas = [],
  activeEmpresaId = null,
  onSelectedEmpresasChange,
  onActiveEmpresaChange,
  onActiveEmpresaDetailChange,
  onCopyEmpresaActividades,
  onPasteEmpresaActividades,
  pasteClipboardInvalidLocked = false,
  empresaActividadesEditing = false,
  onCheckedRfcIdsChange,
  onHiddenEmpresasChange,
  onHydrationSettled,
  showToast,
}: WorkflowConfigEmpresasSectionProps) {
  const [bloqueadoById, setBloqueadoById] = useState<Record<number, boolean>>({});
  const [warningById, setWarningById] = useState<Record<number, boolean>>({});
  const [modificadoById, setModificadoById] = useState<Record<number, boolean>>({});
  const [disenoPersonalizadoById, setDisenoPersonalizadoById] = useState<Record<number, boolean>>({});
  const [empresaDisenoIdById, setEmpresaDisenoIdById] = useState<Record<number, number | null>>({});

  const initialSelectedIds = useMemo(
    () => initialSelectedEmpresas.map(e => e.id),
    [initialSelectedEmpresas],
  );
  const [allEmpresas, setAllEmpresas] = useState<EmpresaItemIndexed[]>([]);
  const [selectedEmpresas, setSelectedEmpresas] = useState<EmpresaItemIndexed[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [highlightAvailableId, setHighlightAvailableId] = useState<number | null>(null);
  const [highlightSelectedId, setHighlightSelectedId] = useState<number | null>(null);
  const selectedEmpresaRowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const [searchParams] = useSearchParams();
  const empresaQuery = (searchParams.get('query') ?? '').trim();
  const empresaQueryRef = useRef(empresaQuery);
  empresaQueryRef.current = empresaQuery;
  const [filterAvailable, setFilterAvailable] = useState(empresaQuery);
  const [filterSelected, setFilterSelected] = useState(empresaQuery);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState<UsuarioSearchItem[]>([]);
  const [pinnedUsersById, setPinnedUsersById] = useState<Record<number, UsuarioSearchItem>>({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [highlightUserId, setHighlightUserId] = useState<number | null>(null);
  const [checkedRfcIds, setCheckedRfcIds] = useState<Set<number>>(() => new Set());
  const [empresaDragOverPanel, setEmpresaDragOverPanel] = useState<EmpresaPanelSide | null>(null);
  const [hiddenEmpresaIds, setHiddenEmpresaIds] = useState<Set<number>>(() => new Set());
  const [draftHiddenEmpresaIds, setDraftHiddenEmpresaIds] = useState<Set<number>>(() => new Set());
  const [draftLockedEmpresaIds, setDraftLockedEmpresaIds] = useState<Set<number>>(() => new Set());
  const [empresasVisibilityOpen, setEmpresasVisibilityOpen] = useState(false);

  const initialSelectionKeyRef = useRef('');
  const selectedEmpresasNotifyKeyRef = useRef('');
  const initialSelectionJustAppliedRef = useRef(false);
  const hydrationNotifiedRef = useRef(false);
  const initialConfigByIdRef = useRef(new Map<number, EmpresaSeleccionadaItem>());
  const userSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userSearchAbortRef = useRef<AbortController | null>(null);
  const configUsuariosPinnedKeyRef = useRef('');
  const usersListScrollRef = useRef<HTMLDivElement>(null);

  const initialConfigUsuariosKey = useMemo(
    () => initialConfigUsuarios.map(u => u.id).join(','),
    [initialConfigUsuarios],
  );

  const initialSelectionMetaKey = useMemo(
    () => initialSelectedEmpresas
      .map(e => `${e.id}:${e.bloqueado ? 1 : 0}`)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .join(','),
    [initialSelectedEmpresas],
  );

  const initialWarningKey = useMemo(
    () => initialSelectedEmpresas
      .map(e => `${e.id}:${e.warning ? 1 : 0}`)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .join(','),
    [initialSelectedEmpresas],
  );

  const initialModificadoKey = useMemo(
    () => initialSelectedEmpresas
      .map(e => `${e.id}:${e.modificado ? 1 : 0}`)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .join(','),
    [initialSelectedEmpresas],
  );

  const initialDisenoKey = useMemo(
    () => initialSelectedEmpresas
      .map(e => `${e.id}:${e.disenoPersonalizado ? 1 : 0}:${e.empresaDisenoId ?? ''}`)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .join(','),
    [initialSelectedEmpresas],
  );

  const debouncedFilterAvailable = useDebouncedValue(filterAvailable, 200);
  const debouncedFilterSelected = useDebouncedValue(filterSelected, 200);

  const { confirmConfig, ask, dismiss } = useConfirm();

  useEffect(() => {
    setSelectedEmpresas([]);
    setHighlightAvailableId(null);
    setHighlightSelectedId(null);
    setFilterAvailable(empresaQueryRef.current);
    setFilterSelected(empresaQueryRef.current);
    setUserQuery('');
    setUserResults([]);
    setPinnedUsersById({});
    setHighlightUserId(null);
    setCheckedRfcIds(new Set());
    setBloqueadoById({});
    setWarningById({});
    setModificadoById({});
    setDisenoPersonalizadoById({});
    setEmpresaDisenoIdById({});
    setEmpresaDragOverPanel(null);
    setHiddenEmpresaIds(new Set());
    setDraftHiddenEmpresaIds(new Set());
    setDraftLockedEmpresaIds(new Set());
    setEmpresasVisibilityOpen(false);
    onHiddenEmpresasChange?.(new Set());
    initialSelectionKeyRef.current = '';
    selectedEmpresasNotifyKeyRef.current = '';
    initialSelectionJustAppliedRef.current = false;
    hydrationNotifiedRef.current = false;
    configUsuariosPinnedKeyRef.current = '';
  }, [workflowId]);

  useEffect(() => {
    setFilterAvailable(empresaQuery);
    setFilterSelected(empresaQuery);
  }, [empresaQuery]);

  useEffect(() => {
    const pinKey = `${workflowId ?? ''}:${initialConfigUsuariosKey}`;
    if (pinKey === configUsuariosPinnedKeyRef.current) return;
    configUsuariosPinnedKeyRef.current = pinKey;
    if (!initialConfigUsuariosKey) {
      setPinnedUsersById({});
      return;
    }
    setPinnedUsersById(configUsuariosToPinnedMap(initialConfigUsuarios));
  }, [workflowId, initialConfigUsuarios, initialConfigUsuariosKey]);

  useEffect(() => {
    if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);

    const q = userQuery.trim();
    if (!q) {
      userSearchAbortRef.current?.abort();
      setUserResults([]);
      setLoadingUsers(false);
      return;
    }

    userSearchDebounceRef.current = setTimeout(() => {
      userSearchAbortRef.current?.abort();
      const controller = new AbortController();
      userSearchAbortRef.current = controller;
      setLoadingUsers(true);

      axios
        .get('api/usuarios', {
          params: { query: q, page: 0, size: 30, sort: 'nombre,asc' },
          signal: controller.signal,
        })
        .then(res => {
          if (!controller.signal.aborted) {
            setUserResults(parseUsuariosResponse(res.data));
          }
        })
        .catch(err => {
          if (!axios.isCancel(err) && !controller.signal.aborted) {
            setUserResults([]);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoadingUsers(false);
        });
    }, 250);

    return () => {
      if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);
    };
  }, [userQuery]);

  useEffect(() => () => {
    userSearchAbortRef.current?.abort();
  }, []);

  const displayedUsers = useMemo(() => {
    const pinned = Object.values(pinnedUsersById);
    const pinnedIds = new Set(pinned.map(u => u.id));
    const fromSearch = userResults.filter(u => !pinnedIds.has(u.id));
    return [...pinned, ...fromSearch];
  }, [pinnedUsersById, userResults]);

  useLayoutEffect(() => {
    const q = userQuery.trim();
    if (!q || loadingUsers || userResults.length === 0) return;
    const el = usersListScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [userQuery, loadingUsers, userResults]);

  const togglePinUser = useCallback((user: UsuarioSearchItem, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    playSelectButtonSound();
    setPinnedUsersById(prev => {
      if (prev[user.id]) {
        const next = { ...prev };
        delete next[user.id];
        return next;
      }
      return { ...prev, [user.id]: user };
    });
  }, []);

  const clearUserSearch = useCallback(() => {
    const hasQuery = userQuery.trim().length > 0;
    const hasUnpinnedResults = userResults.some(u => !pinnedUsersById[u.id]);
    if (!hasQuery && !hasUnpinnedResults) return;

    playFlashSound();
    userSearchAbortRef.current?.abort();
    if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);
    setUserQuery('');
    setUserResults([]);
    setLoadingUsers(false);
    setHighlightUserId(prev => (prev != null && pinnedUsersById[prev] != null ? prev : null));
  }, [userQuery, userResults, pinnedUsersById]);

  const canClearUserSearch = userQuery.trim().length > 0
    || userResults.some(u => !pinnedUsersById[u.id]);

  useEffect(() => {
    initialConfigByIdRef.current = new Map(
      initialSelectedEmpresas.map(e => [e.id, e]),
    );
  }, [initialSelectedEmpresas]);

  useEffect(() => {
    const warning: Record<number, boolean> = {};
    initialSelectedEmpresas.forEach(e => {
      warning[e.id] = !!e.warning;
    });
    setWarningById(prev => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(warning);
      if (
        prevKeys.length === nextKeys.length
        && nextKeys.every(id => prev[Number(id)] === warning[Number(id)])
      ) {
        return prev;
      }
      return warning;
    });
  }, [initialWarningKey, initialSelectedEmpresas]);

  useEffect(() => {
    const modificado: Record<number, boolean> = {};
    initialSelectedEmpresas.forEach(e => {
      modificado[e.id] = !!e.modificado;
    });
    setModificadoById(prev => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(modificado);
      if (
        prevKeys.length === nextKeys.length
        && nextKeys.every(id => prev[Number(id)] === modificado[Number(id)])
      ) {
        return prev;
      }
      return modificado;
    });
  }, [initialModificadoKey, initialSelectedEmpresas]);

  useEffect(() => {
    const disenoPersonalizado: Record<number, boolean> = {};
    const empresaDisenoId: Record<number, number | null> = {};
    initialSelectedEmpresas.forEach(e => {
      disenoPersonalizado[e.id] = !!e.disenoPersonalizado;
      empresaDisenoId[e.id] = e.empresaDisenoId ?? null;
    });
    setDisenoPersonalizadoById(prev => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(disenoPersonalizado);
      if (
        prevKeys.length === nextKeys.length
        && nextKeys.every(id => prev[Number(id)] === disenoPersonalizado[Number(id)])
      ) {
        return prev;
      }
      return disenoPersonalizado;
    });
    setEmpresaDisenoIdById(prev => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(empresaDisenoId);
      if (
        prevKeys.length === nextKeys.length
        && nextKeys.every(id => prev[Number(id)] === empresaDisenoId[Number(id)])
      ) {
        return prev;
      }
      return empresaDisenoId;
    });
  }, [initialDisenoKey, initialSelectedEmpresas]);

  useEffect(() => {
    if (!allEmpresas.length) return;

    const selectionKey = `${workflowId ?? ''}:${initialSelectionMetaKey}`;
    if (initialSelectionKeyRef.current === selectionKey) return;

    const byId = new Map(allEmpresas.map(e => [e.id, e]));
    const nextSelected: EmpresaItemIndexed[] = [];
    for (const id of initialSelectedIds) {
      const item = byId.get(id);
      if (item) nextSelected.push(item);
    }
    nextSelected.sort((a, b) =>
      empresaLabel(a).localeCompare(empresaLabel(b), undefined, { numeric: true }),
    );

    const bloqueado: Record<number, boolean> = {};
    const modificado: Record<number, boolean> = {};
    const disenoPersonalizado: Record<number, boolean> = {};
    const empresaDisenoId: Record<number, number | null> = {};
    initialSelectedEmpresas.forEach(e => {
      bloqueado[e.id] = !!e.bloqueado;
      modificado[e.id] = !!e.modificado;
      disenoPersonalizado[e.id] = !!e.disenoPersonalizado;
      empresaDisenoId[e.id] = e.empresaDisenoId ?? null;
    });

    setSelectedEmpresas(nextSelected);
    setBloqueadoById(bloqueado);
    setModificadoById(modificado);
    setDisenoPersonalizadoById(disenoPersonalizado);
    setEmpresaDisenoIdById(empresaDisenoId);
    initialSelectionKeyRef.current = selectionKey;
    selectedEmpresasNotifyKeyRef.current = buildEmpresaNotifyKey(
      nextSelected,
      bloqueado,
      modificado,
      disenoPersonalizado,
      empresaDisenoId,
    );
    initialSelectionJustAppliedRef.current = true;
  }, [allEmpresas, initialSelectionMetaKey, initialSelectedIds, initialSelectedEmpresas, workflowId]);

  useEffect(() => {
    const notifyKey = buildEmpresaNotifyKey(
      selectedEmpresas,
      bloqueadoById,
      modificadoById,
      disenoPersonalizadoById,
      empresaDisenoIdById,
    );
    if (selectedEmpresasNotifyKeyRef.current === notifyKey) {
      initialSelectionJustAppliedRef.current = false;
      return;
    }
    if (initialSelectionJustAppliedRef.current) {
      initialSelectionJustAppliedRef.current = false;
      return;
    }
    selectedEmpresasNotifyKeyRef.current = notifyKey;

    const configById = initialConfigByIdRef.current;
    onSelectedEmpresasChange?.(selectedEmpresas.map(e => {
      const fromInitial = configById.get(e.id);
      const disenoPersonalizado = !!disenoPersonalizadoById[e.id];
      const empresaDisenoId = e.id in empresaDisenoIdById
        ? (empresaDisenoIdById[e.id] ?? null)
        : (fromInitial?.empresaDisenoId ?? null);
      const disenoDirty = isEmpresaDisenoDirty(disenoPersonalizado, empresaDisenoId, fromInitial);
      return markEmpresaModificado({
        id: e.id,
        bloqueado: !!bloqueadoById[e.id],
        warning: !!warningById[e.id],
        modificado: !!modificadoById[e.id] || disenoDirty,
        disenoPersonalizado,
        empresaDisenoId,
        instanciaId: fromInitial?.instanciaId ?? null,
        instanciaEstado: fromInitial?.instanciaEstado ?? null,
        workflowEmpresaId: fromInitial?.workflowEmpresaId ?? null,
        actividades: fromInitial?.actividades ?? {},
      });
    }));
  }, [
    selectedEmpresas,
    bloqueadoById,
    warningById,
    modificadoById,
    disenoPersonalizadoById,
    empresaDisenoIdById,
    onSelectedEmpresasChange,
  ]);

  useEffect(() => {
    if (hydrationNotifiedRef.current) return;
    if (initialSelectedIds.length > 0 && !allEmpresas.length) return;
    if (initialSelectionKeyRef.current === '') return;
    if (initialSelectedIds.length > 0 && selectedEmpresas.length === 0) return;
    hydrationNotifiedRef.current = true;
    onHydrationSettled?.();
  }, [allEmpresas, selectedEmpresas, initialSelectedIds, onHydrationSettled]);

  useEffect(() => {
    if (activeEmpresaId != null) setHighlightSelectedId(activeEmpresaId);
  }, [activeEmpresaId]);

  const registerSelectedEmpresaRowRef = useCallback((id: number, el: HTMLTableRowElement | null) => {
    if (el) selectedEmpresaRowRefs.current.set(id, el);
    else selectedEmpresaRowRefs.current.delete(id);
  }, []);

  const checkedRfcIdsKey = useMemo(
    () => Array.from(checkedRfcIds).sort((a, b) => a - b).join(','),
    [checkedRfcIds],
  );

  useEffect(() => {
    onCheckedRfcIdsChange?.(Array.from(checkedRfcIds).sort((a, b) => a - b));
  }, [checkedRfcIdsKey, onCheckedRfcIdsChange, checkedRfcIds]);

  useEffect(() => {
    if (!onActiveEmpresaDetailChange) return;
    if (activeEmpresaId == null) {
      onActiveEmpresaDetailChange(null);
      return;
    }
    const empresa = selectedEmpresas.find(e => e.id === activeEmpresaId) ?? null;
    onActiveEmpresaDetailChange(empresa);
  }, [activeEmpresaId, selectedEmpresas, onActiveEmpresaDetailChange]);

  useEffect(() => {
    let cancelled = false;
    setLoadingEmpresas(true);

    axios
      .get('api/empresas', {
        params: { page: 0, size: 1000, sort: 'razonSocial,asc' },
      })
      .then(res => {
        if (!cancelled) {
          setAllEmpresas(indexEmpresas(sortEmpresas(parseEmpresasResponse(res.data))));
        }
      })
      .catch(() => {
        if (!cancelled) setAllEmpresas([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingEmpresas(false);
      });

    return () => { cancelled = true; };
  }, []);

  const allEmpresasById = useMemo(
    () => new Map(allEmpresas.map(e => [e.id, e])),
    [allEmpresas],
  );

  const resolveEmpresaIndexed = useCallback(
    (empresa: EmpresaItem) => toEmpresaItemIndexed(empresa, allEmpresasById),
    [allEmpresasById],
  );

  const selectedIds = useMemo(
    () => new Set(selectedEmpresas.map(e => e.id)),
    [selectedEmpresas],
  );

  const availableEmpresas = useMemo(
    () => allEmpresas.filter(e => !selectedIds.has(e.id)),
    [allEmpresas, selectedIds],
  );

  const filteredAvailableEmpresas = useMemo(
    () => availableEmpresas.filter(e => matchesEmpresaFilter(e, debouncedFilterAvailable)),
    [availableEmpresas, debouncedFilterAvailable],
  );

  const filteredSelectedEmpresas = useMemo(
    () => selectedEmpresas
      .filter(e => !hiddenEmpresaIds.has(e.id))
      .filter(e => matchesEmpresaFilter(e, debouncedFilterSelected)),
    [selectedEmpresas, hiddenEmpresaIds, debouncedFilterSelected],
  );

  useEffect(() => {
    if (activeEmpresaId == null) return;
    const row = selectedEmpresaRowRefs.current.get(activeEmpresaId);
    if (!row) return;
    const t = window.setTimeout(() => {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
    return () => window.clearTimeout(t);
  }, [activeEmpresaId, filteredSelectedEmpresas]);

  const configByEmpresaId = useMemo(
    () => new Map(initialSelectedEmpresas.map(e => [e.id, e])),
    [initialSelectedEmpresas],
  );

  const instanciaInfoById = useMemo(() => {
    const map = new Map<number, { instanciaId: number | null; instanciaEstado: string | null }>();
    for (const e of initialSelectedEmpresas) {
      map.set(e.id, {
        instanciaId: e.instanciaId ?? null,
        instanciaEstado: e.instanciaEstado ?? null,
      });
    }
    return map;
  }, [initialSelectedEmpresas]);

  const checkedRfcEmpresaIds = useMemo(
    () => Array.from(checkedRfcIds).sort((a, b) => a - b),
    [checkedRfcIds],
  );

  const isEmpresaBloqueada = (empresaId: number) => !!bloqueadoById[empresaId];
  const isEmpresaWarning = (empresaId: number) => !!warningById[empresaId];
  const isEmpresaModificado = (empresaId: number) => !!modificadoById[empresaId];
  const isDisenoPersonalizado = (empresaId: number) => !!disenoPersonalizadoById[empresaId];
  const getEmpresaDisenoId = (empresaId: number) => empresaDisenoIdById[empresaId] ?? null;
  const getEmpresaInstanciaIcon = (empresaId: number): EmpresaInstanciaIcon | null => {
    const info = instanciaInfoById.get(empresaId);
    return resolveEmpresaInstanciaIcon(info?.instanciaId, info?.instanciaEstado);
  };

  const hasWorkflowEmpresaId = (empresaId: number): boolean => {
    const workflowEmpresaId = configByEmpresaId.get(empresaId)?.workflowEmpresaId;
    return workflowEmpresaId != null && !Number.isNaN(Number(workflowEmpresaId));
  };

  const empresaVisibilityItems = useMemo(
    () => sortEmpresas(selectedEmpresas).map(e => ({
      id: e.id,
      label: empresaPrimaryLine(e),
      bloqueado: !!bloqueadoById[e.id],
    })),
    [selectedEmpresas, bloqueadoById],
  );

  const allSelectedEmpresasBlocked = selectedEmpresas.length > 0
    && selectedEmpresas.every(e => isEmpresaBloqueada(e.id));

  const openEmpresasVisibility = useCallback(() => {
    setDraftHiddenEmpresaIds(new Set(hiddenEmpresaIds));
    const locked = new Set<number>();
    selectedEmpresas.forEach(e => {
      if (bloqueadoById[e.id]) locked.add(e.id);
    });
    setDraftLockedEmpresaIds(locked);
    setEmpresasVisibilityOpen(true);
  }, [hiddenEmpresaIds, selectedEmpresas, bloqueadoById]);

  const toggleDraftEmpresaVisibility = useCallback((empresaId: number) => {
    let hiding = false;
    setDraftHiddenEmpresaIds(prev => {
      hiding = !prev.has(empresaId);
      const next = new Set(prev);
      if (hiding) next.add(empresaId);
      else next.delete(empresaId);
      return next;
    });
    if (hiding) {
      setDraftLockedEmpresaIds(prev => {
        const next = new Set(prev);
        next.add(empresaId);
        return next;
      });
    }
  }, []);

  const toggleDraftEmpresaLocked = useCallback((empresaId: number) => {
    setDraftLockedEmpresaIds(prev => {
      const next = new Set(prev);
      if (next.has(empresaId)) next.delete(empresaId);
      else next.add(empresaId);
      return next;
    });
  }, []);

  const showAllDraftEmpresas = useCallback(() => {
    setDraftHiddenEmpresaIds(new Set());
  }, []);

  const hideAllDraftEmpresas = useCallback(() => {
    const allIds = empresaVisibilityItems.map(e => e.id);
    setDraftHiddenEmpresaIds(new Set(allIds));
    setDraftLockedEmpresaIds(new Set(allIds));
  }, [empresaVisibilityItems]);

  const lockAllDraftEmpresas = useCallback(() => {
    setDraftLockedEmpresaIds(new Set(empresaVisibilityItems.map(e => e.id)));
  }, [empresaVisibilityItems]);

  const unlockAllDraftEmpresas = useCallback(() => {
    setDraftLockedEmpresaIds(new Set());
  }, []);

  const pasteDraftEmpresasVisibilityFromClipboard = useCallback(async () => {
    const snapshot = await readClipboardPasteSnapshot();
    const rfcs = resolveEmpresasRfcsFromClipboard(snapshot);
    if (!rfcs?.length) {
      showToast(
        'error',
        'Formato no reconocido. Pegue una grilla de Excel (1–3 columnas) o datos Profase de empresas.',
      );
      return;
    }

    const rfcSet = new Set(rfcs);
    const visibleIds = new Set<number>();
    selectedEmpresas.forEach(e => {
      const rfc = e.rfc?.trim();
      if (rfc && rfcSet.has(normalizeEmpresaRfc(rfc))) visibleIds.add(e.id);
    });

    if (!visibleIds.size) {
      showToast('info', 'Ninguna empresa seleccionada coincide con los RFC pegados.');
      return;
    }

    const nextHidden = new Set<number>();
    const nextLocked = new Set<number>();
    selectedEmpresas.forEach(e => {
      if (visibleIds.has(e.id)) return;
      nextHidden.add(e.id);
      nextLocked.add(e.id);
    });

    setDraftHiddenEmpresaIds(nextHidden);
    setDraftLockedEmpresaIds(nextLocked);
    showToast(
      'success',
      `Se muestran ${visibleIds.size} empresa${visibleIds.size === 1 ? '' : 's'} `
      + `(${selectedEmpresas.length - visibleIds.size} oculta${selectedEmpresas.length - visibleIds.size === 1 ? '' : 's'} y bloqueada${selectedEmpresas.length - visibleIds.size === 1 ? '' : 's'}).`,
    );
  }, [selectedEmpresas, showToast]);

  const syncEmpresaHiddenState = useCallback((nextHidden: Set<number>) => {
    setHiddenEmpresaIds(nextHidden);
    onHiddenEmpresasChange?.(nextHidden);
    if (activeEmpresaId != null && nextHidden.has(activeEmpresaId)) {
      const firstVisible = selectedEmpresas.find(e => !nextHidden.has(e.id));
      onActiveEmpresaChange?.(firstVisible?.id ?? null);
    }
    setCheckedRfcIds(prev => {
      const next = new Set<number>();
      prev.forEach(id => {
        if (!nextHidden.has(id)) next.add(id);
      });
      return next;
    });
  }, [activeEmpresaId, onActiveEmpresaChange, onHiddenEmpresasChange, selectedEmpresas]);

  const hideEmpresaFromGrid = useCallback((empresaId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (hiddenEmpresaIds.has(empresaId)) return;
    const nextHidden = new Set(hiddenEmpresaIds);
    nextHidden.add(empresaId);
    setBloqueadoById(prev => ({ ...prev, [empresaId]: true }));
    setModificadoById(prev => ({ ...prev, [empresaId]: true }));
    playLockDoorSound();
    syncEmpresaHiddenState(nextHidden);
  }, [hiddenEmpresaIds, syncEmpresaHiddenState]);

  const applyEmpresasVisibility = useCallback(() => {
    const prevLocked = new Set<number>();
    selectedEmpresas.forEach(e => {
      if (bloqueadoById[e.id]) prevLocked.add(e.id);
    });
    const nextLocked = draftLockedEmpresaIds;
    const anyNewlyLocked = Array.from(nextLocked).some(id => !prevLocked.has(id));
    const anyNewlyUnlocked = Array.from(prevLocked).some(id => !nextLocked.has(id));
    if (anyNewlyLocked) playLockDoorSound();
    else if (anyNewlyUnlocked) playUnlockDoorSound();

    const nextBloqueado: Record<number, boolean> = { ...bloqueadoById };
    const nextModificado: Record<number, boolean> = { ...modificadoById };
    selectedEmpresas.forEach(e => {
      const nextLocked = draftLockedEmpresaIds.has(e.id);
      if (!!bloqueadoById[e.id] !== nextLocked) nextModificado[e.id] = true;
      nextBloqueado[e.id] = nextLocked;
    });
    setBloqueadoById(nextBloqueado);
    setModificadoById(nextModificado);

    syncEmpresaHiddenState(new Set(draftHiddenEmpresaIds));
    setEmpresasVisibilityOpen(false);
  }, [
    bloqueadoById,
    modificadoById,
    selectedEmpresas,
    draftLockedEmpresaIds,
    draftHiddenEmpresaIds,
    syncEmpresaHiddenState,
  ]);

  const closeEmpresasVisibility = useCallback(() => {
    setEmpresasVisibilityOpen(false);
    setDraftHiddenEmpresaIds(new Set(hiddenEmpresaIds));
    const locked = new Set<number>();
    selectedEmpresas.forEach(e => {
      if (bloqueadoById[e.id]) locked.add(e.id);
    });
    setDraftLockedEmpresaIds(locked);
  }, [hiddenEmpresaIds, selectedEmpresas, bloqueadoById]);

  useEffect(() => {
    if (
      highlightAvailableId != null
      && !filteredAvailableEmpresas.some(e => e.id === highlightAvailableId)
    ) {
      setHighlightAvailableId(null);
    }
  }, [filteredAvailableEmpresas, highlightAvailableId]);

  useEffect(() => {
    if (
      highlightSelectedId != null
      && !filteredSelectedEmpresas.some(e => e.id === highlightSelectedId)
    ) {
      setHighlightSelectedId(null);
    }
  }, [filteredSelectedEmpresas, highlightSelectedId]);

  const moveToSelected = useCallback((
    items: EmpresaItemIndexed[],
    options?: { skipSound?: boolean },
  ) => {
    if (!items.length) return;
    const configById = initialConfigByIdRef.current;
    startTransition(() => {
      setSelectedEmpresas(prev => {
        const ids = new Set(prev.map(e => e.id));
        const next = [...prev];
        items.forEach(e => {
          if (!ids.has(e.id)) {
            ids.add(e.id);
            next.push(e);
          }
        });
        return sortEmpresas(next);
      });
      setBloqueadoById(prev => {
        const next = { ...prev };
        items.forEach(e => {
          if (e.id in next) return;
          const fromInitial = configById.get(e.id);
          next[e.id] = fromInitial ? !!fromInitial.bloqueado : false;
        });
        return next;
      });
      setWarningById(prev => {
        const next = { ...prev };
        items.forEach(e => {
          if (e.id in next) return;
          const fromInitial = configById.get(e.id);
          next[e.id] = fromInitial ? !!fromInitial.warning : false;
        });
        return next;
      });
      setModificadoById(prev => {
        const next = { ...prev };
        items.forEach(e => {
          if (e.id in next) return;
          const fromInitial = configById.get(e.id);
          next[e.id] = fromInitial ? !!fromInitial.modificado : false;
        });
        return next;
      });
      setDisenoPersonalizadoById(prev => {
        const next = { ...prev };
        items.forEach(e => {
          if (e.id in next) return;
          const fromInitial = configById.get(e.id);
          next[e.id] = fromInitial ? !!fromInitial.disenoPersonalizado : false;
        });
        return next;
      });
      setEmpresaDisenoIdById(prev => {
        const next = { ...prev };
        items.forEach(e => {
          if (e.id in next) return;
          const fromInitial = configById.get(e.id);
          next[e.id] = fromInitial?.empresaDisenoId ?? null;
        });
        return next;
      });
    });
    setHighlightAvailableId(null);
    if (!options?.skipSound) {
      if (items.length === 1) {
        onActiveEmpresaChange?.(items[0].id);
        setHighlightSelectedId(items[0].id);
        playSelectEmpresaCardSound();
      } else if (items.length > 1) {
        playShuffleEmpresaCardSound();
      }
    }
  }, [onActiveEmpresaChange]);

  const moveToAvailable = useCallback((
    items: EmpresaItemIndexed[],
    options?: { bulk?: boolean },
  ) => {
    const movable = items.filter(e => !bloqueadoById[e.id]);
    if (!movable.length) return;
    if (options?.bulk || movable.length > 1) playShuffleEmpresaCardSound();
    else playSelectEmpresaCardSound();
    const removeIds = new Set(movable.map(e => e.id));
    startTransition(() => {
      setSelectedEmpresas(prev => prev.filter(e => !removeIds.has(e.id)));
      setBloqueadoById(prev => {
        const next = { ...prev };
        removeIds.forEach(rid => { delete next[rid]; });
        return next;
      });
      setWarningById(prev => {
        const next = { ...prev };
        removeIds.forEach(rid => { delete next[rid]; });
        return next;
      });
      setModificadoById(prev => {
        const next = { ...prev };
        removeIds.forEach(rid => { delete next[rid]; });
        return next;
      });
      setDisenoPersonalizadoById(prev => {
        const next = { ...prev };
        removeIds.forEach(rid => { delete next[rid]; });
        return next;
      });
      setEmpresaDisenoIdById(prev => {
        const next = { ...prev };
        removeIds.forEach(rid => { delete next[rid]; });
        return next;
      });
    });
    setHighlightSelectedId(null);
    setCheckedRfcIds(prev => {
      const next = new Set(prev);
      removeIds.forEach(rid => next.delete(rid));
      return next;
    });
  }, [bloqueadoById]);

  const toggleEmpresaBloqueado = useCallback((empresaId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const locking = !bloqueadoById[empresaId];
    if (locking) playLockDoorSound();
    else playUnlockDoorSound();
    setBloqueadoById(prev => ({
      ...prev,
      [empresaId]: locking,
    }));
    setModificadoById(prev => ({
      ...prev,
      [empresaId]: true,
    }));
  }, [bloqueadoById]);

  const toggleDisenoPersonalizado = useCallback((empresaId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!empresaActividadesEditing) {
      showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
      return;
    }
    const workflowEmpresaId = configByEmpresaId.get(empresaId)?.workflowEmpresaId;
    if (workflowEmpresaId == null || Number.isNaN(Number(workflowEmpresaId))) return;
    if (bloqueadoById[empresaId]) return;

    playSelectButtonSound();
    const activating = !disenoPersonalizadoById[empresaId];
    setDisenoPersonalizadoById(prev => ({
      ...prev,
      [empresaId]: activating,
    }));
    setEmpresaDisenoIdById(prev => ({
      ...prev,
      [empresaId]: activating
        ? (prev[empresaId] ?? initialConfigByIdRef.current.get(empresaId)?.empresaDisenoId ?? null)
        : null,
    }));
    setModificadoById(prev => ({
      ...prev,
      [empresaId]: true,
    }));
  }, [bloqueadoById, configByEmpresaId, disenoPersonalizadoById, empresaActividadesEditing, showToast]);

  const toggleRfcChecked = useCallback((empresaId: number, checked: boolean) => {
    setCheckedRfcIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(empresaId);
      else next.delete(empresaId);
      return next;
    });
  }, []);

  const toggleAllRfcVisible = useCallback((items: EmpresaItemIndexed[], checked: boolean) => {
    const ids = items.map(e => e.id);
    if (!ids.length) return;
    playSelectButtonSound();
    setCheckedRfcIds(prev => {
      const next = new Set(prev);
      ids.forEach(rid => {
        if (checked) next.add(rid);
        else next.delete(rid);
      });
      return next;
    });
  }, []);

  const confirmSeleccionarTodo = () => {
    const visibleEmpresas = availableEmpresas.filter(e =>
      matchesEmpresaFilter(e, debouncedFilterAvailable),
    );
    if (!visibleEmpresas.length) return;

    const isFiltered = debouncedFilterAvailable.trim().length > 0;
    const n = visibleEmpresas.length;
    const label = n === 1 ? 'empresa visible' : 'empresas visibles';

    ask(
      <>
        ¿Desea seleccionar {isFiltered ? `las ${n} ${label}` : `las ${n} empresas`}
        {' '}para esta plantilla?
      </>,
      () => {
        const toSelect = availableEmpresas.filter(e =>
          matchesEmpresaFilter(e, debouncedFilterAvailable),
        );
        moveToSelected(toSelect);
        dismiss();
      },
      { title: 'Seleccionar todo', confirmLabel: 'Seleccionar', variant: 'info' },
    );
  };

  const confirmDeseleccionarTodo = () => {
    if (!canDeseleccionarTodo) return;

    const movable = selectedEmpresas.filter(e => !isEmpresaBloqueada(e.id));
    if (!movable.length) return;

    const bloqueadasCount = selectedEmpresas.length - movable.length;
    const mensaje = bloqueadasCount > 0
      ? (
        <>
          ¿Desea deseleccionar las empresas no bloqueadas ({movable.length})?
          {' '}Las {bloqueadasCount} empresa{bloqueadasCount === 1 ? '' : 's'} con candado activo permanecerán seleccionadas.
        </>
      )
      : (
        <>¿Desea deseleccionar todas las empresas de esta plantilla ({movable.length})?</>
      );

    ask(
      mensaje,
      () => {
        moveToAvailable(movable, { bulk: true });
        dismiss();
      },
      { title: 'Deseleccionar todo', confirmLabel: 'Deseleccionar', variant: 'warning' },
    );
  };

  const handleSeleccionar = () => {
    const item = filteredAvailableEmpresas.find(e => e.id === highlightAvailableId);
    if (item) moveToSelected([item]);
  };

  const handleDeseleccionar = () => {
    const item = filteredSelectedEmpresas.find(e => e.id === highlightSelectedId);
    if (item && !isEmpresaBloqueada(item.id)) moveToAvailable([item]);
  };

  const selectAvailable = useCallback((empresaId: number) => {
    setHighlightAvailableId(prev => {
      const next = prev === empresaId ? null : empresaId;
      if (next != null) playSelectButtonSound();
      return next;
    });
    setHighlightSelectedId(null);
  }, []);

  const selectSelected = useCallback((empresaId: number) => {
    setHighlightSelectedId(prev => {
      const next = prev === empresaId ? null : empresaId;
      if (next != null) playSelectButtonSound();
      return next;
    });
    setHighlightAvailableId(null);
    onActiveEmpresaChange?.(empresaId);
  }, [onActiveEmpresaChange]);

  const canSeleccionar =
    highlightAvailableId != null
    && filteredAvailableEmpresas.some(e => e.id === highlightAvailableId);

  const canDeseleccionar =
    highlightSelectedId != null
    && filteredSelectedEmpresas.some(e => e.id === highlightSelectedId)
    && !isEmpresaBloqueada(highlightSelectedId);

  const canDeseleccionarTodo = selectedEmpresas.some(e => !isEmpresaBloqueada(e.id));

  const canPasteActividades = empresaActividadesEditing
    && activeEmpresaId != null
    && selectedEmpresas.some(
      e => e.id === activeEmpresaId && !isEmpresaBloqueada(e.id),
    )
    && !pasteClipboardInvalidLocked;

  const canPegar = !loadingEmpresas;

  const handlePegarClick = useCallback(async () => {
    const snapshot = await readClipboardPasteSnapshot();
    const resolved = resolveClipboardPaste(snapshot);

    if (resolved?.kind === 'empresas-rfc') {
      const rfcSet = new Set(resolved.rfcs);
      const toMove = availableEmpresas.filter(e => {
        const rfc = e.rfc?.trim();
        return rfc && rfcSet.has(normalizeEmpresaRfc(rfc));
      });

      if (!toMove.length) {
        showToast('info', 'No se encontraron empresas con esos RFC en Empresas a Seleccionar.');
        return;
      }

      moveToSelected(toMove, { skipSound: true });
      playUsuarioApplyAllSound();
      showToast(
        'success',
        `Se agregaron ${toMove.length} Empresa${toMove.length === 1 ? '' : 's'}`,
      );
      return;
    }

    if (resolved?.kind === 'actividades') {
      if (!empresaActividadesEditing) {
        showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
        return;
      }
      if (!canPasteActividades) {
        showToast('info', 'Seleccione una empresa desbloqueada en la lista de empresas seleccionadas.');
        return;
      }
      onPasteEmpresaActividades?.(resolved.text);
      return;
    }

    showToast('error', 'Formato no reconocido en el portapapeles.');
  }, [
    availableEmpresas,
    canPasteActividades,
    empresaActividadesEditing,
    moveToSelected,
    onPasteEmpresaActividades,
    showToast,
  ]);

  const canCopyAllEmpresas = !loadingEmpresas && allEmpresas.length > 0;
  const canCopySelectedEmpresas = selectedEmpresas.length > 0;

  const copyEmpresasToClipboard = useCallback(async (empresas: EmpresaItem[]) => {
    if (empresas.length === 0) return;
    const ok = await copyEmpresasListToClipboardRobust(empresas);
    if (ok) {
      showToast('success', `${empresas.length} empresa(s) copiada(s) al portapapeles.`);
    } else {
      showToast('error', 'No se pudo copiar al portapapeles.');
    }
  }, [showToast]);

  const copyAllEmpresasList = useCallback(
    () => copyEmpresasToClipboard(allEmpresas),
    [allEmpresas, copyEmpresasToClipboard],
  );

  const copySelectedEmpresasList = useCallback(
    () => copyEmpresasToClipboard(selectedEmpresas),
    [selectedEmpresas, copyEmpresasToClipboard],
  );

  const actionBtnStyle = (enabled: boolean): React.CSSProperties => ({
    ...empresaS.actionBtn,
    ...(enabled ? {} : empresaS.actionBtnDisabled),
  });

  const clearEmpresaDragOver = useCallback(() => setEmpresaDragOverPanel(null), []);

  const handleEmpresaListDragOver = (e: React.DragEvent, panel: EmpresaPanelSide) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setEmpresaDragOverPanel(panel);
  };

  const handleEmpresaListDragLeave = (e: React.DragEvent, panel: EmpresaPanelSide) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setEmpresaDragOverPanel(prev => (prev === panel ? null : prev));
  };

  const handleEmpresaListDrop = (e: React.DragEvent, panel: EmpresaPanelSide) => {
    e.preventDefault();
    setEmpresaDragOverPanel(null);
    try {
      const raw = e.dataTransfer.getData(EMPRESA_DRAG_TYPE);
      if (!raw) return;
      const payload = JSON.parse(raw) as { side?: EmpresaPanelSide; empresa?: EmpresaItem };
      const empresa = payload.empresa;
      if (!empresa?.id || !payload.side) return;

      const indexed = resolveEmpresaIndexed(empresa);
      if (panel === 'selected' && payload.side === 'available') {
        moveToSelected([indexed]);
      } else if (panel === 'available' && payload.side === 'selected' && !isEmpresaBloqueada(empresa.id)) {
        moveToAvailable([indexed]);
      }
    } catch {
      /* ignore invalid payload */
    }
  };

  const renderList = (
    title: string,
    panelSide: EmpresaPanelSide,
    items: EmpresaItemIndexed[],
    highlightId: number | null,
    onHighlight: (id: number) => void,
    emptyText: string,
    filter: string,
    onFilterChange: (value: string) => void,
    loading?: boolean,
    withRfcCheckbox?: boolean,
    onCopyList?: () => void,
    canCopyList?: boolean,
  ) => {
    const visibleRfcIds = withRfcCheckbox
      ? items.filter(e => !hiddenEmpresaIds.has(e.id)).map(e => e.id)
      : [];
    const allRfcVisibleChecked =
      visibleRfcIds.length > 0 && visibleRfcIds.every(rid => checkedRfcIds.has(rid));
    const anyRfcVisibleChecked = visibleRfcIds.some(rid => checkedRfcIds.has(rid));
    const canSelectAllRfcVisible = visibleRfcIds.length > 0 && !allRfcVisibleChecked;
    const canDeselectAllRfcVisible = anyRfcVisibleChecked;
    const canToggleAllEmpresasLock = selectedEmpresas.length > 0;
    const hasHiddenEmpresas = hiddenEmpresaIds.size > 0;

    const rfcHeaderBtnStyle = (enabled: boolean): React.CSSProperties => ({
      ...empresaS.rfcHeaderBtn,
      ...(enabled ? {} : empresaS.actionBtnDisabled),
    });

    const copyListBtn = onCopyList ? (
      <button
        type="button"
        style={rfcHeaderBtnStyle(!!canCopyList)}
        disabled={!canCopyList}
        onClick={() => {
          if (!canCopyList) return;
          playCopyCutSound();
          void onCopyList();
        }}
        title="Copiar alias, RFC y razón social (separados por tabulador)"
        aria-label="Copiar empresas al portapapeles"
      >
        Copiar
      </button>
    ) : null;

    return (
    <div style={empresaS.panel}>
      {withRfcCheckbox ? (
        <div style={empresaS.panelTitleRow}>
          {copyListBtn}
          {onPasteEmpresaActividades ? (
            <button
              type="button"
              style={rfcHeaderBtnStyle(canPegar)}
              disabled={!canPegar}
              onClick={() => { void handlePegarClick(); }}
              title={
                pasteClipboardInvalidLocked
                  ? 'Formato inválido en el portapapeles. Copie actividades válidas u otro contenido para habilitar pegar.'
                  : 'Pegar empresas por RFC o actividades en la empresa seleccionada'
              }
              aria-label="Pegar"
            >
              Pegar
            </button>
          ) : null}
          <button
            type="button"
            style={rfcHeaderBtnStyle(canSelectAllRfcVisible)}
            disabled={!canSelectAllRfcVisible}
            onClick={() => toggleAllRfcVisible(items, true)}
          >
            Seleccionar Todos
          </button>
          <button
            type="button"
            style={rfcHeaderBtnStyle(canDeselectAllRfcVisible)}
            disabled={!canDeselectAllRfcVisible}
            onClick={() => toggleAllRfcVisible(items, false)}
          >
            Deseleccionar Todos
          </button>
        </div>
      ) : (
        <div style={empresaS.panelTitleRow}>
          <span style={empresaS.panelTitle}>{title}</span>
          {copyListBtn}
        </div>
      )}
      <div
        style={{
          ...empresaS.listBox,
          ...(empresaDragOverPanel === panelSide ? empresaS.listBoxDragOver : {}),
        }}
        onDragOver={e => handleEmpresaListDragOver(e, panelSide)}
        onDragLeave={e => handleEmpresaListDragLeave(e, panelSide)}
        onDrop={e => handleEmpresaListDrop(e, panelSide)}
      >
        <div style={empresaS.empresaGridToolbar}>
          <input
            type="text"
            value={filter}
            onChange={e => onFilterChange(e.target.value)}
            placeholder="Buscar RFC, razón social, alias…"
            style={empresaS.empresaGridSearch}
            aria-label={
              withRfcCheckbox
                ? 'Buscar y filtrar empresas seleccionadas'
                : 'Buscar y filtrar empresas disponibles'
            }
          />
          {withRfcCheckbox ? (
            <>
              <button
                type="button"
                style={{
                  ...empresaS.rfcHeaderVisibilityBtn,
                  ...(hasHiddenEmpresas ? empresaS.rfcHeaderVisibilityBtnActive : {}),
                  ...(!canToggleAllEmpresasLock ? empresaS.rfcHeaderLockBtnDisabled : {}),
                }}
                disabled={!canToggleAllEmpresasLock}
                onClick={openEmpresasVisibility}
                title="Visibilidad de empresas (modal)"
                aria-label="Visibilidad de empresas"
              >
                <FaEye size={14} />
              </button>
              <button
                type="button"
                style={{
                  ...empresaS.rfcHeaderLockBtn,
                  ...(allSelectedEmpresasBlocked ? empresaS.lockBtnActive : empresaS.lockBtnInactive),
                  ...(!canToggleAllEmpresasLock ? empresaS.rfcHeaderLockBtnDisabled : {}),
                }}
                disabled={!canToggleAllEmpresasLock}
                onClick={openEmpresasVisibility}
                title="Visibilidad y bloqueo de empresas (modal)"
                aria-label="Visibilidad y bloqueo de empresas"
                aria-pressed={allSelectedEmpresasBlocked}
              >
                <FaLock size={14} />
              </button>
            </>
          ) : null}
        </div>
        {withRfcCheckbox && hiddenEmpresaIds.size > 0 ? (
          <div style={empresaS.hiddenEmpresasInfo} role="status">
            <FaInfoCircle size={14} style={empresaS.hiddenEmpresasInfoIcon} aria-hidden />
            <span>
              Hay {hiddenEmpresaIds.size} empresa{hiddenEmpresaIds.size === 1 ? '' : 's'}
              {' '}que se encuentran ocultas y bloqueadas
            </span>
          </div>
        ) : null}
        {loading ? (
          <div style={empresaS.empty}>Cargando empresas…</div>
        ) : withRfcCheckbox ? (
          <div style={empresaS.empresaGridScroll}>
            <table style={empresaS.empresaGridTable}>
              <thead>
                <tr>
                  <th style={{ ...empresaS.empresaGridTh, width: 36 }} aria-label="RFC en grilla" />
                  <th style={empresaS.empresaGridTh}>Empresa</th>
                  <th style={{ ...empresaS.empresaGridTh, width: 52, textAlign: 'center' }}>Visible</th>
                  <th style={{ ...empresaS.empresaGridTh, width: 52, textAlign: 'center' }}>Bloqueo</th>
                  <th style={{ ...empresaS.empresaGridTh, width: 92 }} />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={empresaS.empresaGridEmpty}>{emptyText}</td>
                  </tr>
                ) : items.map(item => {
                  const bloqueada = isEmpresaBloqueada(item.id);
                  const empresaWarning = isEmpresaWarning(item.id);
                  const empresaModificado = isEmpresaModificado(item.id);
                  const instanciaIcon = getEmpresaInstanciaIcon(item.id);
                  const missingWorkflowEmpresaId = !hasWorkflowEmpresaId(item.id);
                  const disenoPersonalizado = isDisenoPersonalizado(item.id);
                  const empresaDisenoId = getEmpresaDisenoId(item.id);
                  const disenoAccionDisabled = missingWorkflowEmpresaId || bloqueada || !empresaActividadesEditing;
                  const rowActive = highlightId === item.id;
                  const gridActive = activeEmpresaId === item.id;
                  return (
                    <tr
                      key={item.id}
                      ref={withRfcCheckbox ? el => registerSelectedEmpresaRowRef(item.id, el) : undefined}
                      style={{
                        ...empresaS.empresaGridTr,
                        ...(rowActive ? empresaS.empresaGridTrActive : {}),
                        ...(gridActive ? empresaS.empresaGridTrGridActive : {}),
                      }}
                    >
                      <td style={empresaS.empresaGridTd}>
                        <input
                          type="checkbox"
                          checked={checkedRfcIds.has(item.id)}
                          style={empresaS.listCheckboxItem}
                          onClick={e => e.stopPropagation()}
                          onChange={e => toggleRfcChecked(item.id, e.target.checked)}
                          aria-label={`RFC seleccionado ${item.rfc ?? item.id}`}
                        />
                      </td>
                      <td style={empresaS.empresaGridTd}>
                        <button
                          type="button"
                          draggable={!loadingEmpresas && !bloqueada}
                          title={bloqueada ? 'Empresa bloqueada' : 'Arrastrar a la otra lista'}
                          onDragStart={e => handleEmpresaDragStart(e, 'selected', item)}
                          onDragEnd={clearEmpresaDragOver}
                          style={{
                            ...empresaS.empresaGridEmpresaBtn,
                            ...(bloqueada ? {} : empresaS.listItemDraggable),
                          }}
                          onClick={() => onHighlight(item.id)}
                        >
                          <EmpresaListLabel
                            empresa={item}
                            active={rowActive || gridActive}
                            modificado={empresaModificado}
                          />
                        </button>
                      </td>
                      <td style={{ ...empresaS.empresaGridTd, textAlign: 'center' }}>
                        <button
                          type="button"
                          style={{
                            ...empresaS.empresaGridIconBtn,
                            ...empresaS.empresaGridEyeVisible,
                          }}
                          title="Ocultar empresa (volver a mostrar desde el modal de visibilidad)"
                          aria-label={`Ocultar ${item.rfc ?? item.id}`}
                          aria-pressed
                          onClick={e => hideEmpresaFromGrid(item.id, e)}
                        >
                          <FaEye size={14} />
                        </button>
                      </td>
                      <td style={{ ...empresaS.empresaGridTd, textAlign: 'center' }}>
                        <button
                          type="button"
                          style={{
                            ...empresaS.empresaGridIconBtn,
                            ...(bloqueada ? empresaS.lockBtnActive : empresaS.lockBtnInactive),
                          }}
                          onClick={e => toggleEmpresaBloqueado(item.id, e)}
                          title={bloqueada ? 'Desbloquear empresa' : 'Bloquear empresa'}
                          aria-label={bloqueada ? 'Desbloquear empresa' : 'Bloquear empresa'}
                          aria-pressed={bloqueada}
                        >
                          <FaLock size={14} />
                        </button>
                      </td>
                      <td style={empresaS.empresaGridTd}>
                        <div style={empresaS.empresaGridActions}>
                          {onCopyEmpresaActividades ? (
                            <button
                              type="button"
                              style={empresaS.copyBtn}
                              onClick={e => {
                                e.stopPropagation();
                                playCopyCutSound();
                                onCopyEmpresaActividades(item.id);
                              }}
                              title="Copiar actividades de la empresa (JSON)"
                              aria-label={`Copiar actividades de ${item.rfc ?? item.id}`}
                            >
                              <FaCopy size={13} />
                            </button>
                          ) : null}
                          <span
                            style={{
                              ...empresaS.warningIcon,
                              ...(empresaWarning ? empresaS.warningIconActive : empresaS.warningIconDisabled),
                            }}
                            title={
                              empresaWarning
                                ? 'Advertencia en la configuración de la empresa'
                                : 'Sin advertencias'
                            }
                            role="img"
                            aria-label={empresaWarning ? 'Advertencia' : 'Sin advertencias'}
                          >
                            <FaExclamationTriangle size={14} />
                          </span>
                          <button
                            type="button"
                            style={{
                              ...empresaS.empresaGridIconBtn,
                              ...(disenoPersonalizado
                                ? empresaS.disenoBtnActive
                                : empresaS.disenoBtnInactive),
                              ...(disenoAccionDisabled ? empresaS.instanciaIconDisabled : {}),
                            }}
                            disabled={disenoAccionDisabled}
                            onClick={e => toggleDisenoPersonalizado(item.id, e)}
                            title={
                              disenoAccionDisabled
                                ? !empresaActividadesEditing
                                  ? EMPRESA_ACTIVIDADES_EDIT_TOAST
                                  : bloqueada
                                    ? 'Empresa bloqueada'
                                    : 'Empresa sin workflowEmpresaId configurado'
                                : disenoPersonalizado
                                  ? `Diseño personalizado activo${empresaDisenoId ? ` (#${empresaDisenoId})` : ''} — clic para desactivar`
                                  : 'Activar diseño personalizado'
                            }
                            aria-label={
                              disenoPersonalizado
                                ? 'Desactivar diseño personalizado'
                                : 'Activar diseño personalizado'
                            }
                            aria-pressed={disenoPersonalizado}
                          >
                            <FaDraftingCompass size={13} />
                          </button>
                          {instanciaIcon === 'firefighter' ? (
                            <span
                              style={{
                                ...empresaS.warningIcon,
                                ...empresaS.instanciaFirefighterIcon,
                              }}
                              title="Pendiente de corregir"
                              role="img"
                              aria-label="Pendiente de corregir"
                            >
                              🚒
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <VirtualEmpresaList
            items={items}
            viewportHeight={EMPRESA_LIST_CONTENT_HEIGHT}
            empty={emptyText}
            emptyStyle={empresaS.empty}
            renderItem={(item) => (
              <AvailableEmpresaRow
                key={item.id}
                item={item}
                active={highlightId === item.id}
                loadingEmpresas={!!loadingEmpresas}
                onHighlight={onHighlight}
                onDragEnd={clearEmpresaDragOver}
              />
            )}
          />
        )}
      </div>
      <div style={empresaS.listCountFooter} role="status">
        <FaInfoCircle size={12} style={empresaS.listCountFooterIcon} aria-hidden />
        <span>
          {items.length} {items.length === 1 ? 'Empresa' : 'Empresas'}{' '}
          {panelSide === 'selected'
            ? (items.length === 1 ? 'seleccionada' : 'seleccionadas')
            : 'para seleccionar'}
        </span>
      </div>
    </div>
    );
  };

  return (
    <div style={empresaS.section}>
      <Confirm config={confirmConfig} />

      <WorkflowEmpresasVisibilityModal
        open={empresasVisibilityOpen}
        empresas={empresaVisibilityItems}
        draftHiddenIds={draftHiddenEmpresaIds}
        draftLockedIds={draftLockedEmpresaIds}
        onToggleVisible={toggleDraftEmpresaVisibility}
        onToggleLocked={toggleDraftEmpresaLocked}
        onShowAll={showAllDraftEmpresas}
        onHideAll={hideAllDraftEmpresas}
        onLockAll={lockAllDraftEmpresas}
        onUnlockAll={unlockAllDraftEmpresas}
        onPaste={pasteDraftEmpresasVisibilityFromClipboard}
        onApply={applyEmpresasVisibility}
        onClose={closeEmpresasVisibility}
      />

      <div style={{
        ...empresaS.row,
        ...(empresaActividadesEditing ? {} : empresaS.rowWithoutUsers),
      }}>
      {renderList(
        'Empresas a Seleccionar',
        'available',
        filteredAvailableEmpresas,
        highlightAvailableId,
        selectAvailable,
        filterAvailable.trim() ? 'Sin coincidencias' : 'No hay empresas disponibles',
        filterAvailable,
        setFilterAvailable,
        loadingEmpresas,
        false,
        copyAllEmpresasList,
        canCopyAllEmpresas,
      )}

      <div style={empresaS.actions}>
        <button
          type="button"
          style={actionBtnStyle(canSeleccionar && !loadingEmpresas)}
          onClick={handleSeleccionar}
          disabled={!canSeleccionar || loadingEmpresas}
        >
          Seleccionar
        </button>
        <button
          type="button"
          style={actionBtnStyle(canDeseleccionar)}
          onClick={handleDeseleccionar}
          disabled={!canDeseleccionar}
        >
          Deseleccionar
        </button>
        <button
          type="button"
          style={actionBtnStyle(!loadingEmpresas && filteredAvailableEmpresas.length > 0)}
          onClick={confirmSeleccionarTodo}
          disabled={!filteredAvailableEmpresas.length || loadingEmpresas}
        >
          Seleccionar Todo
        </button>
        <button
          type="button"
          style={actionBtnStyle(canDeseleccionarTodo)}
          onClick={confirmDeseleccionarTodo}
          disabled={!canDeseleccionarTodo}
        >
          Deseleccionar Todo
        </button>
      </div>

      {renderList(
        '',
        'selected',
        filteredSelectedEmpresas,
        highlightSelectedId,
        selectSelected,
        filterSelected.trim()
          ? 'Sin coincidencias'
          : 'Sin empresas seleccionadas',
        filterSelected,
        setFilterSelected,
        false,
        true,
        copySelectedEmpresasList,
        canCopySelectedEmpresas,
      )}

      {empresaActividadesEditing ? (
      <div style={{ ...empresaS.panel, ...empresaS.usersPanel }}>
        <div style={empresaS.panelTitle}>Usuarios</div>
        <div style={empresaS.userSearchRow}>
          <input
            type="text"
            value={userQuery}
            onChange={e => setUserQuery(e.target.value)}
            placeholder="Buscar usuario…"
            style={empresaS.searchInputInline}
            aria-label="Buscar usuarios"
            autoComplete="off"
          />
          <button
            type="button"
            style={{
              ...empresaS.userSearchClearBtn,
              ...(canClearUserSearch ? {} : empresaS.userSearchClearBtnDisabled),
            }}
            disabled={!canClearUserSearch}
            onClick={clearUserSearch}
            title="Limpiar búsqueda"
            aria-label="Limpiar búsqueda de usuarios"
          >
            <FaTimes size={14} />
          </button>
        </div>
        <div ref={usersListScrollRef} style={{ ...empresaS.listBox, ...empresaS.usersListBox }}>
          {loadingUsers ? (
            <div style={empresaS.empty}>
              <FaSpinner style={{ animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
              Buscando…
            </div>
          ) : !userQuery.trim() && displayedUsers.length === 0 ? (
            <div style={empresaS.empty}>Escriba para buscar usuarios</div>
          ) : displayedUsers.length === 0 ? (
            <div style={empresaS.empty}>Sin resultados</div>
          ) : (
            displayedUsers.map((user, index) => {
              const active = highlightUserId === user.id;
              const pinned = !!pinnedUsersById[user.id];
              return (
                <div
                  key={user.id}
                  style={{
                    ...empresaS.userListItemRow,
                    ...(index < displayedUsers.length - 1 ? empresaS.userListItemRowDivider : {}),
                    ...(active ? empresaS.userListItemRowActive : {}),
                  }}
                >
                  <button
                    type="button"
                    draggable
                    title="Arrastrar a Usuario o Autoriza en la grilla"
                    style={{
                      ...empresaS.userListItem,
                      ...empresaS.userListItemDraggable,
                    }}
                    onDragStart={e => handleUsuarioDragStart(e, user)}
                    onClick={() => setHighlightUserId(prev => (prev === user.id ? null : user.id))}
                  >
                    <UserAvatar usuario={user} size={32} />
                    <div style={empresaS.userInfo}>
                      <span style={empresaS.userName}>{usuarioDisplayName(user)}</span>
                      {user.sysPerfil?.nombre && (
                        <span style={{
                          ...empresaS.userPerfil,
                          ...(active ? empresaS.userPerfilActive : {}),
                        }}
                        >
                          {user.sysPerfil.nombre}
                        </span>
                      )}
                      {user.esContador && (
                        <span style={{
                          ...empresaS.userContador,
                          ...(active ? empresaS.userPerfilActive : {}),
                        }}
                        >
                          (Contador)
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    style={{
                      ...empresaS.userPinBtn,
                      ...(pinned ? empresaS.userPinBtnActive : {}),
                    }}
                    title={pinned ? 'Desfijar usuario de la lista' : 'Fijar usuario en la lista'}
                    aria-label={pinned ? 'Desfijar usuario' : 'Fijar usuario'}
                    aria-pressed={pinned}
                    onClick={e => togglePinUser(user, e)}
                  >
                    <FaThumbtack size={12} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
      ) : null}
      </div>
    </div>
  );
}

function getCellValue(row: any, field: string): string | number | boolean {
  switch (field) {
    case 'id':
      return row.id ?? '';
    case 'profaseActividad.descripcion':
      return row.profaseActividad?.descripcion ?? row.data?.label ?? '';
    case 'habilitado':
      return row.habilitado == null ? '' : !!row.habilitado;
    case 'habilitadoEmpresa':
      return habilitadoEmpresaToSelectValue(row.habilitadoEmpresa);
    case 'asignarAContadorEmpresa':
      return row.asignarAContadorEmpresa == null ? '' : !!row.asignarAContadorEmpresa;
    case 'requiereAutorizacion':
      return row.requiereAutorizacion == null ? '' : !!row.requiereAutorizacion;
    case 'requiereAutorizacionEmpresa':
      return row.requiereAutorizacionEmpresa == null ? '' : !!row.requiereAutorizacionEmpresa;
    case 'requiereArchivoEmpresa':
      return row.requiereArchivoEmpresa == null ? '' : !!row.requiereArchivoEmpresa;
    case 'usuario':
      return usuarioLabel(row.usuario);
    case 'autoriza':
      return usuarioLabel(row.autoriza);
    default:
      return '';
  }
}

function compareValues(a: string | number | boolean, b: string | number | boolean, order: string): number {
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    const cmp = Number(a) - Number(b);
    return order === 'desc' ? -cmp : cmp;
  }
  const sa = String(a).toLowerCase();
  const sb = String(b).toLowerCase();
  const cmp = sa.localeCompare(sb, undefined, { numeric: true });
  return order === 'desc' ? -cmp : cmp;
}

function getActivityVisibilityLabel(nodo: Record<string, unknown>): string {
  const profaseActividad = nodo.profaseActividad as { descripcion?: string } | undefined;
  const data = nodo.data as { label?: string } | undefined;
  const id = nodo.id;
  return profaseActividad?.descripcion ?? data?.label ?? (id != null ? `Actividad #${id}` : '—');
}

function buildActivityVisibilityItems(nodos: Record<string, unknown>[]) {
  return nodos
    .filter(n => n.id != null)
    .map(n => ({
      id: n.id as number,
      label: getActivityVisibilityLabel(n),
      bloqueado: !!n.bloqueado,
    }))
    .sort((a, b) => a.id - b.id);
}

function buildLockedActivityRowIds(nodos: Record<string, unknown>[]): Set<number> {
  const ids = new Set<number>();
  nodos.forEach(n => {
    if (n.id != null && n.bloqueado) ids.add(n.id as number);
  });
  return ids;
}

function serializeEmpresaForApiSave(e: EmpresaSeleccionadaItem) {
  const actividades: Record<string, EmpresaActividadConfig> = {};
  for (const [key, cfg] of Object.entries(e.actividades ?? {})) {
    actividades[key] = {
      id: cfg.id,
      nodoId: cfg.nodoId,
      habilitado: habilitadoEmpresaForApi(cfg.habilitado),
      requiereAutorizacion: cfg.requiereAutorizacion ?? null,
      asignarAContador: cfg.asignarAContador ?? null,
      requiereArchivo: cfg.requiereArchivo ?? null,
      modificado: !!cfg.modificado,
      usuario: cfg.usuario ?? null,
      autoriza: cfg.autoriza ?? null,
    };
  }
  return {
    id: e.id,
    bloqueado: !!e.bloqueado,
    modificado: !!e.modificado,
    disenoPersonalizado: !!e.disenoPersonalizado,
    empresaDisenoId: e.empresaDisenoId ?? null,
    actividades,
  };
}

function workflowConfigDraftSignature(
  empresas: EmpresaSeleccionadaItem[],
  periodos: PeriodoActividadWorkflowPeriodo[],
  templateNodos: Record<string, unknown>[],
): string {
  return JSON.stringify({
    empresas: empresas
      .map(empresa => {
        const saved = serializeEmpresaForApiSave(empresa);
        const actividades: Record<string, Omit<typeof saved.actividades[string], 'modificado'>> = {};
        for (const key of Object.keys(saved.actividades).sort()) {
          const { modificado: _modificado, ...actividad } = saved.actividades[key];
          actividades[key] = {
            ...actividad,
            usuario: actividad.usuario?.id != null ? { id: actividad.usuario.id } : null,
            autoriza: actividad.autoriza?.id != null ? { id: actividad.autoriza.id } : null,
          };
        }
        return {
          id: saved.id,
          bloqueado: saved.bloqueado,
          disenoPersonalizado: saved.disenoPersonalizado,
          empresaDisenoId: saved.empresaDisenoId,
          actividades,
        };
      })
      .sort((a, b) => a.id - b.id),
    periodos: serializePeriodosForConfigSave(periodos)
      .map(periodo => periodo.id)
      .sort((a, b) => a - b),
    nodos: templateNodos
      .map(nodo => ({
        id: nodo.id ?? null,
        habilitado: nodo.habilitado ?? null,
        requiereAutorizacion: nodo.requiereAutorizacion ?? null,
        bloqueado: !!nodo.bloqueado,
      }))
      .sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true })),
  });
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { detail?: string; message?: string; title?: string } };
    message?: string;
  };
  const data = err?.response?.data;
  return data?.detail ?? data?.message ?? data?.title ?? err?.message ?? fallback;
}

function filterAndSortNodos(allNodos: any[], params: any, hiddenRowIds?: Set<number>) {
  let data = [...allNodos];

  if (hiddenRowIds?.size) {
    data = data.filter(row => row.id == null || !hiddenRowIds.has(row.id));
  }

  for (const f of params.filters ?? []) {
    const needle = String(f.filterValue ?? '').toLowerCase();
    if (!needle) continue;
    data = data.filter(row =>
      String(getCellValue(row, f.filterName)).toLowerCase().includes(needle),
    );
  }

  const [field, order] = String(params.sort ?? 'id,asc').split(',');
  data.sort((a, b) => compareValues(getCellValue(a, field), getCellValue(b, field), order));

  return data;
}

export default function WorkflowAppConfigPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const syncUrlFromSelectionRef = useRef(false);

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [configReady, setConfigReady] = useState(false);
  const calendarioPeriodoOpen = shouldOpenWorkflowPeriodoModal(location.pathname, configReady);
  const [workflowInfo, setWorkflowInfo] = useState<WorkflowConfigResponse['workflow']>();
  const [periodosConfig, setPeriodosConfig] = useState<PeriodoActividadWorkflowPeriodo[]>([]);
  const [empresasSeleccionadasConfig, setEmpresasSeleccionadasConfig] = useState<EmpresaSeleccionadaItem[]>([]);
  const [savedDraftSignature, setSavedDraftSignature] = useState('');
  const [configStatusReady, setConfigStatusReady] = useState(false);
  const [hydrating, setHydrating] = useState(true);
  const configStatusReadyRef = useRef(false);
  const hydratingRef = useRef(true);
  configStatusReadyRef.current = configStatusReady;
  hydratingRef.current = hydrating;
  const empresasSeleccionadasConfigRef = useRef(empresasSeleccionadasConfig);
  const periodosConfigRef = useRef(periodosConfig);
  empresasSeleccionadasConfigRef.current = empresasSeleccionadasConfig;
  periodosConfigRef.current = periodosConfig;

  useEffect(() => {
    const workflowId = workflowInfo?.id ?? (id != null ? Number(id) : undefined);
    setSuperContaContext({
      workflow: compactWorkflowConfig({
        id: Number.isFinite(workflowId) ? workflowId : undefined,
        nombre: workflowInfo?.nombre,
        periodos: periodosConfig,
        empresasSeleccionadas: empresasSeleccionadasConfig,
      }),
    });
    return () => clearSuperContaContext();
  }, [id, workflowInfo, periodosConfig, empresasSeleccionadasConfig]);
  const [configUsuarios, setConfigUsuarios] = useState<ConfigUsuarioRef[]>([]);
  const [activeEmpresaId, setActiveEmpresaId] = useState<number | null>(null);
  const [activeEmpresa, setActiveEmpresa] = useState<EmpresaItem | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(() => new Set());
  const [rfcEmpresaIds, setRfcEmpresaIds] = useState<number[]>([]);
  const [checkedRfcEmpresaIds, setCheckedRfcEmpresaIds] = useState<Set<number>>(() => new Set());
  const [pendingUsuarioAssign, setPendingUsuarioAssign] = useState<PendingUsuarioAssign | null>(null);
  const [pendingUsuarioClear, setPendingUsuarioClear] = useState<PendingUsuarioClear | null>(null);
  const [pendingEmpresaFieldToggle, setPendingEmpresaFieldToggle] = useState<PendingEmpresaFieldToggle | null>(null);
  const [pendingEmpresaPasteAlso, setPendingEmpresaPasteAlso] = useState<PendingEmpresaPasteAlso | null>(null);
  const [gridVersion, setGridVersion] = useState(0);
  const [workflowSearchOpen, setWorkflowSearchOpen] = useState(false);
  const [activitiesVisibilityOpen, setActivitiesVisibilityOpen] = useState(false);
  const [hiddenActivityRowIds, setHiddenActivityRowIds] = useState<Set<number>>(() => new Set());
  const [draftHiddenActivityRowIds, setDraftHiddenActivityRowIds] = useState<Set<number>>(() => new Set());
  const [draftLockedActivityRowIds, setDraftLockedActivityRowIds] = useState<Set<number>>(() => new Set());
  const [pasteClipboardInvalidLocked, setPasteClipboardInvalidLocked] = useState(false);
  const [empresaActividadesEditing, setEmpresaActividadesEditing] = useState(false);
  const [lockedEmpresaColumns, setLockedEmpresaColumns] = useState<Set<EmpresaActividadColumnKey>>(
    () => new Set(),
  );
  const pasteClipboardBlockedTextRef = useRef<string | null>(null);

  const baseNodosRef = useRef<Record<string, unknown>[]>([]);
  const allNodosRef = useRef<any[]>([]);
  const assignConfirmRef = useRef<HTMLDivElement>(null);
  const adoptSavedBaselineTimerRef = useRef<number | null>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const adoptCurrentConfigAsSaved = useCallback(() => {
    if (adoptSavedBaselineTimerRef.current != null) {
      window.clearTimeout(adoptSavedBaselineTimerRef.current);
    }
    adoptSavedBaselineTimerRef.current = window.setTimeout(() => {
      adoptSavedBaselineTimerRef.current = null;
      setSavedDraftSignature(workflowConfigDraftSignature(
        empresasSeleccionadasConfigRef.current,
        periodosConfigRef.current,
        baseNodosRef.current,
      ));
      setConfigStatusReady(true);
    }, 0);
  }, []);

  const finishConfigHydration = useCallback(() => {
    adoptCurrentConfigAsSaved();
    window.setTimeout(() => {
      setHydrating(false);
    }, 0);
  }, [adoptCurrentConfigAsSaved]);

  useEffect(() => () => {
    if (adoptSavedBaselineTimerRef.current != null) {
      window.clearTimeout(adoptSavedBaselineTimerRef.current);
    }
  }, []);

  const syncWorkflowEmpresaQueryParam = useCallback((empresaId: number | null) => {
    if (!id) return;

    syncUrlFromSelectionRef.current = true;
    const nextParams = new URLSearchParams(searchParams);

    if (empresaId == null) {
      nextParams.delete('workflowEmpresaId');
    } else {
      const cfg = empresasSeleccionadasConfig.find(e => e.id === empresaId);
      const workflowEmpresaId = cfg?.workflowEmpresaId;
      if (workflowEmpresaId != null && !Number.isNaN(Number(workflowEmpresaId))) {
        nextParams.set('workflowEmpresaId', String(workflowEmpresaId));
      } else {
        nextParams.delete('workflowEmpresaId');
      }
    }

    if (nextParams.toString() === searchParams.toString()) {
      syncUrlFromSelectionRef.current = false;
      return;
    }

    setSearchParams(nextParams, { replace: true });
  }, [id, empresasSeleccionadasConfig, searchParams, setSearchParams]);

  const applyActiveEmpresaId = useCallback((
    empresaId: number | null,
    options?: { syncUrl?: boolean },
  ) => {
    setActiveEmpresaId(empresaId);
    if (options?.syncUrl !== false && configReady) {
      syncWorkflowEmpresaQueryParam(empresaId);
    }
  }, [configReady, syncWorkflowEmpresaQueryParam]);

  const handleActiveEmpresaChange = useCallback((empresaId: number | null) => {
    applyActiveEmpresaId(empresaId, { syncUrl: true });
  }, [applyActiveEmpresaId]);

  const unlockPasteClipboard = useCallback(() => {
    pasteClipboardBlockedTextRef.current = null;
    setPasteClipboardInvalidLocked(false);
  }, []);

  const lockPasteForInvalidClipboard = useCallback((clipboardText: string) => {
    pasteClipboardBlockedTextRef.current = clipboardText.trim();
    setPasteClipboardInvalidLocked(true);
    playUsuarioDropDeniedSound();
  }, []);

  useEffect(() => {
    const onClipboardMutation = () => unlockPasteClipboard();
    document.addEventListener('copy', onClipboardMutation);
    document.addEventListener('cut', onClipboardMutation);
    return () => {
      document.removeEventListener('copy', onClipboardMutation);
      document.removeEventListener('cut', onClipboardMutation);
    };
  }, [unlockPasteClipboard]);

  useEffect(() => {
    if (!pasteClipboardInvalidLocked) return;
    const onFocus = () => {
      void (async () => {
        const text = await readEmpresaActividadesClipboardTextRobust();
        if (text == null) return;
        const trimmed = text.trim();
        if (trimmed && trimmed !== pasteClipboardBlockedTextRef.current) {
          unlockPasteClipboard();
        }
      })();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [pasteClipboardInvalidLocked, unlockPasteClipboard]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    if (adoptSavedBaselineTimerRef.current != null) {
      window.clearTimeout(adoptSavedBaselineTimerRef.current);
      adoptSavedBaselineTimerRef.current = null;
    }
    baseNodosRef.current = [];
    allNodosRef.current = [];
    setConfigReady(false);
    setWorkflowInfo(undefined);
    setPeriodosConfig([]);
    setEmpresasSeleccionadasConfig([]);
    setSavedDraftSignature('');
    setConfigStatusReady(false);
    setHydrating(true);
    setConfigUsuarios([]);
    setActiveEmpresaId(null);
    setActiveEmpresa(null);
    setSelectedRowIds(new Set());
    setRfcEmpresaIds([]);
    setCheckedRfcEmpresaIds(new Set());
    setPendingUsuarioAssign(null);
    setPendingUsuarioClear(null);
    setHiddenActivityRowIds(new Set());
    setDraftHiddenActivityRowIds(new Set());
    setDraftLockedActivityRowIds(new Set());
    setActivitiesVisibilityOpen(false);
    setPasteClipboardInvalidLocked(false);
    pasteClipboardBlockedTextRef.current = null;
    setGridVersion(0);
    setRows([]);
    setLoading(true);

    (async () => {
      try {
        const res = await axios.get<WorkflowConfigResponse>(`api/profase-workflows/${id}/config`);
        if (cancelled) return;

        const nodos = Array.isArray(res.data?.nodos) ? res.data.nodos : [];
        const empresasSeleccionadas = parseEmpresasSeleccionadas(res.data?.empresasSeleccionadas);
        const usuarios = parseConfigUsuarios(res.data?.usuarios);
        const baseNodos = normalizeConfigNodos(nodos);
        const mappedEmpresas = mapEmpresasConfigWithWarning(empresasSeleccionadas, baseNodos);
        const activeIdFromQuery = resolveEmpresaIdFromWorkflowEmpresaQuery(
          mappedEmpresas,
          new URLSearchParams(window.location.search).get('workflowEmpresaId'),
        );
        const activeId = activeIdFromQuery ?? mappedEmpresas[0]?.id ?? null;
        const activeEmpresaConfig = mappedEmpresas.find(e => e.id === activeId);

        setWorkflowInfo(res.data?.workflow);
        const loadedPeriodos = parseConfigPeriodos(res.data?.periodos);
        setPeriodosConfig(loadedPeriodos);
        setConfigUsuarios(usuarios);
        setEmpresasSeleccionadasConfig(mappedEmpresas);
        baseNodosRef.current = baseNodos;
        allNodosRef.current = buildNodosForEmpresa(baseNodos, activeEmpresaConfig);
        setActiveEmpresaId(activeId);
        setSavedDraftSignature(workflowConfigDraftSignature(mappedEmpresas, loadedPeriodos, baseNodos));
        setConfigReady(true);
        setGridVersion(v => v + 1);
      } catch {
        if (!cancelled) {
          showToastRef.current('error', 'No se pudo cargar la configuración del workflow.');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!configReady) return;
    if (syncUrlFromSelectionRef.current) {
      syncUrlFromSelectionRef.current = false;
      return;
    }

    const queryValue = searchParams.get('workflowEmpresaId');
    if (!queryValue) return;

    const activeIdFromQuery = resolveEmpresaIdFromWorkflowEmpresaQuery(
      empresasSeleccionadasConfig,
      queryValue,
    );
    if (activeIdFromQuery == null) return;

    setActiveEmpresaId(prev => (prev === activeIdFromQuery ? prev : activeIdFromQuery));
  }, [configReady, searchParams, empresasSeleccionadasConfig]);

  const fetchData = useCallback(async (params: any) => {
    if (!configReady) return;

    const pageRows = filterAndSortNodos(allNodosRef.current, params, hiddenActivityRowIds);
    setRows(pageRows);
  }, [configReady, hiddenActivityRowIds]);

  const handleSelectedEmpresasChange = useCallback((empresas: EmpresaSeleccionadaItem[]) => {
    const clearingSelection = empresas.length === 0
      && empresasSeleccionadasConfigRef.current.length > 0;
    const empresaIds = empresas.map(e => e.id);
    setRfcEmpresaIds(empresaIds);
    setEmpresasSeleccionadasConfig(prev => {
      const byId = new Map(prev.map(e => [e.id, e]));
      const next = mapEmpresasConfigWithWarning(
        empresas.map(emp => {
          const existing = byId.get(emp.id);
          const bloqueadoChanged = existing != null && !!existing.bloqueado !== !!emp.bloqueado;
          const disenoChanged = existing != null && (
            !!existing.disenoPersonalizado !== !!emp.disenoPersonalizado
            || (existing.empresaDisenoId ?? null) !== (emp.empresaDisenoId ?? null)
          );
          return {
            id: emp.id,
            bloqueado: emp.bloqueado,
            modificado: hydratingRef.current
              ? !!existing?.modificado
              : !!(existing?.modificado || emp.modificado || bloqueadoChanged || disenoChanged),
            disenoPersonalizado: emp.disenoPersonalizado,
            empresaDisenoId: emp.empresaDisenoId ?? null,
            instanciaId: existing?.instanciaId ?? emp.instanciaId ?? null,
            instanciaEstado: existing?.instanciaEstado ?? emp.instanciaEstado ?? null,
            workflowEmpresaId: existing?.workflowEmpresaId ?? emp.workflowEmpresaId ?? null,
            actividades: resolveEmpresaActividadesOnSelect(existing, emp, baseNodosRef.current),
          };
        }),
        baseNodosRef.current,
      );
      if (empresasConfigNotifyEqual(prev, next)) return prev;
      return next;
    });

    let nextActiveId: number | null = null;
    setActiveEmpresaId(cur => {
      nextActiveId = cur != null && empresaIds.includes(cur) ? cur : (empresaIds[0] ?? null);
      return nextActiveId;
    });
    if (configReady) {
      syncWorkflowEmpresaQueryParam(nextActiveId);
    }
    if (hydratingRef.current && !clearingSelection) {
      adoptCurrentConfigAsSaved();
    }
  }, [configReady, syncWorkflowEmpresaQueryParam, adoptCurrentConfigAsSaved]);

  useEffect(() => {
    if (!configReady) return;

    const empresa = activeEmpresaId != null
      ? empresasSeleccionadasConfig.find(e => e.id === activeEmpresaId)
      : undefined;
    allNodosRef.current = buildNodosForEmpresa(baseNodosRef.current, empresa);
    setSelectedRowIds(new Set());
    setPendingUsuarioAssign(null);
    setPendingUsuarioClear(null);
    setGridVersion(v => v + 1);
  }, [activeEmpresaId, configReady]);

  const activeEmpresaBloqueada = useMemo(() => {
    if (activeEmpresaId == null) return false;
    return !!empresasSeleccionadasConfig.find(e => e.id === activeEmpresaId)?.bloqueado;
  }, [activeEmpresaId, empresasSeleccionadasConfig]);

  const canEditEmpresaActividades = empresaActividadesEditing;

  const isEmpresaColumnLocked = useCallback(
    (column: EmpresaActividadColumnKey) => lockedEmpresaColumns.has(column),
    [lockedEmpresaColumns],
  );

  const toggleEmpresaColumnLock = useCallback((column: EmpresaActividadColumnKey) => {
    setLockedEmpresaColumns(prev => {
      const next = new Set(prev);
      if (next.has(column)) {
        next.delete(column);
        playUnlockDoorSound();
      } else {
        next.add(column);
        playLockDoorSound();
      }
      return next;
    });
  }, []);

  const renderEmpresaColumnLockButton = useCallback((column: EmpresaActividadColumnKey) => {
    const locked = lockedEmpresaColumns.has(column);
    const label = EMPRESA_ACTIVIDAD_COLUMN_LABELS[column];
    return (
      <button
        type="button"
        style={{
          ...s.actividadLockBtn,
          ...(locked ? s.actividadLockBtnActive : s.actividadLockBtnInactive),
        }}
        onClick={e => {
          e.stopPropagation();
          toggleEmpresaColumnLock(column);
        }}
        title={locked ? `Desbloquear columna ${label}` : `Bloquear columna ${label}`}
        aria-label={locked ? `Desbloquear columna ${label}` : `Bloquear columna ${label}`}
        aria-pressed={locked}
      >
        <FaLock size={14} />
      </button>
    );
  }, [lockedEmpresaColumns, toggleEmpresaColumnLock]);

  useEffect(() => {
    setEmpresaActividadesEditing(false);
  }, [id]);

  useEffect(() => {
    if (empresaActividadesEditing) return;
    setPendingUsuarioAssign(null);
    setPendingUsuarioClear(null);
    setPendingEmpresaFieldToggle(null);
    setPendingEmpresaPasteAlso(null);
  }, [empresaActividadesEditing]);

  const handleCheckedRfcIdsChange = useCallback((ids: number[]) => {
    setCheckedRfcEmpresaIds(new Set(ids));
  }, []);

  const rfcEmpresaAlsoApplicableCount = useMemo(
    () => empresasSeleccionadasConfig.filter(
      e => !e.bloqueado && e.id !== activeEmpresaId,
    ).length,
    [empresasSeleccionadasConfig, activeEmpresaId],
  );

  const checkedRfcAlsoApplicableCount = useMemo(
    () => empresasSeleccionadasConfig.filter(
      e => checkedRfcEmpresaIds.has(e.id) && !e.bloqueado,
    ).length,
    [empresasSeleccionadasConfig, checkedRfcEmpresaIds],
  );

  const pasteEmpresaAlsoApplicableCount = useMemo(
    () => empresasSeleccionadasConfig.filter(
      e => checkedRfcEmpresaIds.has(e.id) && !e.bloqueado && e.id !== activeEmpresaId,
    ).length,
    [empresasSeleccionadasConfig, checkedRfcEmpresaIds, activeEmpresaId],
  );

  const usuarioClearAlsoApplicableCount = useMemo(() => {
    if (!pendingUsuarioClear) return 0;
    const actividadKey = getEmpresaActividadMapKey(pendingUsuarioClear.targetRow);
    if (actividadKey == null) return 0;
    const { field } = pendingUsuarioClear;
    return empresasSeleccionadasConfig.filter(e =>
      checkedRfcEmpresaIds.has(e.id)
      && !e.bloqueado
      && e.id !== activeEmpresaId
      && !!e.actividades?.[actividadKey]?.[field]?.id,
    ).length;
  }, [
    pendingUsuarioClear,
    empresasSeleccionadasConfig,
    checkedRfcEmpresaIds,
    activeEmpresaId,
  ]);

  const workflowName =
    workflowInfo?.nombre
    ?? (allNodosRef.current[0]?.profaseWorkflow as { nombre?: string } | undefined)?.nombre;

  const displayWorkflowName = workflowName?.trim() || (id ? `Workflow #${id}` : '');

  const activeEmpresaGroupHeader = useMemo(
    () => buildActiveEmpresaGroupHeader(activeEmpresa),
    [activeEmpresa],
  );

  const activeEmpresaConfig = useMemo(() => {
    if (activeEmpresaId == null) return null;
    return empresasSeleccionadasConfig.find(e => e.id === activeEmpresaId) ?? null;
  }, [activeEmpresaId, empresasSeleccionadasConfig]);

  const disenoPersonalizadoEnabled = !!activeEmpresaConfig?.disenoPersonalizado;

  const saveEmpresaCount = useMemo(() => {
    const modified = empresasSeleccionadasConfig.filter(e => e.modificado).length;
    return modified > 0 ? modified : empresasSeleccionadasConfig.length;
  }, [empresasSeleccionadasConfig]);

  const performSaveConfig = useCallback(async () => {
    if (!id) return;
    setConfirmLoading(true);
    try {
      await axios.put(`api/profase-workflows/${id}/config`, {
        empresasSeleccionadas: empresasSeleccionadasConfig.map(serializeEmpresaForApiSave),
        periodos: serializePeriodosForConfigSave(periodosConfig),
      });
      dismiss();
      playUsuarioApplyAllSound();
      setEmpresaActividadesEditing(false);

      const res = await axios.get<WorkflowConfigResponse>(`api/profase-workflows/${id}/config`);
      const nodos = Array.isArray(res.data?.nodos) ? res.data.nodos : [];
      const empresasSeleccionadas = parseEmpresasSeleccionadas(res.data?.empresasSeleccionadas);
      const baseNodos = normalizeConfigNodos(nodos);
      const preservedEmpresaId = activeEmpresaId != null
        && empresasSeleccionadas.some(e => e.id === activeEmpresaId)
        ? activeEmpresaId
        : empresasSeleccionadas[0]?.id ?? null;
      const preservedEmpresa = empresasSeleccionadas.find(e => e.id === preservedEmpresaId);

      const savedEmpresas = mapEmpresasConfigWithWarning(empresasSeleccionadas, baseNodos);
      const savedPeriodos = parseConfigPeriodos(res.data?.periodos);
      if (adoptSavedBaselineTimerRef.current != null) {
        window.clearTimeout(adoptSavedBaselineTimerRef.current);
        adoptSavedBaselineTimerRef.current = null;
      }
      empresasSeleccionadasConfigRef.current = savedEmpresas;
      periodosConfigRef.current = savedPeriodos;
      baseNodosRef.current = baseNodos;
      setEmpresasSeleccionadasConfig(savedEmpresas);
      setPeriodosConfig(savedPeriodos);
      allNodosRef.current = buildNodosForEmpresa(baseNodos, preservedEmpresa);
      setActiveEmpresaId(preservedEmpresaId);
      setSavedDraftSignature(workflowConfigDraftSignature(
        savedEmpresas,
        savedPeriodos,
        baseNodos,
      ));
      setConfigStatusReady(true);
      setGridVersion(v => v + 1);
      showToast('success', `Configuracion de Workflow: ${displayWorkflowName} guardada con éxito`);
    } catch (err) {
      dismiss();
      playUsuarioDropDeniedSound();
      showToast('error', extractApiErrorMessage(err, 'No se pudo guardar la configuración.'));
    } finally {
      setConfirmLoading(false);
    }
  }, [
    id,
    displayWorkflowName,
    empresasSeleccionadasConfig,
    periodosConfig,
    activeEmpresaId,
    dismiss,
    setConfirmLoading,
    showToast,
  ]);

  const confirmSaveConfig = useCallback(() => {
    const n = saveEmpresaCount;
    const label = n === 1 ? 'empresa' : 'empresas';
    const periodoCount = periodosConfig.length;
    const periodoLabel = periodoCount === 1 ? 'periodo' : 'periodos';
    ask(
      n === 0
        ? <>¿Desea guardar el workflow sin empresas seleccionadas y con <strong>{periodoCount}</strong> {periodoLabel} asignados?</>
        : <>¿Desea guardar cambios en <strong>{n}</strong> {label} y <strong>{periodoCount}</strong> {periodoLabel} asignados?</>,
      () => { void performSaveConfig(); },
      {
        title: 'Guardar configuración',
        confirmLabel: 'Guardar',
        cancelLabel: 'Cancelar',
        variant: 'info',
      },
    );
  }, [ask, performSaveConfig, saveEmpresaCount, periodosConfig.length]);

  const isDraft = useMemo(() => {
    if (hydrating || !configReady || !configStatusReady || !savedDraftSignature) return false;
    return workflowConfigDraftSignature(
      empresasSeleccionadasConfig,
      periodosConfig,
      baseNodosRef.current,
    ) !== savedDraftSignature;
  }, [hydrating, configReady, configStatusReady, savedDraftSignature, empresasSeleccionadasConfig, periodosConfig, gridVersion, rows]);

  const pageSubtitle = useMemo(() => {
    const empresaPart = activeEmpresa
      ? [activeEmpresa.rfc?.trim(), activeEmpresa.razonSocial?.trim()].filter(Boolean).join(' ')
      : '';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>{displayWorkflowName}</span>
        {empresaPart ? <span>{` - ${empresaPart}`}</span> : null}
        {configReady ? (
          <span style={isDraft ? s.configStatusModified : s.configStatusUnchanged}>
            {isDraft ? 'Modificado' : 'Sin Modificaciones'}
          </span>
        ) : null}
      </span>
    );
  }, [displayWorkflowName, activeEmpresa, isDraft, configReady]);

  const isDraftRef = useRef(isDraft);
  isDraftRef.current = isDraft;

  const requestLeaveTo = useCallback((
    destination: { kind: 'spa' | 'document'; to: string },
  ) => {
    const nextPathname = destination.kind === 'spa'
      ? destination.to.split('?')[0]
      : resolveHashRouterHref(destination.to)?.pathname ?? destination.to;
    if (!isDraftRef.current || isWorkflowConfigStayNavigation(location.pathname, nextPathname)) {
      if (destination.kind === 'document') window.location.assign(destination.to);
      else navigate(destination.to);
      return;
    }
    ask(
      WORKFLOW_CONFIG_LEAVE_MESSAGE,
      () => {
        dismiss();
        if (destination.kind === 'document') window.location.assign(destination.to);
        else navigate(destination.to);
      },
      {
        confirmLabel: 'Sí',
        cancelLabel: 'Cancelar',
        skipLoading: true,
      },
    );
  }, [ask, dismiss, location.pathname, navigate]);

  useEffect(() => {
    if (!isDraft) return;
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target as Element | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.closest('.crm-superconta-panel, .crm-superconta-chat, .crm-superconta-html')) return;
      const resolved = resolveHashRouterHref(anchor.getAttribute('href') ?? '', window.location.origin);
      if (!resolved) return;
      if (isWorkflowConfigStayNavigation(location.pathname, resolved.pathname)) return;
      event.preventDefault();
      event.stopPropagation();
      if (resolved.kind === 'document') {
        const isAdmin = resolved.pathname === '/admin' || resolved.pathname.startsWith('/admin/');
        if (isAdmin) {
          requestLeaveTo({ kind: 'document', to: resolved.href });
          return;
        }
        requestLeaveTo({ kind: 'spa', to: `${resolved.pathname}${resolved.search}` });
        return;
      }
      requestLeaveTo({ kind: 'spa', to: `${resolved.pathname}${resolved.search}` });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isDraft, location.pathname, requestLeaveTo]);

  const handleDisenoPersonalizado = useCallback(() => {
    const workflowEmpresaId = activeEmpresaConfig?.workflowEmpresaId;
    if (!id || workflowEmpresaId == null || Number.isNaN(Number(workflowEmpresaId))) return;
    requestLeaveTo({
      kind: 'spa',
      to: `/workflow/${id}/workflow-empresa/${workflowEmpresaId}/diseno`,
    });
  }, [activeEmpresaConfig, id, requestLeaveTo]);

  const periodosWarning = configReady
    ? periodosConfigWarningTitle(periodosConfig.length)
    : null;

  const syncNodoInCache = useCallback((
    updated: any,
    patchField:
      | 'habilitado'
      | 'habilitadoEmpresa'
      | 'asignarAContadorEmpresa'
      | 'requiereAutorizacion'
      | 'requiereAutorizacionEmpresa'
      | 'requiereArchivoEmpresa'
      | 'bloqueado',
  ) => {
    const nodoId = updated?.id;
    if (nodoId == null) return;

    const marksTemplateModificado =
      patchField === 'habilitado' || patchField === 'requiereAutorizacion';
    const nextUpdated = marksTemplateModificado ? markNodoModificado(updated) : updated;

    const idx = allNodosRef.current.findIndex(n => n.id === nodoId);
    if (idx >= 0) allNodosRef.current[idx] = nextUpdated;
    setRows(prev => prev.map(r => (r.id === nodoId ? nextUpdated : r)));

    if (patchField === 'bloqueado') {
      const baseIdx = baseNodosRef.current.findIndex(n => n.id === nodoId);
      if (baseIdx >= 0) {
        baseNodosRef.current[baseIdx] = {
          ...baseNodosRef.current[baseIdx],
          bloqueado: !!updated.bloqueado,
        };
      }
      return;
    }

    if (patchField === 'habilitado' || patchField === 'requiereAutorizacion') {
      const baseIdx = baseNodosRef.current.findIndex(n => n.id === nodoId);
      if (baseIdx >= 0) {
        baseNodosRef.current[baseIdx] = markNodoModificado({
          ...baseNodosRef.current[baseIdx],
          [patchField]: nextUpdated[patchField],
        });
      }
    }

    if (
      activeEmpresaId == null
      || (patchField !== 'habilitadoEmpresa'
        && patchField !== 'asignarAContadorEmpresa'
        && patchField !== 'requiereAutorizacionEmpresa'
        && patchField !== 'requiereArchivoEmpresa')
    ) {
      return;
    }

    const actividadKey = getEmpresaActividadMapKey(nextUpdated);
    if (actividadKey == null) return;

    const fromRow = nodoToEmpresaActividadConfig(nextUpdated);
    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => {
        if (emp.id !== activeEmpresaId) return emp;
        const actividades = { ...(emp.actividades ?? {}) };
        const actividadCfg = markEmpresaActividadModificado(
          mergeEmpresaActividadFromRow(actividades[actividadKey], fromRow),
        );
        actividades[actividadKey] = actividadCfg;
        return markEmpresaModificado({ ...emp, actividades });
      }),
      baseNodosRef.current,
    ));
  }, [activeEmpresaId]);

  const patchNodo = useCallback((
    row: any,
    field: EmpresaCheckboxField,
    value: boolean,
  ) => {
    const next = applyRowEmpresaCheckboxUpdate(row, field, value);
    const unchanged = field === 'asignarAContadorEmpresa'
      ? row.asignarAContadorEmpresa === value
        && (value ? !row.usuario?.id : true)
      : field === 'requiereAutorizacionEmpresa'
        ? row.requiereAutorizacionEmpresa === value
          && (value ? true : !row.autoriza?.id)
        : field === 'requiereArchivoEmpresa'
          ? row.requiereArchivoEmpresa === value
          : row[field] != null && row[field] === value;
    if (unchanged) return;
    syncNodoInCache(next, field);
  }, [syncNodoInCache]);

  const patchNodoHabilitadoEmpresa = useCallback((
    row: any,
    value: HabilitadoEmpresaSelectValue,
  ) => {
    if (habilitadoEmpresaRowsEqual(row.habilitadoEmpresa, value)) return;
    const next = applyRowHabilitadoEmpresaUpdate(row, value);
    syncNodoInCache(next, 'habilitadoEmpresa');
  }, [syncNodoInCache]);

  const patchNodoUsuarioField = useCallback((
    row: any,
    field: 'usuario' | 'autoriza',
    value: { id?: number } | null,
  ) => {
    if (activeEmpresaId == null) {
      showToast('info', 'Seleccione una empresa en la lista de empresas seleccionadas.');
      return;
    }
    const nodoId = row?.id;
    if (nodoId == null) return;
    if (value == null && !row[field]?.id) return;
    if (value?.id != null && row[field]?.id === value.id) return;

    const updated = applyRowEmpresaUsuarioFieldUpdate(row, field, value);
    const idx = allNodosRef.current.findIndex(n => n.id === nodoId);
    if (idx >= 0) allNodosRef.current[idx] = updated;
    setRows(prev => prev.map(r => (r.id === nodoId ? updated : r)));

    const actividadKey = getEmpresaActividadMapKey(updated);
    if (actividadKey == null) return;

    const actividadCfg = nodoToEmpresaActividadConfig(updated);
    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => {
        if (emp.id !== activeEmpresaId) return emp;
        const actividades = { ...(emp.actividades ?? {}) };
        actividades[actividadKey] = markEmpresaActividadModificado(
          mergeEmpresaActividadFromRow(actividades[actividadKey], actividadCfg),
        );
        return markEmpresaModificado({ ...emp, actividades });
      }),
      baseNodosRef.current,
    ));
  }, [activeEmpresaId, showToast]);

  const requestUsuarioClear = useCallback((
    targetRow: any,
    field: 'usuario' | 'autoriza',
  ) => {
    if (activeEmpresaId == null) {
      showToast('info', 'Seleccione una empresa en la lista de empresas seleccionadas.');
      return;
    }
    if (activeEmpresaBloqueada) {
      showToast('info', 'La empresa activa está bloqueada.');
      return;
    }
    if (!canEditEmpresaActividades) {
      showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
      return;
    }
    if (isEmpresaColumnLocked(field)) {
      showToast('info', 'La columna está bloqueada.');
      return;
    }
    if (targetRow?.bloqueado) {
      showToast('info', 'La actividad está bloqueada.');
      return;
    }
    if (!targetRow?.[field]?.id) return;

    setPendingUsuarioAssign(null);
    setPendingEmpresaFieldToggle(null);
    setPendingEmpresaPasteAlso(null);

    const fieldLabel = field === 'usuario' ? 'Usuario' : 'Autoriza (usuario)';
    const actividadLabel =
      targetRow?.profaseActividad?.descripcion ?? targetRow?.data?.label ?? 'actividad';

    patchNodoUsuarioField(targetRow, field, null);
    showToast('success', `${fieldLabel} quitado en "${actividadLabel}" en la empresa seleccionada.`);

    const otherEmpresasCount = empresasSeleccionadasConfig.filter(
      e => !e.bloqueado && e.id !== activeEmpresaId,
    ).length;
    if (otherEmpresasCount === 0) return;

    setPendingUsuarioClear({
      field,
      targetRow: applyRowEmpresaUsuarioFieldUpdate(targetRow, field, null),
    });
  }, [
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    empresasSeleccionadasConfig,
    patchNodoUsuarioField,
    showToast,
    isEmpresaColumnLocked,
  ]);

  const applyUsuarioClearToCheckedEmpresas = useCallback(() => {
    const pending = pendingUsuarioClear;
    if (!pending) return;

    const { field, targetRow } = pending;
    const fieldLabel = field === 'usuario' ? 'Usuario' : 'Autoriza (usuario)';
    const actividadLabel =
      targetRow?.profaseActividad?.descripcion ?? targetRow?.data?.label ?? 'actividad';
    const actividadKey = getEmpresaActividadMapKey(targetRow);
    if (actividadKey == null) {
      setPendingUsuarioClear(null);
      return;
    }

    const apiNodoId = empresaActividadNodoIdForApi(targetRow);
    const updatesById = clearUsuarioFieldOnCheckedEmpresas(
      field,
      actividadKey,
      apiNodoId,
      empresasSeleccionadasConfig,
      checkedRfcEmpresaIds,
      activeEmpresaId,
    );

    if (!updatesById.size) {
      setPendingUsuarioClear(null);
      showToast(
        'info',
        'Las empresas marcadas no tienen ese usuario en esa actividad o no hay otras aplicables.',
      );
      return;
    }

    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => updatesById.get(emp.id) ?? emp),
      baseNodosRef.current,
    ));
    setPendingUsuarioClear(null);
    showToast(
      'success',
      `${fieldLabel} quitado en "${actividadLabel}" para ${updatesById.size} empresa${updatesById.size === 1 ? '' : 's'} seleccionada${updatesById.size === 1 ? '' : 's'}.`,
    );
  }, [
    pendingUsuarioClear,
    empresasSeleccionadasConfig,
    checkedRfcEmpresaIds,
    activeEmpresaId,
    showToast,
  ]);

  useEffect(() => {
    if (activeEmpresaBloqueada) setPendingUsuarioClear(null);
  }, [activeEmpresaBloqueada]);

  useEffect(() => {
    if (!pendingUsuarioAssign && !pendingUsuarioClear && !pendingEmpresaFieldToggle && !pendingEmpresaPasteAlso) return;
    const t = setTimeout(
      () => assignConfirmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
      30,
    );
    return () => clearTimeout(t);
  }, [pendingUsuarioAssign, pendingUsuarioClear, pendingEmpresaFieldToggle, pendingEmpresaPasteAlso]);

  const requestEmpresaFieldToggle = useCallback((
    row: any,
    field: EmpresaCheckboxField,
    value: boolean,
  ) => {
    if (activeEmpresaId == null) {
      showToast('info', 'Seleccione una empresa en la lista de empresas seleccionadas.');
      return;
    }
    if (activeEmpresaBloqueada) {
      showToast('info', 'La empresa activa está bloqueada.');
      return;
    }
    if (!canEditEmpresaActividades) {
      showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
      return;
    }
    if (isEmpresaColumnLocked(field)) {
      showToast('info', 'La columna está bloqueada.');
      return;
    }
    if (row?.bloqueado) {
      showToast('info', 'La actividad está bloqueada.');
      return;
    }
    if (row[field] != null && row[field] === value) return;

    patchNodo(row, field, value);

    const otherEmpresasCount = empresasSeleccionadasConfig.filter(
      e => !e.bloqueado && e.id !== activeEmpresaId,
    ).length;
    if (otherEmpresasCount === 0) return;

    setPendingUsuarioAssign(null);
    setPendingUsuarioClear(null);
    setPendingEmpresaPasteAlso(null);
    setPendingEmpresaFieldToggle({
      targetRow: applyRowEmpresaCheckboxUpdate(row, field, value),
      field,
      value,
    });
  }, [activeEmpresaId, activeEmpresaBloqueada, canEditEmpresaActividades, empresasSeleccionadasConfig, patchNodo, showToast, isEmpresaColumnLocked]);

  const requestHabilitadoEmpresaChange = useCallback((
    row: any,
    value: HabilitadoEmpresaSelectValue,
  ) => {
    if (activeEmpresaId == null) {
      showToast('info', 'Seleccione una empresa en la lista de empresas seleccionadas.');
      return;
    }
    if (activeEmpresaBloqueada) {
      showToast('info', 'La empresa activa está bloqueada.');
      return;
    }
    if (!canEditEmpresaActividades) {
      showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
      return;
    }
    if (isEmpresaColumnLocked('habilitadoEmpresa')) {
      showToast('info', 'La columna está bloqueada.');
      return;
    }
    if (row?.bloqueado) {
      showToast('info', 'La actividad está bloqueada.');
      return;
    }
    if (habilitadoEmpresaRowsEqual(row.habilitadoEmpresa, value)) return;

    patchNodoHabilitadoEmpresa(row, value);

    const otherEmpresasCount = empresasSeleccionadasConfig.filter(
      e => !e.bloqueado && e.id !== activeEmpresaId,
    ).length;
    if (otherEmpresasCount === 0) return;

    setPendingUsuarioAssign(null);
    setPendingUsuarioClear(null);
    setPendingEmpresaPasteAlso(null);
    setPendingEmpresaFieldToggle({
      targetRow: applyRowHabilitadoEmpresaUpdate(row, value),
      field: 'habilitadoEmpresa',
      value,
    });
  }, [
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    empresasSeleccionadasConfig,
    patchNodoHabilitadoEmpresa,
    showToast,
    isEmpresaColumnLocked,
  ]);

  const applyEmpresaFieldToggle = useCallback((scope: EmpresaFieldToggleScope) => {
    const pending = pendingEmpresaFieldToggle;
    if (!pending) return;

    const { targetRow, value, field } = pending;
    const isBulk = (pending.bulkActivityUpdates?.length ?? 0) > 0;

    const activityUpdates = isBulk
      ? pending.bulkActivityUpdates!
      : (() => {
        if (targetRow?.bloqueado) return [];
        const actividadKey = getEmpresaActividadMapKey(targetRow);
        if (actividadKey == null) return [];
        return [{
          actividadKey,
          nodoId: empresaActividadNodoIdForApi(targetRow),
        }];
      })();

    if (!isBulk && targetRow?.bloqueado) {
      setPendingEmpresaFieldToggle(null);
      showToast('info', 'La actividad está bloqueada.');
      return;
    }

    if (!activityUpdates.length) {
      setPendingEmpresaFieldToggle(null);
      return;
    }

    const updatesById = new Map<number, EmpresaSeleccionadaItem>();

    for (const empresa of empresasSeleccionadasConfig) {
      if (empresa.bloqueado) continue;
      if (empresa.id === activeEmpresaId) continue;
      if (scope === 'checkedEmpresas' && !checkedRfcEmpresaIds.has(empresa.id)) continue;

      const actividades = { ...(empresa.actividades ?? {}) };
      let changed = false;
      for (const { actividadKey, nodoId } of activityUpdates) {
        const prevCfg = actividades[actividadKey] ?? { nodoId };
        if (empresaActividadCfgFieldMatches(prevCfg, field, value)) continue;
        actividades[actividadKey] = applyEmpresaActividadCfgField(prevCfg, field, value, nodoId);
        changed = true;
      }
      if (changed) {
        updatesById.set(empresa.id, markEmpresaModificado({ ...empresa, actividades }));
      }
    }

    if (!updatesById.size) {
      setPendingEmpresaFieldToggle(null);
      showToast('info', 'No hay otras empresas desbloqueadas para actualizar.');
      return;
    }

    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => updatesById.get(emp.id) ?? emp),
      baseNodosRef.current,
    ));

    setPendingEmpresaFieldToggle(null);

    const updatedEmpresas = updatesById.size;
    const scopeLabel = scope === 'checkedEmpresas'
      ? `empresas seleccionadas (${checkedRfcEmpresaIds.size})`
      : 'todas las empresas';
    const actividadLabel = isBulk
      ? `${activityUpdates.length} actividad${activityUpdates.length === 1 ? '' : 'es'}`
      : (
        targetRow?.profaseActividad?.descripcion
        ?? targetRow?.data?.label
        ?? 'actividad'
      );
    showToast(
      'success',
      `${empresaFieldToggleSuccessLabel(field, value)} también en ${actividadLabel} para ${scopeLabel} `
      + `(${updatedEmpresas} empresa${updatedEmpresas === 1 ? '' : 's'} adicional${updatedEmpresas === 1 ? '' : 'es'}).`,
    );
  }, [
    pendingEmpresaFieldToggle,
    empresasSeleccionadasConfig,
    checkedRfcEmpresaIds,
    activeEmpresaId,
    showToast,
  ]);

  const applyUsuarioToActiveRowIds = useCallback((
    rowIds: Set<number>,
    field: 'usuario' | 'autoriza',
    user: { id?: number },
  ) => {
    const applyFieldToNodo = (n: any) => {
      if (n.id == null || n.bloqueado || !rowIds.has(n.id)) return n;
      if (n[field]?.id === user.id) return n;
      return applyRowEmpresaUsuarioFieldUpdate(n, field, user);
    };

    allNodosRef.current = allNodosRef.current.map(n => applyFieldToNodo(n));
    setRows(prev => prev.map(r => applyFieldToNodo(r)));

    if (activeEmpresaId == null) return;

    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => {
        if (emp.id !== activeEmpresaId) return emp;
        const actividades = { ...(emp.actividades ?? {}) };
        let changed = false;
        allNodosRef.current.forEach(n => {
          if (n.id == null || n.bloqueado || !rowIds.has(n.id)) return;
          const key = getEmpresaActividadMapKey(n);
          if (key == null) return;
          const prevCfg = actividades[key];
          const nextCfg = markEmpresaActividadModificado(
            mergeEmpresaActividadFromRow(prevCfg, nodoToEmpresaActividadConfig(n)),
          );
          if (
            prevCfg?.usuario?.id === nextCfg.usuario?.id
            && prevCfg?.autoriza?.id === nextCfg.autoriza?.id
            && prevCfg?.habilitado === nextCfg.habilitado
            && prevCfg?.requiereAutorizacion === nextCfg.requiereAutorizacion
            && prevCfg?.asignarAContador === nextCfg.asignarAContador
            && prevCfg?.requiereArchivo === nextCfg.requiereArchivo
          ) {
            return;
          }
          actividades[key] = nextCfg;
          changed = true;
        });
        return changed ? markEmpresaModificado({ ...emp, actividades }) : emp;
      }),
      baseNodosRef.current,
    ));
  }, [activeEmpresaId]);

  const requestUsuarioAssign = useCallback((
    row: any,
    field: 'usuario' | 'autoriza',
    user: any,
  ) => {
    if (!user?.id) return;
    if (activeEmpresaId == null) {
      playUsuarioDropDeniedSound();
      showToast('info', 'Seleccione una empresa en la lista de empresas seleccionadas.');
      return;
    }
    if (activeEmpresaBloqueada) {
      playUsuarioDropDeniedSound();
      showToast('info', 'La empresa activa está bloqueada.');
      return;
    }
    if (!canEditEmpresaActividades) {
      playUsuarioDropDeniedSound();
      showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
      return;
    }
    if (isEmpresaColumnLocked(field)) {
      playUsuarioDropDeniedSound();
      showToast('info', 'La columna está bloqueada.');
      return;
    }
    if (row?.bloqueado) {
      playUsuarioDropDeniedSound();
      showToast('info', 'La actividad está bloqueada.');
      return;
    }

    playUsuarioDropSound();
    patchNodoUsuarioField(row, field, user);

    setPendingEmpresaFieldToggle(null);
    setPendingEmpresaPasteAlso(null);
    setPendingUsuarioClear(null);
    const nextRow = applyRowEmpresaUsuarioFieldUpdate(row, field, user);
    setPendingUsuarioAssign({
      user,
      field,
      targetRow: nextRow,
      columnDrop: false,
    });
  }, [
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    patchNodoUsuarioField,
    showToast,
    isEmpresaColumnLocked,
  ]);

  const applyUsuarioAssignAlso = useCallback((scope: UsuarioAssignAlsoScope) => {
    const pending = pendingUsuarioAssign;
    if (!pending) return;

    const { user, field, targetRow, columnDrop } = pending;
    const fieldLabel = field === 'usuario' ? 'Usuario' : 'Autoriza (usuario)';
    const actividadLabel =
      targetRow?.profaseActividad?.descripcion ?? targetRow?.data?.label ?? 'actividad';

    if (scope === 'checkedEmpresas') {
      const activityConfigs = buildUsuarioAssignActivityConfigs(
        pending,
        allNodosRef.current,
        selectedRowIds,
      );
      const activityCount = Object.keys(activityConfigs).length;
      if (!activityCount) {
        setPendingUsuarioAssign(null);
        return;
      }

      const updatesById = copyActivityConfigsToCheckedEmpresas(
        activityConfigs,
        empresasSeleccionadasConfig,
        checkedRfcEmpresaIds,
      );

      if (!updatesById.size) {
        setPendingUsuarioAssign(null);
        showToast('info', 'Las empresas seleccionadas ya tienen esas asignaciones.');
        return;
      }

      setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
        prev.map(emp => updatesById.get(emp.id) ?? emp),
        baseNodosRef.current,
      ));
      setPendingUsuarioAssign(null);
      showToast(
        'success',
        `Selección de usuarios copiada en ${activityCount} actividad${activityCount === 1 ? '' : 'es'} `
        + `para ${updatesById.size} empresa${updatesById.size === 1 ? '' : 's'} seleccionada${updatesById.size === 1 ? '' : 's'}.`,
      );
      return;
    }

    const rowIdsToUpdate = new Set<number>();
    const targetId = targetRow?.id;
    if (targetId != null) {
      const targetNodo = allNodosRef.current.find(n => n.id === targetId);
      if (targetNodo && !targetNodo.bloqueado) rowIdsToUpdate.add(targetId);
    }
    allNodosRef.current.forEach(n => {
      if (n.id != null && selectedRowIds.has(n.id) && !n.bloqueado) {
        rowIdsToUpdate.add(n.id);
      }
    });

    if (!rowIdsToUpdate.size) {
      setPendingUsuarioAssign(null);
      showToast('info', 'No hay actividades seleccionadas desbloqueadas para actualizar.');
      return;
    }

    const applyFieldToNodo = (n: any) => {
      if (n.id == null || n.bloqueado || !rowIdsToUpdate.has(n.id)) return n;
      if (n[field]?.id === user.id) return n;
      return applyRowEmpresaUsuarioFieldUpdate(n, field, user);
    };

    let updated = 0;
    allNodosRef.current = allNodosRef.current.map(n => {
      const next = applyFieldToNodo(n);
      if (next !== n) updated += 1;
      return next;
    });

    setRows(prev => prev.map(r => {
      const next = applyFieldToNodo(r);
      return next === r ? r : next;
    }));

    if (activeEmpresaId != null) {
      setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
        prev.map(emp => {
          if (emp.id !== activeEmpresaId) return emp;
          const actividades = { ...(emp.actividades ?? {}) };
          let changed = false;
          allNodosRef.current.forEach(n => {
            if (n.id == null || n.bloqueado || !rowIdsToUpdate.has(n.id)) return;
            const key = getEmpresaActividadMapKey(n);
            if (key == null) return;
            const prevCfg = actividades[key];
            const nextCfg = markEmpresaActividadModificado(
              mergeEmpresaActividadFromRow(prevCfg, nodoToEmpresaActividadConfig(n)),
            );
            if (
              prevCfg?.usuario?.id === nextCfg.usuario?.id
              && prevCfg?.autoriza?.id === nextCfg.autoriza?.id
              && prevCfg?.habilitado === nextCfg.habilitado
              && prevCfg?.requiereAutorizacion === nextCfg.requiereAutorizacion
              && prevCfg?.asignarAContador === nextCfg.asignarAContador
              && prevCfg?.requiereArchivo === nextCfg.requiereArchivo
            ) {
              return;
            }
            actividades[key] = nextCfg;
            changed = true;
          });
          return changed ? markEmpresaModificado({ ...emp, actividades }) : emp;
        }),
        baseNodosRef.current,
      ));
    }

    const activityConfigsToCopy: Record<string, EmpresaActividadConfig> = {};
    allNodosRef.current.forEach(n => {
      if (n.id == null || !rowIdsToUpdate.has(n.id)) return;
      const key = getEmpresaActividadMapKey(n);
      if (key == null) return;
      activityConfigsToCopy[key] = nodoToEmpresaActividadConfig(n);
    });

    const scopeLabel = `actividades seleccionadas (${rowIdsToUpdate.size}, no bloqueadas)`;
    showToast(
      'success',
      `${fieldLabel} asignado también en ${scopeLabel} `
      + `(${updated} actividad${updated === 1 ? '' : 'es'} actualizada${updated === 1 ? '' : 's'}).`,
    );

    setPendingUsuarioAssign({
      ...pending,
      afterActivitiesStep: true,
      activityConfigsToCopy,
      appliedActivitiesCount: rowIdsToUpdate.size,
    });
  }, [
    pendingUsuarioAssign,
    empresasSeleccionadasConfig,
    checkedRfcEmpresaIds,
    activeEmpresaId,
    selectedRowIds,
    showToast,
  ]);

  const applyUsuarioAssignActivitiesToEmpresas = useCallback(() => {
    const pending = pendingUsuarioAssign;
    if (!pending?.afterActivitiesStep || !pending.activityConfigsToCopy) return;

    const configs = pending.activityConfigsToCopy;
    const activityCount = Object.keys(configs).length;
    if (!activityCount) {
      setPendingUsuarioAssign(null);
      showToast('info', 'No hay actividades para copiar a otras empresas.');
      return;
    }

    const updatesById = copyActivityConfigsToCheckedEmpresas(
      configs,
      empresasSeleccionadasConfig,
      checkedRfcEmpresaIds,
    );

    if (!updatesById.size) {
      setPendingUsuarioAssign(null);
      showToast('info', 'Las empresas seleccionadas ya tienen esas asignaciones.');
      return;
    }

    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => updatesById.get(emp.id) ?? emp),
      baseNodosRef.current,
    ));
    setPendingUsuarioAssign(null);
    showToast(
      'success',
      `Selección de usuarios copiada en ${activityCount} actividad${activityCount === 1 ? '' : 'es'} `
      + `para ${updatesById.size} empresa${updatesById.size === 1 ? '' : 's'} seleccionada${updatesById.size === 1 ? '' : 's'}.`,
    );
  }, [
    pendingUsuarioAssign,
    empresasSeleccionadasConfig,
    checkedRfcEmpresaIds,
    activeEmpresaId,
    showToast,
  ]);

  const requestUsuarioAssignToColumn = useCallback((
    field: 'usuario' | 'autoriza',
    user: any,
  ) => {
    if (!user?.id) return;
    if (activeEmpresaId == null) {
      playUsuarioDropDeniedSound();
      showToast('info', 'Seleccione una empresa en la lista de empresas seleccionadas.');
      return;
    }
    if (activeEmpresaBloqueada) {
      playUsuarioDropDeniedSound();
      showToast('info', 'La empresa activa está bloqueada.');
      return;
    }
    if (!canEditEmpresaActividades) {
      playUsuarioDropDeniedSound();
      showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
      return;
    }
    if (isEmpresaColumnLocked(field)) {
      playUsuarioDropDeniedSound();
      showToast('info', 'La columna está bloqueada.');
      return;
    }

    const rowIds = new Set<number>();
    allNodosRef.current.forEach(n => {
      if (n.id != null && !n.bloqueado) rowIds.add(n.id);
    });
    if (!rowIds.size) {
      playUsuarioDropDeniedSound();
      showToast('info', 'No hay actividades desbloqueadas para actualizar.');
      return;
    }

    playUsuarioDropSound();
    applyUsuarioToActiveRowIds(rowIds, field, user);

    setPendingEmpresaFieldToggle(null);
    setPendingEmpresaPasteAlso(null);
    setPendingUsuarioClear(null);
    setPendingUsuarioAssign({ user, field, targetRow: {}, columnDrop: true });
  }, [
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    applyUsuarioToActiveRowIds,
    showToast,
    isEmpresaColumnLocked,
  ]);

  const applyUsuarioToActividadAllEmpresas = useCallback((
    row: any,
    user: any,
  ) => {
    requestUsuarioAssign(row, 'usuario', user);
  }, [requestUsuarioAssign]);

  const usuarioAssignSelectedActivitiesUnlockedCount = useMemo(
    () => allNodosRef.current.filter(
      n => n.id != null && selectedRowIds.has(n.id) && !n.bloqueado,
    ).length,
    [pendingUsuarioAssign, selectedRowIds, gridVersion],
  );

  const allActivitiesBlocked = useMemo(() => {
    const nodos = allNodosRef.current.filter(n => n.id != null);
    return nodos.length > 0 && nodos.every(n => !!n.bloqueado);
  }, [gridVersion, configReady]);

  const openActivitiesVisibility = useCallback(() => {
    setDraftHiddenActivityRowIds(new Set(hiddenActivityRowIds));
    setDraftLockedActivityRowIds(buildLockedActivityRowIds(baseNodosRef.current));
    setActivitiesVisibilityOpen(true);
  }, [hiddenActivityRowIds]);

  const renderActividadFilterLock = useCallback(() => (
    <button
      type="button"
      style={{
        ...s.actividadLockBtn,
        ...(allActivitiesBlocked ? s.actividadLockBtnActive : s.actividadLockBtnInactive),
      }}
      onClick={openActivitiesVisibility}
      title="Visibilidad y bloqueo de actividades"
      aria-label="Visibilidad y bloqueo de actividades"
      aria-pressed={allActivitiesBlocked}
    >
      <FaLock size={14} />
    </button>
  ), [allActivitiesBlocked, openActivitiesVisibility]);

  const renderUsuarioDropHeader = useCallback((
    label: string,
    field: 'usuario' | 'autoriza',
  ) => (
    <span style={s.empresaColumnHeaderWrap}>
      {renderEmpresaColumnLockButton(field)}
      <UsuarioDropHeader
        label={label}
        field={field}
        onColumnDrop={requestUsuarioAssignToColumn}
        disabled={
          activeEmpresaId == null
          || !canEditEmpresaActividades
          || isEmpresaColumnLocked(field)
        }
      />
    </span>
  ), [
    requestUsuarioAssignToColumn,
    activeEmpresaId,
    canEditEmpresaActividades,
    isEmpresaColumnLocked,
    renderEmpresaColumnLockButton,
  ]);

  const renderUsuarioDropCell = useCallback((
    row: any,
    field: 'usuario' | 'autoriza',
  ) => (
    <UsuarioDropCell
      usuario={row?.[field]}
      row={row}
      field={field}
      onDropRequest={requestUsuarioAssign}
      onClear={requestUsuarioClear}
      clearDisabled={
        activeEmpresaId == null
        || activeEmpresaBloqueada
        || !canEditEmpresaActividades
        || isEmpresaColumnLocked(field)
      }
      dropDisabled={
        !!row?.bloqueado
        || !canEditEmpresaActividades
        || isEmpresaColumnLocked(field)
      }
      dropDisabledTitle={
        isEmpresaColumnLocked(field)
          ? 'Columna bloqueada: no se puede asignar usuario'
          : !canEditEmpresaActividades
            ? EMPRESA_ACTIVIDADES_EDIT_TOAST
            : 'Actividad bloqueada: no se puede asignar usuario'
      }
    />
  ), [
    requestUsuarioAssign,
    requestUsuarioClear,
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    isEmpresaColumnLocked,
  ]);

  const renderCheckboxCell = useCallback((
    row: any,
    field: EmpresaCheckboxField,
  ) => {
    const unset = row[field] == null;
    const disabled =
      !canEditEmpresaActividades
      || activeEmpresaId == null
      || activeEmpresaBloqueada
      || !!row.bloqueado
      || isEmpresaColumnLocked(field);
    const checkboxStyle = unset ? s.checkboxUnset : s.checkbox;
    return (
      <span
        role="presentation"
        onClick={e => {
          e.stopPropagation();
          if (disabled) playUsuarioDropDeniedSound();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          cursor: disabled ? 'not-allowed' : undefined,
        }}
      >
        <input
          type="checkbox"
          checked={!unset && !!row[field]}
          disabled={disabled}
          style={{
            ...checkboxStyle,
            ...(disabled ? { pointerEvents: 'none' } : undefined),
          }}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            if (disabled) return;
            playSelectButtonSound();
            requestEmpresaFieldToggle(row, field, e.target.checked);
          }}
        />
      </span>
    );
  }, [activeEmpresaId, activeEmpresaBloqueada, canEditEmpresaActividades, requestEmpresaFieldToggle, isEmpresaColumnLocked]);

  const toggleNodoBloqueado = useCallback((row: any) => {
    const locking = !row?.bloqueado;
    if (locking) playLockDoorSound();
    else playUnlockDoorSound();
    syncNodoInCache({ ...row, bloqueado: locking }, 'bloqueado');
  }, [syncNodoInCache]);

  const copyEmpresaActividadesById = useCallback(async (empresaId: number) => {
    const empresa = empresasSeleccionadasConfig.find(e => e.id === empresaId);
    if (!empresa) {
      showToast('info', 'Empresa no encontrada.');
      return;
    }
    const nodos = empresaId === activeEmpresaId
      ? allNodosRef.current
      : buildNodosForEmpresa(baseNodosRef.current, empresa);
    const actividades = buildEmpresaActividadesClipboardMap(nodos);
    const payload = { data: { actividades } };
    const ok = await copyEmpresaActividadesToClipboardRobust(payload);
    const count = Object.keys(actividades).length;
    if (ok) {
      unlockPasteClipboard();
      showToast(
        'success',
        `Actividades copiadas al portapapeles (${count} actividad${count === 1 ? '' : 'es'}).`,
      );
    } else {
      showToast('error', 'No se pudo copiar al portapapeles.');
    }
  }, [empresasSeleccionadasConfig, activeEmpresaId, showToast, unlockPasteClipboard]);

  const pasteEmpresaActividadesToActiveEmpresa = useCallback(async (clipboardText?: string) => {
    if (!canEditEmpresaActividades) {
      showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
      return;
    }
    if (activeEmpresaId == null) {
      showToast('info', 'Seleccione una empresa en la lista de empresas seleccionadas.');
      return;
    }

    const empresa = empresasSeleccionadasConfig.find(e => e.id === activeEmpresaId);
    if (!empresa || empresa.bloqueado) {
      showToast('info', 'La empresa seleccionada está bloqueada o no está disponible.');
      return;
    }

    const text = clipboardText ?? await readEmpresaActividadesClipboardTextRobust();
    if (text == null || !text.trim()) {
      showToast('error', 'No se pudo leer el portapapeles.');
      return;
    }

    const pasted = parseEmpresaActividadesClipboardText(text);
    if (!pasted || !Object.keys(pasted).length) {
      lockPasteForInvalidClipboard(text);
      showToast('error', 'Formato inválido: se espera { data: { actividades: { ... } } }.');
      return;
    }

    unlockPasteClipboard();

    const { actividades, applied } = mergePastedEmpresaActividades(
      empresa.actividades ?? {},
      pasted,
      baseNodosRef.current,
    );

    if (!applied) {
      showToast('info', 'Ninguna actividad del portapapeles coincide con este workflow.');
      return;
    }

    const updatedEmpresa = markEmpresaModificado({ ...empresa, actividades });
    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => (emp.id === activeEmpresaId ? updatedEmpresa : emp)),
      baseNodosRef.current,
    ));

    allNodosRef.current = buildNodosForEmpresa(baseNodosRef.current, updatedEmpresa);
    setGridVersion(v => v + 1);

    showToast(
      'success',
      `Actividades pegadas en la empresa seleccionada `
      + `(${applied} actividad${applied === 1 ? '' : 'es'}).`,
    );

    setPendingUsuarioAssign(null);
    setPendingUsuarioClear(null);
    setPendingEmpresaFieldToggle(null);
    setPendingEmpresaPasteAlso({ pasted, appliedActivitiesCount: applied });
  }, [
    empresasSeleccionadasConfig,
    activeEmpresaId,
    canEditEmpresaActividades,
    showToast,
    lockPasteForInvalidClipboard,
    unlockPasteClipboard,
  ]);

  const applyEmpresaPasteToCheckedEmpresas = useCallback(() => {
    const pending = pendingEmpresaPasteAlso;
    if (!pending) return;

    const { updatesById, activitiesApplied } = applyPastedActividadesToCheckedEmpresas(
      pending.pasted,
      empresasSeleccionadasConfig,
      checkedRfcEmpresaIds,
      baseNodosRef.current,
      activeEmpresaId,
    );

    if (!updatesById.size) {
      setPendingEmpresaPasteAlso(null);
      showToast('info', 'Las empresas seleccionadas ya tienen esas actividades o no hay otras aplicables.');
      return;
    }

    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => updatesById.get(emp.id) ?? emp),
      baseNodosRef.current,
    ));
    setPendingEmpresaPasteAlso(null);
    showToast(
      'success',
      `Actividades pegadas en ${updatesById.size} empresa${updatesById.size === 1 ? '' : 's'} `
      + `(${activitiesApplied} actividad${activitiesApplied === 1 ? '' : 'es'} por empresa).`,
    );
  }, [
    pendingEmpresaPasteAlso,
    empresasSeleccionadasConfig,
    checkedRfcEmpresaIds,
    activeEmpresaId,
    showToast,
  ]);

  const handlePasteEmpresaActividades = useCallback((clipboardText?: string) => {
    void pasteEmpresaActividadesToActiveEmpresa(clipboardText);
  }, [pasteEmpresaActividadesToActiveEmpresa]);

  const renderActividadCell = useCallback((row: any) => {
    const label = row?.profaseActividad?.descripcion ?? row?.actividad?.descripcion ?? row?.data?.label ?? '—';
    const bloqueado = !!row?.bloqueado;
    const modificado = !!row?.modificado;
    const politicaMail = resolveNodoPoliticaMail(row);
    const requiereMail = isMailRequeridoParaTerminar(politicaMail);
    const mailTitle = requiereMail
      ? 'Mail requerido para terminar'
      : 'Mail no requerido para terminar';

    return (
      <span style={s.actividadCell}>
        <button
          type="button"
          style={{
            ...s.actividadLockBtn,
            ...(bloqueado ? s.actividadLockBtnActive : s.actividadLockBtnInactive),
          }}
          onClick={e => {
            e.stopPropagation();
            toggleNodoBloqueado(row);
          }}
          title={bloqueado ? 'Desbloquear actividad' : 'Bloquear actividad'}
          aria-label={bloqueado ? 'Desbloquear actividad' : 'Bloquear actividad'}
          aria-pressed={bloqueado}
        >
          <FaLock size={14} />
        </button>
        <span
          title={mailTitle}
          aria-label={mailTitle}
          style={{
            ...s.actividadMailIcon,
            ...(requiereMail ? s.actividadMailIconRequired : s.actividadMailIconOptional),
            opacity: mailIconOpacity(politicaMail),
          }}
        >
          <FaEnvelope size={14} aria-hidden />
        </span>
        <ActividadNameDropZone
          label={label}
          row={row}
          actividadBloqueada={bloqueado}
          dropDisabled={!canEditEmpresaActividades}
          modificado={modificado}
          onDropOnActividad={applyUsuarioToActividadAllEmpresas}
        />
      </span>
    );
  }, [toggleNodoBloqueado, applyUsuarioToActividadAllEmpresas, canEditEmpresaActividades]);

  const toggleRowSelected = useCallback((rowId: number, checked: boolean) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  }, []);

  const renderSelectedCheckbox = useCallback((row: any) => {
    const rowId = row?.id;
    if (rowId == null) return null;
    return (
      <input
        type="checkbox"
        checked={selectedRowIds.has(rowId)}
        style={s.checkbox}
        onClick={e => e.stopPropagation()}
        onChange={e => toggleRowSelected(rowId, e.target.checked)}
        aria-label="Seleccionar fila"
      />
    );
  }, [selectedRowIds, toggleRowSelected]);

  const renderSelectAllGridHeader = useCallback(() => {
    const allIds = allNodosRef.current
      .map(n => n.id)
      .filter((rowId): rowId is number => rowId != null && !hiddenActivityRowIds.has(rowId));
    const allSelected = allIds.length > 0 && allIds.every(rowId => selectedRowIds.has(rowId));
    const someSelected = allIds.some(rowId => selectedRowIds.has(rowId)) && !allSelected;

    return (
      <SelectAllCheckbox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={checked => {
          setSelectedRowIds(checked ? new Set(allIds) : new Set());
        }}
        ariaLabel="Seleccionar todas las actividades"
        style={s.checkbox}
      />
    );
  }, [selectedRowIds, hiddenActivityRowIds, configReady, gridVersion]);

  const applyEmpresaCheckboxFieldBulk = useCallback((
    field: EmpresaCheckboxField,
    value: boolean,
  ) => {
    if (activeEmpresaId == null) {
      showToast('info', 'Seleccione una empresa en la lista de empresas seleccionadas.');
      return;
    }
    if (activeEmpresaBloqueada) {
      showToast('info', 'La empresa activa está bloqueada.');
      return;
    }
    if (!canEditEmpresaActividades) {
      showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
      return;
    }
    if (isEmpresaColumnLocked(field)) {
      showToast('info', 'La columna está bloqueada.');
      return;
    }

    const updates = new Map<number, any>();
    for (const row of rows) {
      if (row?.id == null || row.bloqueado) continue;
      if (row[field] === value) continue;
      updates.set(row.id, applyRowEmpresaCheckboxUpdate(row, field, value));
    }
    if (!updates.size) return;

    playSelectButtonSound();

    allNodosRef.current = allNodosRef.current.map(n => (
      n.id != null && updates.has(n.id) ? updates.get(n.id) : n
    ));
    setRows(prev => prev.map(r => (
      r.id != null && updates.has(r.id) ? updates.get(r.id) : r
    )));

    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => {
        if (emp.id !== activeEmpresaId) return emp;
        const actividades = { ...(emp.actividades ?? {}) };
        updates.forEach(updated => {
          const actividadKey = getEmpresaActividadMapKey(updated);
          if (actividadKey != null) {
            actividades[actividadKey] = markEmpresaActividadModificado(
              mergeEmpresaActividadFromRow(
                actividades[actividadKey],
                nodoToEmpresaActividadConfig(updated),
              ),
            );
          }
        });
        return markEmpresaModificado({ ...emp, actividades });
      }),
      baseNodosRef.current,
    ));
    setGridVersion(v => v + 1);

    const otherEmpresasCount = empresasSeleccionadasConfig.filter(
      e => !e.bloqueado && e.id !== activeEmpresaId,
    ).length;
    if (otherEmpresasCount === 0) return;

    const bulkActivityUpdates: Array<{ actividadKey: string; nodoId: number }> = [];
    updates.forEach(updated => {
      const actividadKey = getEmpresaActividadMapKey(updated);
      if (actividadKey == null) return;
      bulkActivityUpdates.push({
        actividadKey,
        nodoId: empresaActividadNodoIdForApi(updated),
      });
    });
    if (!bulkActivityUpdates.length) return;

    setPendingUsuarioAssign(null);
    setPendingUsuarioClear(null);
    setPendingEmpresaPasteAlso(null);
    setPendingEmpresaFieldToggle({
      field,
      value,
      bulkActivityUpdates,
    });
  }, [
    rows,
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    empresasSeleccionadasConfig,
    showToast,
    isEmpresaColumnLocked,
  ]);

  const renderHabilitadoActividadHeader = useCallback(() => (
    <span>Habilitado</span>
  ), []);

  const renderHabilitadoActividadCell = useCallback((row: any) => {
    const unset = row?.habilitado == null;
    const enabled = !unset && row.habilitado === true;
    const label = unset ? 'Sin definir' : enabled ? 'Habilitado' : 'Deshabilitado';
    return (
      <span
        style={s.habilitadoActividadStatus}
        title={label}
        role="img"
        aria-label={label}
      >
        <FaCheckCircle
          size={16}
          style={{ color: enabled ? '#16a34a' : '#94a3b8', flexShrink: 0 }}
          aria-hidden
        />
        <span style={{
          ...s.habilitadoActividadLabel,
          ...(enabled ? {} : s.habilitadoActividadLabelDisabled),
        }}
        >
          {label}
        </span>
      </span>
    );
  }, []);

  const applyHabilitadoEmpresaBulk = useCallback((value: HabilitadoEmpresaSelectValue) => {
    if (activeEmpresaId == null) {
      showToast('info', 'Seleccione una empresa en la lista de empresas seleccionadas.');
      return;
    }
    if (activeEmpresaBloqueada) {
      showToast('info', 'La empresa activa está bloqueada.');
      return;
    }
    if (!canEditEmpresaActividades) {
      showToast('info', EMPRESA_ACTIVIDADES_EDIT_TOAST);
      return;
    }
    if (isEmpresaColumnLocked('habilitadoEmpresa')) {
      showToast('info', 'La columna está bloqueada.');
      return;
    }

    const updates = new Map<number, any>();
    for (const row of rows) {
      if (row?.id == null || row.bloqueado) continue;
      if (habilitadoEmpresaRowsEqual(row.habilitadoEmpresa, value)) continue;
      updates.set(row.id, applyRowHabilitadoEmpresaUpdate(row, value));
    }
    if (!updates.size) return;

    playSelectButtonSound();

    allNodosRef.current = allNodosRef.current.map(n => (
      n.id != null && updates.has(n.id) ? updates.get(n.id) : n
    ));
    setRows(prev => prev.map(r => (
      r.id != null && updates.has(r.id) ? updates.get(r.id) : r
    )));

    setEmpresasSeleccionadasConfig(prev => mapEmpresasConfigWithWarning(
      prev.map(emp => {
        if (emp.id !== activeEmpresaId) return emp;
        const actividades = { ...(emp.actividades ?? {}) };
        updates.forEach(updated => {
          const actividadKey = getEmpresaActividadMapKey(updated);
          if (actividadKey != null) {
            actividades[actividadKey] = markEmpresaActividadModificado(
              mergeEmpresaActividadFromRow(
                actividades[actividadKey],
                nodoToEmpresaActividadConfig(updated),
              ),
            );
          }
        });
        return markEmpresaModificado({ ...emp, actividades });
      }),
      baseNodosRef.current,
    ));
    setGridVersion(v => v + 1);

    const otherEmpresasCount = empresasSeleccionadasConfig.filter(
      e => !e.bloqueado && e.id !== activeEmpresaId,
    ).length;
    if (otherEmpresasCount === 0) return;

    const bulkActivityUpdates: Array<{ actividadKey: string; nodoId: number }> = [];
    updates.forEach(updated => {
      const actividadKey = getEmpresaActividadMapKey(updated);
      if (actividadKey == null) return;
      bulkActivityUpdates.push({
        actividadKey,
        nodoId: empresaActividadNodoIdForApi(updated),
      });
    });
    if (!bulkActivityUpdates.length) return;

    setPendingUsuarioAssign(null);
    setPendingUsuarioClear(null);
    setPendingEmpresaPasteAlso(null);
    setPendingEmpresaFieldToggle({
      field: 'habilitadoEmpresa',
      value,
      bulkActivityUpdates,
    });
  }, [
    rows,
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    empresasSeleccionadasConfig,
    showToast,
    isEmpresaColumnLocked,
  ]);

  const renderHabilitadoEmpresaHeader = useCallback(() => {
    const eligible = rows.filter(r => r?.id != null && !r.bloqueado);
    const columnLocked = isEmpresaColumnLocked('habilitadoEmpresa');
    const disabled = !canEditEmpresaActividades
      || activeEmpresaId == null
      || activeEmpresaBloqueada
      || eligible.length === 0
      || columnLocked;

    return (
      <>
        {renderEmpresaColumnLockButton('habilitadoEmpresa')}
        <HabilitadoEmpresaSelect
          value=""
          disabled={disabled}
          bulkPlaceholder
          onChange={v => applyHabilitadoEmpresaBulk(v)}
        />
        <span>Habilitada</span>
      </>
    );
  }, [
    rows,
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    applyHabilitadoEmpresaBulk,
    isEmpresaColumnLocked,
    renderEmpresaColumnLockButton,
  ]);

  const renderHabilitadoEmpresaCell = useCallback((row: any) => {
    const disabled = !canEditEmpresaActividades
      || activeEmpresaId == null
      || activeEmpresaBloqueada
      || !!row.bloqueado
      || isEmpresaColumnLocked('habilitadoEmpresa');
    return (
      <span
        role="presentation"
        onClick={e => {
          e.stopPropagation();
          if (disabled) playUsuarioDropDeniedSound();
        }}
        style={{
          display: 'inline-flex',
          width: '100%',
          minWidth: 0,
          cursor: disabled ? 'not-allowed' : undefined,
        }}
      >
        <HabilitadoEmpresaSelect
          value={habilitadoEmpresaToSelectValue(row.habilitadoEmpresa)}
          disabled={disabled}
          onChange={v => {
            if (disabled) return;
            playSelectButtonSound();
            requestHabilitadoEmpresaChange(row, v);
          }}
        />
      </span>
    );
  }, [activeEmpresaId, activeEmpresaBloqueada, canEditEmpresaActividades, requestHabilitadoEmpresaChange, isEmpresaColumnLocked]);

  const renderAsignarAContadorEmpresaHeader = useCallback(() => {
    const eligible = rows.filter(r => r?.id != null && !r.bloqueado);
    const allChecked = eligible.length > 0
      && eligible.every(r => r.asignarAContadorEmpresa === true);
    const someChecked = eligible.some(r => r.asignarAContadorEmpresa === true) && !allChecked;
    const disabled = !canEditEmpresaActividades
      || activeEmpresaId == null
      || activeEmpresaBloqueada
      || eligible.length === 0
      || isEmpresaColumnLocked('asignarAContadorEmpresa');

    return (
      <>
        {renderEmpresaColumnLockButton('asignarAContadorEmpresa')}
        <SelectAllCheckbox
          checked={allChecked}
          indeterminate={someChecked}
          disabled={disabled}
          onChange={checked => applyEmpresaCheckboxFieldBulk('asignarAContadorEmpresa', checked)}
          ariaLabel="Marcar asignar a contador en todas las actividades visibles para la empresa"
          style={s.checkbox}
        />
        <span>Asig. contador</span>
      </>
    );
  }, [
    rows,
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    applyEmpresaCheckboxFieldBulk,
    isEmpresaColumnLocked,
    renderEmpresaColumnLockButton,
  ]);

  const renderRequiereAutorizacionEmpresaHeader = useCallback(() => {
    const eligible = rows.filter(r => r?.id != null && !r.bloqueado);
    const allChecked = eligible.length > 0
      && eligible.every(r => r.requiereAutorizacionEmpresa === true);
    const someChecked = eligible.some(r => r.requiereAutorizacionEmpresa === true) && !allChecked;
    const disabled = !canEditEmpresaActividades
      || activeEmpresaId == null
      || activeEmpresaBloqueada
      || eligible.length === 0
      || isEmpresaColumnLocked('requiereAutorizacionEmpresa');

    return (
      <>
        {renderEmpresaColumnLockButton('requiereAutorizacionEmpresa')}
        <SelectAllCheckbox
          checked={allChecked}
          indeterminate={someChecked}
          disabled={disabled}
          onChange={checked => applyEmpresaCheckboxFieldBulk('requiereAutorizacionEmpresa', checked)}
          ariaLabel="Marcar requiere autorización en todas las actividades visibles para la empresa"
          style={s.checkbox}
        />
        <span>Req. Autoriz.</span>
      </>
    );
  }, [
    rows,
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    applyEmpresaCheckboxFieldBulk,
    isEmpresaColumnLocked,
    renderEmpresaColumnLockButton,
  ]);

  const renderRequiereArchivoEmpresaHeader = useCallback(() => {
    const eligible = rows.filter(r => r?.id != null && !r.bloqueado);
    const allChecked = eligible.length > 0
      && eligible.every(r => r.requiereArchivoEmpresa === true);
    const someChecked = eligible.some(r => r.requiereArchivoEmpresa === true) && !allChecked;
    const disabled = !canEditEmpresaActividades
      || activeEmpresaId == null
      || activeEmpresaBloqueada
      || eligible.length === 0
      || isEmpresaColumnLocked('requiereArchivoEmpresa');

    return (
      <>
        {renderEmpresaColumnLockButton('requiereArchivoEmpresa')}
        <SelectAllCheckbox
          checked={allChecked}
          indeterminate={someChecked}
          disabled={disabled}
          onChange={checked => applyEmpresaCheckboxFieldBulk('requiereArchivoEmpresa', checked)}
          ariaLabel="Marcar requiere archivo en todas las actividades visibles para la empresa"
          style={s.checkbox}
        />
        <span>Req. Archivo</span>
      </>
    );
  }, [
    rows,
    activeEmpresaId,
    activeEmpresaBloqueada,
    canEditEmpresaActividades,
    applyEmpresaCheckboxFieldBulk,
    isEmpresaColumnLocked,
    renderEmpresaColumnLockButton,
  ]);

  const activityVisibilityItems = useMemo(
    () => buildActivityVisibilityItems(baseNodosRef.current),
    [configReady, gridVersion],
  );

  const toggleDraftActivityVisibility = useCallback((rowId: number) => {
    let hiding = false;
    setDraftHiddenActivityRowIds(prev => {
      hiding = !prev.has(rowId);
      const next = new Set(prev);
      if (hiding) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
    if (hiding) {
      setDraftLockedActivityRowIds(prev => {
        const next = new Set(prev);
        next.add(rowId);
        return next;
      });
    }
  }, []);

  const toggleDraftActivityLocked = useCallback((rowId: number) => {
    setDraftLockedActivityRowIds(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const showAllDraftActivities = useCallback(() => {
    setDraftHiddenActivityRowIds(new Set());
  }, []);

  const hideAllDraftActivities = useCallback(() => {
    const allIds = activityVisibilityItems.map(a => a.id);
    setDraftHiddenActivityRowIds(new Set(allIds));
    setDraftLockedActivityRowIds(new Set(allIds));
  }, [activityVisibilityItems]);

  const lockAllDraftActivities = useCallback(() => {
    setDraftLockedActivityRowIds(new Set(activityVisibilityItems.map(a => a.id)));
  }, [activityVisibilityItems]);

  const unlockAllDraftActivities = useCallback(() => {
    setDraftLockedActivityRowIds(new Set());
  }, []);

  const applyActivitiesVisibility = useCallback(() => {
    const prevLocked = buildLockedActivityRowIds(baseNodosRef.current);
    const nextLocked = draftLockedActivityRowIds;
    const anyNewlyLocked = Array.from(nextLocked).some(id => !prevLocked.has(id));
    const anyNewlyUnlocked = Array.from(prevLocked).some(id => !nextLocked.has(id));
    if (anyNewlyLocked) playLockDoorSound();
    else if (anyNewlyUnlocked) playUnlockDoorSound();

    const applyRowState = (n: any) => {
      if (n.id == null) return n;
      return { ...n, bloqueado: draftLockedActivityRowIds.has(n.id) };
    };

    allNodosRef.current = allNodosRef.current.map(applyRowState);
    baseNodosRef.current = baseNodosRef.current.map(applyRowState);
    setRows(prev => prev.map(applyRowState));

    setHiddenActivityRowIds(new Set(draftHiddenActivityRowIds));
    setSelectedRowIds(prev => {
      const next = new Set<number>();
      prev.forEach(rowId => {
        if (!draftHiddenActivityRowIds.has(rowId)) next.add(rowId);
      });
      return next;
    });
    setActivitiesVisibilityOpen(false);
    setGridVersion(v => v + 1);
  }, [draftHiddenActivityRowIds, draftLockedActivityRowIds]);

  const closeActivitiesVisibility = useCallback(() => {
    setActivitiesVisibilityOpen(false);
    setDraftHiddenActivityRowIds(new Set(hiddenActivityRowIds));
    setDraftLockedActivityRowIds(buildLockedActivityRowIds(baseNodosRef.current));
  }, [hiddenActivityRowIds]);

  return (
    <div style={s.page}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Workflow', to: APP_LIST, icon: <FaProjectDiagram /> },
        { label: 'Configuración de Plantillas Workflow', icon: <FaCog /> },
      ]} />

      <div style={s.toolbar}>
        <PageHeader
          icon={<FaCog />}
          iconColor="#10b981"
          title="Configuración de Plantillas Workflow"
          subtitle={pageSubtitle}
        />
        <div style={s.toolbarActions}>
          <button
            type="button"
            style={s.btnDesign}
            onClick={() => id && requestLeaveTo({ kind: 'spa', to: `/workflow/${id}/diseno` })}
          >
            <FaDraftingCompass style={{ marginRight: 6 }} />
            Diseño
          </button>
          <button
            type="button"
            style={{
              ...s.btnCalendar,
              ...(periodosWarning ? s.btnCalendarWarning : {}),
            }}
            onClick={() => id && navigate(workflowConfigPeriodoPath(id, location.search))}
            disabled={!configReady || loading || !id}
            title={periodosWarning ?? 'Asociar periodos actividad al workflow'}
            aria-label={periodosWarning ? `Periodo. Advertencia: ${periodosWarning}` : 'Periodo'}
          >
            <FaCalendarAlt style={{ marginRight: 6 }} />
            Periodo
            {periodosWarning ? (
              <span style={s.btnCalendarWarningBadge} aria-hidden>
                <FaExclamationTriangle size={10} />
              </span>
            ) : null}
          </button>
          <button
            type="button"
            style={{
              ...s.btnEdit,
              ...(empresaActividadesEditing ? s.btnEditActive : {}),
            }}
            onClick={() => setEmpresaActividadesEditing(prev => !prev)}
            aria-pressed={empresaActividadesEditing}
            title={
              empresaActividadesEditing
                ? 'Volver a vista (desactivar edición)'
                : 'Habilitar edición de actividades de empresa'
            }
          >
            <FaEdit style={{ marginRight: 6 }} />
            {empresaActividadesEditing ? 'Vista' : 'Editar'}
          </button>
          <button
            type="button"
            style={{
              ...s.btnSave,
              ...((!configReady || loading || (saveEmpresaCount === 0 && !isDraft)) ? s.btnSaveDisabled : {}),
            }}
            onClick={confirmSaveConfig}
            disabled={!configReady || loading || (saveEmpresaCount === 0 && !isDraft)}
          >
            <FaSave style={{ marginRight: 6 }} />
            Guardar
          </button>
          <button type="button" style={s.btnBack} onClick={() => requestLeaveTo({ kind: 'spa', to: APP_LIST })}>
            Volver
          </button>
        </div>
      </div>

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      {loading && (
        <div style={s.configLoadingWrap} role="status" aria-live="polite" aria-busy="true">
          <span style={s.configLoadingText}>Cargando</span>
          <div className="profase-indeterminate-progress" aria-hidden />
        </div>
      )}

      <WorkflowSearchModal
        open={workflowSearchOpen}
        currentWorkflowId={id}
        onClose={() => setWorkflowSearchOpen(false)}
        onNavigateToConfig={path => requestLeaveTo({ kind: 'spa', to: path })}
      />

      {id ? (
        <WorkflowCalendarioPeriodoModal
          open={calendarioPeriodoOpen}
          workflowId={Number(id)}
          workflowNombre={workflowInfo?.nombre}
          workflowPeriodicidad={workflowInfo?.periodicidad ?? null}
          assignedPeriodoIds={periodosConfig.map(p => p.id)}
          onApply={setPeriodosConfig}
          onClose={() => id && navigate(workflowConfigPath(id, location.search))}
          showToast={showToast}
        />
      ) : null}

      <WorkflowActivitiesVisibilityModal
        open={activitiesVisibilityOpen}
        activities={activityVisibilityItems}
        draftHiddenIds={draftHiddenActivityRowIds}
        draftLockedIds={draftLockedActivityRowIds}
        onToggleVisible={toggleDraftActivityVisibility}
        onToggleLocked={toggleDraftActivityLocked}
        onShowAll={showAllDraftActivities}
        onHideAll={hideAllDraftActivities}
        onLockAll={lockAllDraftActivities}
        onUnlockAll={unlockAllDraftActivities}
        onApply={applyActivitiesVisibility}
        onClose={closeActivitiesVisibility}
      />

      {!loading && (
        <WorkflowConfigEmpresasSection
          key={id}
          workflowId={id}
          workflowPeriodicidad={workflowInfo?.periodicidad ?? null}
          initialConfigUsuarios={configUsuarios}
          initialSelectedEmpresas={empresasSeleccionadasConfig}
          activeEmpresaId={activeEmpresaId}
          onSelectedEmpresasChange={handleSelectedEmpresasChange}
          onActiveEmpresaChange={handleActiveEmpresaChange}
          onActiveEmpresaDetailChange={setActiveEmpresa}
          onCopyEmpresaActividades={empresaId => { void copyEmpresaActividadesById(empresaId); }}
          onPasteEmpresaActividades={handlePasteEmpresaActividades}
          pasteClipboardInvalidLocked={pasteClipboardInvalidLocked}
          empresaActividadesEditing={empresaActividadesEditing}
          onCheckedRfcIdsChange={handleCheckedRfcIdsChange}
          onHydrationSettled={finishConfigHydration}
          showToast={showToast}
        />
      )}

      {(pendingUsuarioAssign || pendingUsuarioClear || pendingEmpresaFieldToggle || pendingEmpresaPasteAlso) && (
        <div ref={assignConfirmRef}>
          {pendingEmpresaPasteAlso ? (
            <EmpresaPasteAlsoConfirm
              pending={pendingEmpresaPasteAlso}
              checkedRfcCount={checkedRfcEmpresaIds.size}
              pasteAlsoEmpresasApplicableCount={pasteEmpresaAlsoApplicableCount}
              onApply={applyEmpresaPasteToCheckedEmpresas}
              onDismiss={() => setPendingEmpresaPasteAlso(null)}
            />
          ) : pendingUsuarioClear ? (
            <UsuarioClearAlsoConfirm
              pending={pendingUsuarioClear}
              checkedRfcCount={checkedRfcEmpresaIds.size}
              clearAlsoEmpresasApplicableCount={usuarioClearAlsoApplicableCount}
              onApply={applyUsuarioClearToCheckedEmpresas}
              onDismiss={() => setPendingUsuarioClear(null)}
            />
          ) : pendingEmpresaFieldToggle ? (
            <EmpresaFieldToggleScopeConfirm
              pending={pendingEmpresaFieldToggle}
              rfcEmpresaCount={rfcEmpresaIds.length}
              rfcEmpresaAlsoApplicableCount={rfcEmpresaAlsoApplicableCount}
              checkedRfcCount={checkedRfcEmpresaIds.size}
              checkedRfcAlsoApplicableCount={checkedRfcAlsoApplicableCount}
              onApply={applyEmpresaFieldToggle}
              onDismiss={() => setPendingEmpresaFieldToggle(null)}
            />
          ) : pendingUsuarioAssign ? (
            pendingUsuarioAssign.afterActivitiesStep ? (
              <UsuarioAssignAfterActivitiesEmpresasConfirm
                pending={pendingUsuarioAssign}
                checkedRfcCount={checkedRfcEmpresaIds.size}
                checkedRfcAlsoApplicableCount={checkedRfcAlsoApplicableCount}
                onApply={applyUsuarioAssignActivitiesToEmpresas}
                onDismiss={() => setPendingUsuarioAssign(null)}
              />
            ) : (
              <UsuarioAssignAlsoScopeConfirm
                pending={pendingUsuarioAssign}
                selectedActivitiesUnlockedCount={usuarioAssignSelectedActivitiesUnlockedCount}
                checkedRfcCount={checkedRfcEmpresaIds.size}
                checkedRfcAlsoApplicableCount={checkedRfcAlsoApplicableCount}
                columnDrop={pendingUsuarioAssign.columnDrop}
                onApply={applyUsuarioAssignAlso}
                onDismiss={() => setPendingUsuarioAssign(null)}
              />
            )
          ) : null}
        </div>
      )}

      {configReady && (
        <WorkflowConfigActivitiesTable
          gridVersion={gridVersion}
          rows={rows}
          loading={loading}
          workflowName={displayWorkflowName}
          activeEmpresaGroupHeader={activeEmpresaGroupHeader}
          onWorkflowSearch={() => setWorkflowSearchOpen(true)}
          disenoPersonalizadoEnabled={disenoPersonalizadoEnabled}
          disenoPersonalizadoDisabled={empresaActividadesEditing}
          onDisenoPersonalizado={handleDisenoPersonalizado}
          filters={WORKFLOW_ACTIVIDAD_FILTERS}
          onFetch={fetchData}
          renderSelectAllHeader={renderSelectAllGridHeader}
          renderHabilitadoActividadHeader={renderHabilitadoActividadHeader}
          renderHabilitadoActividadCell={renderHabilitadoActividadCell}
          renderHabilitadoEmpresaHeader={renderHabilitadoEmpresaHeader}
          renderHabilitadoEmpresaCell={renderHabilitadoEmpresaCell}
          renderAsignarAContadorEmpresaHeader={renderAsignarAContadorEmpresaHeader}
          renderRequiereAutorizacionEmpresaHeader={renderRequiereAutorizacionEmpresaHeader}
          renderRequiereArchivoEmpresaHeader={renderRequiereArchivoEmpresaHeader}
          onOpenActivitiesVisibility={openActivitiesVisibility}
          hasHiddenActivities={hiddenActivityRowIds.size > 0}
          hiddenActivitiesCount={hiddenActivityRowIds.size}
          renderActividadFilterPrefix={renderActividadFilterLock}
          renderSelectCell={renderSelectedCheckbox}
          renderActividadCell={renderActividadCell}
          renderCheckbox={renderCheckboxCell}
          renderUsuarioHeader={renderUsuarioDropHeader}
          renderUsuarioCell={renderUsuarioDropCell}
        />
      )}
    </div>
  );
}

const assignConfirmS: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    padding: '16px 20px',
    borderRadius: 10,
    border: '1px solid var(--crm-border)',
    background: 'var(--crm-surface-muted)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
  },
  icon: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: '#2563eb',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: { flex: 1, minWidth: 0 },
  title: {
    margin: '0 0 4px',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--crm-text-primary)',
  },
  message: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
    lineHeight: 1.5,
  },
  fieldLabel: {
    display: 'block',
    margin: '0 0 6px',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--crm-text-primary)',
  },
  select: {
    width: '100%',
    maxWidth: 420,
    marginBottom: 12,
    fontSize: 13,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid var(--crm-border)',
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
  },
  fieldError: {
    margin: '0 0 12px',
    fontSize: 12,
    color: '#dc2626',
  },
  fieldHint: {
    margin: '0 0 12px',
    fontSize: 12,
    color: 'var(--crm-text-muted)',
    fontStyle: 'italic',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  btnScope: {
    padding: '8px 14px',
    background: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnCancel: {
    padding: '8px 14px',
    background: 'var(--crm-surface)',
    color: 'var(--crm-text-secondary)',
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
  configLoadingWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '8px 0 4px',
  },
  configLoadingText: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--crm-text-muted, #64748b)',
  },
  configStatusUnchanged: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 8px',
    borderRadius: 999,
    background: '#ecfdf5',
    color: '#047857',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.02,
  },
  configStatusModified: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 8px',
    borderRadius: 999,
    background: '#fef3c7',
    color: '#92400e',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.02,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  btnDesign: {
    display: 'flex',
    alignItems: 'center',
    padding: '9px 18px',
    background: '#7c3aed',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(124,58,237,0.25)',
  },
  btnCalendar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '9px 18px',
    background: '#0d9488',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(13,148,136,0.25)',
  },
  btnCalendarWarning: {
    boxShadow: '0 2px 8px rgba(217,119,6,0.35)',
  },
  btnCalendarWarningBadge: {
    position: 'absolute',
    top: -7,
    right: -7,
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#f59e0b',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 2px #fff',
    pointerEvents: 'none',
  },
  btnEdit: {
    display: 'flex',
    alignItems: 'center',
    padding: '9px 18px',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
  },
  btnEditActive: {
    background: '#1d4ed8',
    boxShadow: '0 0 0 2px #fff, 0 0 0 4px #2563eb',
  },
  btnSave: {
    display: 'flex',
    alignItems: 'center',
    padding: '9px 18px',
    background: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(16,185,129,0.25)',
  },
  btnSaveDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  btnBack: {
    display: 'flex',
    alignItems: 'center',
    padding: '9px 18px',
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  descripcion: { color: 'var(--crm-grid-cell-text)' },
  actividadCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    width: '100%',
  },
  habilitadoActividadStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: '100%',
    minHeight: 28,
  },
  habilitadoActividadLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--crm-grid-cell-text)',
  },
  habilitadoActividadLabelDisabled: {
    color: '#94a3b8',
  },
  actividadLockBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 28,
    height: 28,
    padding: 0,
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    cursor: 'pointer',
  },
  actividadLockBtnActive: {
    color: '#d97706',
  },
  actividadLockBtnInactive: {
    color: 'var(--crm-text-muted)',
    opacity: 0.55,
  },
  actividadMailIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  actividadMailIconRequired: {
    color: '#2563eb',
  },
  actividadMailIconOptional: {
    color: 'var(--crm-text-muted, #94a3b8)',
  },
  empresaColumnHeaderWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    minWidth: 0,
  },
  checkbox: {
    width: 16,
    height: 16,
    accentColor: '#10b981',
    cursor: 'pointer',
  },
  checkboxUnset: {
    width: 16,
    height: 16,
    accentColor: '#cbd5e1',
    cursor: 'pointer',
    opacity: 0.55,
  },
};

const empresaS: Record<string, React.CSSProperties> = {
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr 200px',
    gap: 16,
    alignItems: 'stretch',
  },
  rowWithoutUsers: {
    gridTemplateColumns: '1fr auto 1fr',
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  },
  usersPanel: {
    width: 200,
    maxWidth: 200,
    flexShrink: 0,
  },
  panelTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--crm-text-primary)',
  },
  panelTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  listCountFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    color: 'var(--crm-text-muted)',
    paddingTop: 2,
  },
  listCountFooterIcon: {
    flexShrink: 0,
    color: 'var(--crm-text-muted)',
  },
  rfcHeaderBtn: {
    padding: '6px 10px',
    background: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  rfcHeaderLockBtn: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-surface)',
    cursor: 'pointer',
  },
  rfcHeaderLockBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  rfcHeaderVisibilityBtn: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-surface)',
    color: 'var(--crm-text-secondary)',
    cursor: 'pointer',
  },
  rfcHeaderVisibilityBtnActive: {
    color: '#10b981',
    borderColor: '#86efac',
  },
  listBox: {
    height: EMPRESA_LIST_VIEW_HEIGHT,
    minHeight: EMPRESA_LIST_VIEW_HEIGHT,
    flexShrink: 0,
    overflow: 'hidden',
    background: 'var(--crm-surface)',
    color: 'var(--crm-text-primary)',
    border: '1px solid var(--crm-border)',
    borderRadius: 10,
    padding: 6,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    transition: 'outline 0.12s, background 0.12s',
  },
  listBoxDragOver: {
    outline: '2px solid #22c55e',
    outlineOffset: -2,
    background: 'var(--crm-surface-hover)',
  },
  usersListBox: {
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
  },
  listItemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  listCheckbox: {
    width: 16,
    height: 16,
    flexShrink: 0,
    accentColor: '#10b981',
    cursor: 'pointer',
  },
  listCheckboxItem: {
    width: 16,
    height: 16,
    flexShrink: 0,
    accentColor: '#10b981',
    cursor: 'pointer',
  },
  listItemText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    width: '100%',
    textAlign: 'left',
  },
  listItemPrimary: {
    fontSize: 13,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listItemPrimaryModificado: {
    fontStyle: 'italic',
  },
  listItemAlias: {
    fontSize: 11,
    color: '#60a5fa',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listItemAliasActive: {
    color: 'var(--crm-panel-list-active-alias)',
  },
  lockBtn: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    padding: 0,
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    cursor: 'pointer',
  },
  lockBtnActive: {
    color: '#f59e0b',
  },
  lockBtnInactive: {
    color: 'var(--crm-text-muted)',
    opacity: 0.4,
  },
  warningIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: 6,
    pointerEvents: 'none',
    userSelect: 'none',
  },
  warningIconActive: {
    color: '#d97706',
    opacity: 1,
  },
  warningIconDisabled: {
    color: 'var(--crm-text-muted)',
    opacity: 0.35,
  },
  instanciaFirefighterIcon: {
    fontSize: 16,
    lineHeight: 1,
    opacity: 1,
  },
  instanciaIconDisabled: {
    opacity: 0.35,
    cursor: 'not-allowed',
  },
  disenoBtnActive: {
    color: '#ec4899',
    borderColor: '#ec4899',
    opacity: 1,
  },
  disenoBtnInactive: {
    color: 'var(--crm-text-muted)',
    opacity: 0.4,
  },
  copyBtn: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    padding: 0,
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    color: 'var(--crm-text-secondary)',
    cursor: 'pointer',
  },
  listItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    border: '1px solid transparent',
    borderRadius: 6,
    background: 'transparent',
    color: 'var(--crm-text-primary)',
    fontSize: 13,
    cursor: 'pointer',
  },
  listItemDraggable: {
    cursor: 'grab',
  },
  listItemBtn: {
    display: 'block',
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    padding: '8px 10px',
    border: '1px solid transparent',
    borderRadius: 6,
    background: 'transparent',
    color: 'var(--crm-text-primary)',
    fontSize: 13,
    cursor: 'pointer',
  },
  listItemActive: {
    background: 'var(--crm-panel-list-active-bg)',
    borderColor: 'var(--crm-panel-list-active-border)',
    color: 'var(--crm-panel-list-active-text)',
    fontWeight: 600,
  },
  listItemGridActive: {
    boxShadow: 'inset 3px 0 0 #10b981',
  },
  empty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    fontSize: 13,
    color: 'var(--crm-text-muted)',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  filterInput: {
    width: '100%',
    marginTop: 4,
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
  },
  searchInput: {
    width: '100%',
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
  },
  userSearchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  searchInputInline: {
    flex: 1,
    minWidth: 0,
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
  },
  userSearchClearBtn: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    padding: 0,
    border: 'none',
    borderRadius: 8,
    background: 'var(--crm-surface-hover, #f1f5f9)',
    color: 'var(--crm-text-secondary)',
    cursor: 'pointer',
  },
  userSearchClearBtnDisabled: {
    background: '#e2e8f0',
    color: '#94a3b8',
    cursor: 'not-allowed',
    opacity: 0.85,
  },
  userListItemRow: {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    borderRadius: 6,
    border: '1px solid transparent',
  },
  userListItemRowDivider: {
    borderBottom: '1px solid var(--crm-border-subtle, #f1f5f9)',
  },
  userPinBtn: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    padding: 0,
    border: 'none',
    borderLeft: '1px solid var(--crm-border-subtle, #e2e8f0)',
    borderRadius: '0 6px 6px 0',
    background: 'transparent',
    color: 'var(--crm-text-muted, #94a3b8)',
    cursor: 'pointer',
  },
  userPinBtnActive: {
    color: '#dc2626',
    background: 'rgba(220, 38, 38, 0.08)',
  },
  userListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    padding: '8px 10px',
    border: 'none',
    borderRadius: '6px 0 0 6px',
    background: 'transparent',
    color: 'var(--crm-text-primary)',
    cursor: 'pointer',
  },
  userListItemDraggable: {
    cursor: 'grab',
  },
  userListItemActive: {
    background: 'var(--crm-panel-list-active-bg)',
    borderColor: 'var(--crm-panel-list-active-border)',
    color: 'var(--crm-panel-list-active-text)',
  },
  userListItemRowActive: {
    background: 'var(--crm-panel-list-active-bg)',
    borderColor: 'var(--crm-panel-list-active-border)',
    color: 'var(--crm-panel-list-active-text)',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userPerfil: {
    fontSize: 11,
    color: 'var(--crm-text-secondary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userPerfilActive: {
    color: 'var(--crm-panel-list-active-alias)',
  },
  userContador: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6366f1',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 8,
    paddingTop: 28,
  },
  actionBtn: {
    padding: '9px 14px',
    background: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  actionBtnDisabled: {
    background: '#e2e8f0',
    color: '#94a3b8',
    cursor: 'not-allowed',
    opacity: 0.85,
  },
  hiddenEmpresasInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    flexShrink: 0,
    marginBottom: 6,
    padding: '8px 10px',
    fontSize: 12,
    lineHeight: 1.4,
    color: 'var(--crm-form-warning-text, #92400e)',
    background: 'var(--crm-form-warning-bg, #fffbeb)',
    border: '1px solid var(--crm-form-warning-border, #fcd34d)',
    borderRadius: 8,
  },
  hiddenEmpresasInfoIcon: {
    flexShrink: 0,
    marginTop: 1,
    color: 'var(--crm-form-warning-text, #d97706)',
  },
  empresaGridToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
    paddingBottom: 4,
  },
  empresaGridSearch: {
    flex: 1,
    minWidth: 0,
    padding: '6px 8px',
    fontSize: 12,
    border: '1px solid var(--crm-border)',
    borderRadius: 6,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
  },
  empresaGridScroll: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  empresaGridTable: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  empresaGridTh: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: '6px 8px',
    fontSize: 11,
    fontWeight: 600,
    textAlign: 'left',
    color: 'var(--crm-text-secondary)',
    background: 'var(--crm-surface)',
    borderBottom: '1px solid var(--crm-border)',
  },
  empresaGridTr: {
    borderBottom: '1px solid var(--crm-border-subtle, #f1f5f9)',
  },
  empresaGridTrHidden: {
    opacity: 0.55,
  },
  empresaGridTrActive: {
    background: 'var(--crm-panel-list-active-bg)',
  },
  empresaGridTrGridActive: {
    boxShadow: 'inset 3px 0 0 #10b981',
  },
  empresaGridTd: {
    padding: '4px 6px',
    verticalAlign: 'middle',
    fontSize: 12,
  },
  empresaGridEmpresaBtn: {
    display: 'block',
    width: '100%',
    minWidth: 0,
    padding: '4px 6px',
    border: 'none',
    borderRadius: 4,
    background: 'transparent',
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  },
  empresaGridIconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    padding: 0,
    border: '1px solid var(--crm-border)',
    borderRadius: 6,
    background: 'var(--crm-surface)',
    cursor: 'pointer',
  },
  empresaGridEyeVisible: {
    color: '#10b981',
  },
  empresaGridEyeHidden: {
    color: 'var(--crm-text-muted)',
    opacity: 0.75,
  },
  empresaGridActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 2,
  },
  empresaGridEmpty: {
    padding: 16,
    textAlign: 'center',
    fontSize: 13,
    color: 'var(--crm-text-muted)',
    fontStyle: 'italic',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    minWidth: 0,
  },
  filterInputInline: {
    flex: 1,
    minWidth: 0,
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
  },
};
